/**
 * Map definitions. Greybox-first: every map is a set of axis-aligned boxes
 * (collision AND render geometry), FFA spawn points, and kill volumes.
 * Both client (render + prediction) and server (authoritative sim) build
 * their world from the same MapDef, so they can never disagree.
 */
import { pushDyn } from "./movement.js";
/** Wagon centre positions + facing for a given base angle. Deterministic. */
export function trainWagons(cfg, baseAngle) {
    const out = [];
    for (let i = 0; i < cfg.wagons; i++) {
        const a = baseAngle - i * cfg.arc;
        out.push({ x: cfg.cx + cfg.radius * Math.cos(a), z: cfg.cz + cfg.radius * Math.sin(a), a });
    }
    return out;
}
/** Dynamic train slabs for prediction/authority. Includes per-frame carry deltas.
 *
 *  Pass `dynamic` + `pool` to append allocation-free (the hot path — see
 *  `pushDyn`); omit both for a fresh array (tests / one-off callers). */
export function trainColliderBoxes(cfg, angle, prevAngle, dynamic, pool) {
    const cur = trainWagons(cfg, angle);
    const prev = trainWagons(cfg, prevAngle);
    const fallback = [{ topOffset: 0, depth: 0.6, half: cfg.half }];
    const specs = cfg.colliders?.length ? cfg.colliders : fallback;
    const out = dynamic ?? [];
    for (let i = 0; i < cur.length; i++) {
        for (const spec of specs) {
            if (spec.wagons && !spec.wagons.includes(i))
                continue;
            const half = spec.half ?? cfg.half;
            const top = cfg.y + spec.topOffset;
            const minX = cur[i].x - half[0], maxX = cur[i].x + half[0];
            const minY = top - spec.depth, maxY = top;
            const minZ = cur[i].z - half[1], maxZ = cur[i].z + half[1];
            const dx = cur[i].x - prev[i].x, dz = cur[i].z - prev[i].z;
            if (dynamic && pool)
                pushDyn(dynamic, pool, minX, maxX, minY, maxY, minZ, maxZ, dx, 0, dz);
            else
                out.push({ minX, maxX, minY, maxY, minZ, maxZ, dx, dy: 0, dz });
        }
    }
    return out;
}
/** Plane centre, heading + walkable deck-pad world positions at a base angle. */
/** Deck pose on the auto circuit at `angle`. */
export function planePose(cfg, angle) {
    return {
        cx: cfg.cx + cfg.radius * Math.cos(angle),
        cz: cfg.cz + cfg.radius * Math.sin(angle),
        cy: cfg.baseY + cfg.climbY * (0.5 - 0.5 * Math.cos(angle)),
        heading: angle + Math.PI / 2, // travel along the tangent
    };
}
/** Walkable deck pads for an ARBITRARY pose (auto circuit or piloted). */
export function planePadsAt(cfg, cx, cy, cz, heading) {
    const fx = -Math.sin(heading), fz = -Math.cos(heading); // forward
    const rx = -fz, rz = fx; // right
    const pads = [];
    if (cfg.deckR) {
        // filled disc: a grid of pads (overlapping so there are no gaps) inside
        // the saucer radius — land anywhere on the UFO
        const R = cfg.deckR, step = cfg.padHalf * 1.5; // 2*padHalf-wide pads at 1.5x step still overlap (no gaps)
        for (let gx = -R; gx <= R + 0.01; gx += step) {
            for (let gz = -R; gz <= R + 0.01; gz += step) {
                if (Math.hypot(gx, gz) > R)
                    continue; // stay within the disc
                pads.push({ x: cx + rx * gx + fx * gz, y: cy, z: cz + rz * gx + fz * gz });
            }
        }
    }
    else {
        for (const lz of cfg.fuselage)
            pads.push({ x: cx + fx * lz, y: cy, z: cz + fz * lz });
        for (const lx of [-cfg.wing, cfg.wing])
            pads.push({ x: cx + rx * lx, y: cy, z: cz + rz * lx });
    }
    return pads;
}
export function planeState(cfg, angle) {
    const pose = planePose(cfg, angle);
    return { ...pose, pads: planePadsAt(cfg, pose.cx, pose.cy, pose.cz, pose.heading) };
}
/** Index of the hold containing this point, or -1. Pure and allocation-free —
 *  it runs once per tick per carrier on the server, and once per frame on the
 *  client for the "IN THE HOLD" readout. */
export function holdAt(map, x, y, z) {
    const holds = map.holds;
    if (!holds)
        return -1;
    for (let i = 0; i < holds.length; i++) {
        const h = holds[i];
        if (Math.abs(x - h.p[0]) > h.s[0] / 2)
            continue;
        if (Math.abs(y - h.p[1]) > h.s[1] / 2)
            continue;
        if (Math.abs(z - h.p[2]) > h.s[2] / 2)
            continue;
        return i;
    }
    return -1;
}
// Bright, clean daytime palette (this map is now a "normal" arena).
const C = {
    floor: "#b7bcc4",
    wall: "#d8d2c4",
    block: "#9aa2ad",
    accentC: "#3f7fbf",
    accentM: "#c85a4a",
    accentL: "#6ea84f",
};
/**
 * MAP 1 — "ARENA": symmetrical concrete arena in daylight. Center cross
 * structure with four gaps, corner cover, two raised platforms with ramps.
 * Tight sightlines, constant collisions — built for Gun Game.
 */
export const MAP_GRID_ARENA = {
    id: "grid_arena",
    name: "Arena",
    modes: ["gungame", "burnout", "plunder"],
    theme: "day",
    killZ: -12,
    bounds: { min: [-26, -14, -26], max: [26, 20, 26] },
    boxes: [
        // floor
        { p: [0, -0.5, 0], s: [48, 1, 48], color: C.floor, glow: C.accentC },
        // outer walls
        { p: [0, 2.5, -24.5], s: [50, 6, 1], color: C.wall, glow: C.accentM },
        { p: [0, 2.5, 24.5], s: [50, 6, 1], color: C.wall, glow: C.accentM },
        { p: [-24.5, 2.5, 0], s: [1, 6, 50], color: C.wall, glow: C.accentM },
        { p: [24.5, 2.5, 0], s: [1, 6, 50], color: C.wall, glow: C.accentM },
        // center cross — four wall segments leaving gaps at the middle of each side
        { p: [0, 1.5, -6], s: [2, 3, 8], color: C.block, glow: C.accentC },
        { p: [0, 1.5, 6], s: [2, 3, 8], color: C.block, glow: C.accentC },
        { p: [-6, 1.5, 0], s: [8, 3, 2], color: C.block, glow: C.accentC },
        { p: [6, 1.5, 0], s: [8, 3, 2], color: C.block, glow: C.accentC },
        // center pillar (cover at the crossing)
        { p: [0, 2, 0], s: [2.4, 4, 2.4], color: C.block, glow: C.accentL },
        // corner L-covers (symmetrical)
        { p: [-15, 1.25, -15], s: [6, 2.5, 1.5], color: C.block, glow: C.accentM },
        { p: [-15, 1.25, -12], s: [1.5, 2.5, 7.5], color: C.block, glow: C.accentM },
        { p: [15, 1.25, 15], s: [6, 2.5, 1.5], color: C.block, glow: C.accentM },
        { p: [15, 1.25, 12], s: [1.5, 2.5, 7.5], color: C.block, glow: C.accentM },
        { p: [15, 1.25, -15], s: [6, 2.5, 1.5], color: C.block, glow: C.accentC },
        { p: [15, 1.25, -12], s: [1.5, 2.5, 7.5], color: C.block, glow: C.accentC },
        { p: [-15, 1.25, 15], s: [6, 2.5, 1.5], color: C.block, glow: C.accentC },
        { p: [-15, 1.25, 12], s: [1.5, 2.5, 7.5], color: C.block, glow: C.accentC },
        // raised platforms (east/west) with ramps
        { p: [-20, 1, 0], s: [7, 2, 10], color: C.block, glow: C.accentL },
        { p: [20, 1, 0], s: [7, 2, 10], color: C.block, glow: C.accentL },
        // stairs up to the platforms — 0.5 risers, walkable via controller step-up
        { p: [-13.5, 0.25, 0], s: [1.2, 0.5, 6], color: C.block },
        { p: [-14.7, 0.5, 0], s: [1.2, 1.0, 6], color: C.block },
        { p: [-15.9, 0.75, 0], s: [1.2, 1.5, 6], color: C.block },
        { p: [13.5, 0.25, 0], s: [1.2, 0.5, 6], color: C.block },
        { p: [14.7, 0.5, 0], s: [1.2, 1.0, 6], color: C.block },
        { p: [15.9, 0.75, 0], s: [1.2, 1.5, 6], color: C.block },
        // scattered crates (mid-lane cover)
        { p: [-8, 0.75, -10], s: [1.6, 1.5, 1.6], color: C.block, glow: C.accentC },
        { p: [8, 0.75, 10], s: [1.6, 1.5, 1.6], color: C.block, glow: C.accentC },
        { p: [-8, 0.75, 10], s: [1.6, 1.5, 1.6], color: C.block, glow: C.accentM },
        { p: [8, 0.75, -10], s: [1.6, 1.5, 1.6], color: C.block, glow: C.accentM },
        { p: [0, 0.75, -16], s: [3, 1.5, 1.6], color: C.block, glow: C.accentL },
        { p: [0, 0.75, 16], s: [3, 1.5, 1.6], color: C.block, glow: C.accentL },
    ],
    // yaw convention: forward = (-sin yaw, -cos yaw); every spawn faces the arena
    spawns: [
        { p: [-21, 0.1, -21], yaw: Math.PI * 1.25 },
        { p: [21, 0.1, -21], yaw: Math.PI * 0.75 },
        { p: [21, 0.1, 21], yaw: Math.PI * 0.25 },
        { p: [-21, 0.1, 21], yaw: Math.PI * 1.75 },
        { p: [0, 0.1, -21], yaw: Math.PI },
        { p: [0, 0.1, 21], yaw: 0 },
        { p: [-21, 2.2, 0], yaw: Math.PI * 1.5 },
        { p: [21, 2.2, 0], yaw: Math.PI * 0.5 },
        { p: [-11, 0.1, -5], yaw: Math.PI * 1.5 },
        { p: [11, 0.1, 5], yaw: Math.PI * 0.5 },
        { p: [-11, 0.1, 5], yaw: Math.PI * 1.5 },
        { p: [11, 0.1, -5], yaw: Math.PI * 0.5 },
    ],
};
// Bright, flat-shaded palette for the daytime map (krunker-ish).
const D = {
    ground: "#8fb96a",
    ground2: "#7ba859",
    crate: "#c9a36b",
    crateDark: "#a8814d",
    wall: "#d8d2c4",
    wallTrim: "#b64a3a",
    metal: "#8a94a0",
    accent: "#e8b84b",
};
/**
 * MAP 2 — "SANDLOT": a bright outdoor block-fort map in daylight. No neon —
 * clean flat colors, wooden crates, sandstone walls. Symmetrical two-fort
 * layout with a central contested yard and side alleys. Krunker-style feel.
 */
export const MAP_SANDLOT = {
    id: "sandlot",
    name: "Practice Field",
    modes: ["gungame", "burnout", "plunder"],
    theme: "day",
    killZ: -12,
    bounds: { min: [-30, -14, -22], max: [30, 20, 22] },
    boxes: [
        // ground (two-tone checker feel via two slabs)
        { p: [0, -0.5, 0], s: [60, 1, 44], color: D.ground },
        { p: [0, -0.49, 0], s: [20, 1, 20], color: D.ground2 },
        // perimeter walls
        { p: [0, 2, -21.5], s: [62, 8, 1], color: D.wall },
        { p: [0, 2, 21.5], s: [62, 8, 1], color: D.wall },
        { p: [-29.5, 2, 0], s: [1, 8, 44], color: D.wall },
        { p: [29.5, 2, 0], s: [1, 8, 44], color: D.wall },
        // wall trim stripes
        { p: [0, 4.4, -21.5], s: [62, 0.4, 1.05], color: D.wallTrim },
        { p: [0, 4.4, 21.5], s: [62, 0.4, 1.05], color: D.wallTrim },
        // two forts (west & east) — raised platforms with a ramp and battlement
        { p: [-24, 1, 0], s: [10, 2, 14], color: D.crate },
        { p: [24, 1, 0], s: [10, 2, 14], color: D.crate },
        // fort battlement walls (cover on the yard-facing side)
        { p: [-20.5, 2.75, -5], s: [3, 1.5, 1], color: D.crateDark },
        { p: [-20.5, 2.75, 5], s: [3, 1.5, 1], color: D.crateDark },
        { p: [20.5, 2.75, -5], s: [3, 1.5, 1], color: D.crateDark },
        { p: [20.5, 2.75, 5], s: [3, 1.5, 1], color: D.crateDark },
        // fort ramps (walkable via step-up: 0.5 risers)
        { p: [-18, 0.25, 8], s: [3, 0.5, 2], color: D.crateDark },
        { p: [-18, 0.75, 6], s: [3, 1.0, 2], color: D.crateDark },
        { p: [-18, 1.25, 4], s: [3, 1.5, 2], color: D.crateDark },
        { p: [18, 0.25, -8], s: [3, 0.5, 2], color: D.crateDark },
        { p: [18, 0.75, -6], s: [3, 1.0, 2], color: D.crateDark },
        { p: [18, 1.25, -4], s: [3, 1.5, 2], color: D.crateDark },
        // central yard cover — crate cluster
        { p: [0, 1, 0], s: [3, 2, 3], color: D.crate },
        { p: [0, 0.75, 4], s: [1.6, 1.5, 1.6], color: D.crateDark },
        { p: [0, 0.75, -4], s: [1.6, 1.5, 1.6], color: D.crateDark },
        { p: [-4, 0.6, 0], s: [1.4, 1.2, 1.4], color: D.crate },
        { p: [4, 0.6, 0], s: [1.4, 1.2, 1.4], color: D.crate },
        // side alley cover (north & south lanes)
        { p: [-10, 0.9, -15], s: [2, 1.8, 4], color: D.metal },
        { p: [10, 0.9, 15], s: [2, 1.8, 4], color: D.metal },
        { p: [10, 0.9, -15], s: [2, 1.8, 4], color: D.metal },
        { p: [-10, 0.9, 15], s: [2, 1.8, 4], color: D.metal },
        // low sandbags mid-lane
        { p: [-8, 0.4, 0], s: [4, 0.8, 1.2], color: D.accent },
        { p: [8, 0.4, 0], s: [4, 0.8, 1.2], color: D.accent },
        // (FLYING ISLANDS are appended AFTER the neon-dimension copy below, so
        // they sit in the empty gap between the two fields and don't get mirrored)
    ],
    // (island jetpack chests + helis are appended AFTER enrichMaps so the ground
    // karts/heli still get placed — see the block below the MAPS registry)
    // forward = (-sin yaw, -cos yaw); yards face inward
    spawns: [
        { p: [-27, 2.2, -4], yaw: Math.PI * 1.5 },
        { p: [-27, 2.2, 4], yaw: Math.PI * 1.5 },
        { p: [27, 2.2, -4], yaw: Math.PI * 0.5 },
        { p: [27, 2.2, 4], yaw: Math.PI * 0.5 },
        { p: [-14, 0.1, -18], yaw: Math.PI * 1.75 },
        { p: [14, 0.1, 18], yaw: Math.PI * 0.75 },
        { p: [-14, 0.1, 18], yaw: Math.PI * 1.25 },
        { p: [14, 0.1, -18], yaw: Math.PI * 0.25 },
        { p: [0, 0.1, -18], yaw: Math.PI },
        { p: [0, 0.1, 18], yaw: 0 },
        { p: [-16, 0.1, 0], yaw: Math.PI * 1.5 },
        { p: [16, 0.1, 0], yaw: Math.PI * 0.5 },
    ],
};
/** Practice-field neon "mirror dimension": a full copy of the field offset far
 *  to the north, a genuinely separate place linked only by the mirror portal.
 *  Added AFTER enrichMaps() (below) so map enrichment (vehicles/pickups/pads)
 *  stays inside the real practice field, not scattered into the empty gap. */
export const PRACTICE_DIM_OFFSET = 200;
const V = {
    floor: "#141826",
    ring: "#1c2438",
    pillar: "#242c44",
    accentC: "#00f0ff",
    accentM: "#ff2ec4",
    accentL: "#9dff00",
};
/**
 * MAP 3 — "REACTOR": a compact neon two-level ring around a central column,
 * with an upper walkway reachable by ramps. Verticality + tight loops keep
 * fights constant. Neon theme but sparse glow (per feedback).
 */
export const MAP_REACTOR = {
    id: "reactor",
    name: "Reactor",
    modes: ["gungame", "burnout", "plunder"],
    theme: "neon",
    killZ: -12,
    bounds: { min: [-22, -14, -22], max: [22, 24, 22] },
    boxes: [
        { p: [0, -0.5, 0], s: [40, 1, 40], color: V.floor, glow: V.accentC },
        // outer walls (tall, two-level)
        { p: [0, 5, -19.5], s: [40, 12, 1], color: V.ring, glow: V.accentM },
        { p: [0, 5, 19.5], s: [40, 12, 1], color: V.ring, glow: V.accentM },
        { p: [-19.5, 5, 0], s: [1, 12, 40], color: V.ring, glow: V.accentM },
        { p: [19.5, 5, 0], s: [1, 12, 40], color: V.ring, glow: V.accentM },
        // central reactor column (glowing)
        { p: [0, 4, 0], s: [4, 8, 4], color: V.pillar, glow: V.accentL },
        { p: [0, 8.2, 0], s: [5, 0.5, 5], color: V.pillar, glow: V.accentC },
        // upper ring walkway (4 platforms) at y≈4
        { p: [0, 3.75, -14], s: [16, 0.5, 5], color: V.pillar, glow: V.accentC },
        { p: [0, 3.75, 14], s: [16, 0.5, 5], color: V.pillar, glow: V.accentC },
        { p: [-14, 3.75, 0], s: [5, 0.5, 16], color: V.pillar, glow: V.accentC },
        { p: [14, 3.75, 0], s: [5, 0.5, 16], color: V.pillar, glow: V.accentC },
        // corner connectors for the upper ring
        { p: [-12, 3.75, -12], s: [6, 0.5, 6], color: V.pillar, glow: V.accentM },
        { p: [12, 3.75, 12], s: [6, 0.5, 6], color: V.pillar, glow: V.accentM },
        { p: [-12, 3.75, 12], s: [6, 0.5, 6], color: V.pillar, glow: V.accentM },
        { p: [12, 3.75, -12], s: [6, 0.5, 6], color: V.pillar, glow: V.accentM },
        // ramps up to the ring (step-up stairs, 0.5 risers, from center-edges)
        { p: [-3, 0.25, -8], s: [2, 0.5, 2], color: V.pillar },
        { p: [-3, 0.75, -9.5], s: [2, 1.0, 2], color: V.pillar },
        { p: [-3, 1.25, -11], s: [2, 1.5, 2], color: V.pillar },
        { p: [-3, 1.75, -12.5], s: [2, 2.0, 2], color: V.pillar },
        { p: [-3, 2.25, -14], s: [2, 2.5, 2], color: V.pillar },
        { p: [-3, 2.75, -15.5], s: [2, 3.0, 2], color: V.pillar },
        { p: [-3, 3.25, -17], s: [2, 3.5, 2], color: V.pillar },
        { p: [3, 0.25, 8], s: [2, 0.5, 2], color: V.pillar },
        { p: [3, 0.75, 9.5], s: [2, 1.0, 2], color: V.pillar },
        { p: [3, 1.25, 11], s: [2, 1.5, 2], color: V.pillar },
        { p: [3, 1.75, 12.5], s: [2, 2.0, 2], color: V.pillar },
        { p: [3, 2.25, 14], s: [2, 2.5, 2], color: V.pillar },
        { p: [3, 2.75, 15.5], s: [2, 3.0, 2], color: V.pillar },
        { p: [3, 3.25, 17], s: [2, 3.5, 2], color: V.pillar },
        // ground-floor cover
        { p: [-9, 0.9, -9], s: [2, 1.8, 2], color: V.pillar, glow: V.accentC },
        { p: [9, 0.9, 9], s: [2, 1.8, 2], color: V.pillar, glow: V.accentC },
        { p: [-9, 0.9, 9], s: [2, 1.8, 2], color: V.pillar, glow: V.accentM },
        { p: [9, 0.9, -9], s: [2, 1.8, 2], color: V.pillar, glow: V.accentM },
    ],
    spawns: [
        { p: [-16, 0.1, -16], yaw: Math.PI * 1.25 },
        { p: [16, 0.1, -16], yaw: Math.PI * 0.75 },
        { p: [16, 0.1, 16], yaw: Math.PI * 0.25 },
        { p: [-16, 0.1, 16], yaw: Math.PI * 1.75 },
        { p: [0, 0.1, -16], yaw: Math.PI },
        { p: [0, 0.1, 16], yaw: 0 },
        { p: [-16, 0.1, 0], yaw: Math.PI * 1.5 },
        { p: [16, 0.1, 0], yaw: Math.PI * 0.5 },
        { p: [0, 4.1, -14], yaw: Math.PI },
        { p: [0, 4.1, 14], yaw: 0 },
        { p: [-14, 4.1, 0], yaw: Math.PI * 1.5 },
        { p: [14, 4.1, 0], yaw: Math.PI * 0.5 },
    ],
};
const STORY = 3; // metres per floor
const WALL_T = 0.4;
const DOOR_W = 2.0;
const DOOR_H = 2.4;
/**
 * A hollow building: 4 walls (a door gap on the -Z face), an interior floor
 * per story above the ground, and a rooftop slab. Enterable and multi-level.
 */
function building(cx, cz, w, d, stories, paint) {
    const boxes = [];
    const H = stories * STORY;
    const { color, glow } = paint;
    // +Z wall (solid)
    boxes.push({ p: [cx, H / 2, cz + d / 2], s: [w, H, WALL_T], color, glow });
    // -Z wall (front) with a doorway gap on the ground floor
    const segW = (w - DOOR_W) / 2;
    boxes.push({ p: [cx - (DOOR_W / 2 + segW / 2), H / 2, cz - d / 2], s: [segW, H, WALL_T], color, glow });
    boxes.push({ p: [cx + (DOOR_W / 2 + segW / 2), H / 2, cz - d / 2], s: [segW, H, WALL_T], color, glow });
    boxes.push({ p: [cx, DOOR_H + (H - DOOR_H) / 2, cz - d / 2], s: [DOOR_W, H - DOOR_H, WALL_T], color, glow }); // lintel
    // side walls
    boxes.push({ p: [cx + w / 2, H / 2, cz], s: [WALL_T, H, d], color, glow });
    boxes.push({ p: [cx - w / 2, H / 2, cz], s: [WALL_T, H, d], color, glow });
    // interior floors (upper stories) — leave a stairwell hole on the +X side.
    // hollow: a completely open atrium, NO interior floors at all (Skyhaven)
    for (let s = 1; s < stories; s++) {
        if (paint.hollow)
            continue;
        const fy = s * STORY;
        const holeW = 2.4;
        boxes.push({ p: [cx - holeW / 2, fy, cz], s: [w - WALL_T - holeW, 0.3, d - WALL_T], color: paint.roof ?? color, glow });
    }
    // rooftop
    if (paint.roofHoles)
        holedSlab(boxes, cx, cz, w, d, H + 0.15, 0.3, paint.roof ?? color, paint.roofHoles);
    else
        boxes.push({ p: [cx, H + 0.15, cz], s: [w, 0.3, d], color: paint.roof ?? color, glow });
    // low parapet on the roof (cover) — skipped when an external stair must
    // land flush on the roof (a parapet would wall the stair off)
    const pT = 0.15;
    if (!paint.noParapet)
        for (const [px, pz, sw, sd] of [
            [cx, cz + d / 2 - pT, w, pT * 2],
            [cx, cz - d / 2 + pT, w, pT * 2],
            [cx + w / 2 - pT, cz, pT * 2, d],
            [cx - w / 2 + pT, cz, pT * 2, d],
        ]) {
            boxes.push({ p: [px, H + 0.8, pz], s: [sw, 1, sd], color, glow });
        }
    return boxes;
}
/**
 * A straight run of stairs rising `targetH` metres, starting at (x,z) and
 * advancing along `dir` ('px'|'nx'|'pz'|'nz'). Each 0.5 riser is a slab.
 */
function staircase(x, z, targetH, dir, width, paint) {
    const boxes = [];
    const steps = Math.ceil(targetH / 0.5);
    const run = 0.9;
    const dx = dir === "px" ? 1 : dir === "nx" ? -1 : 0;
    const dz = dir === "pz" ? 1 : dir === "nz" ? -1 : 0;
    for (let i = 1; i <= steps; i++) {
        const h = i * 0.5;
        const px = x + dx * run * i;
        const pz = z + dz * run * i;
        const sx = dx !== 0 ? run : width;
        const sz = dz !== 0 ? run : width;
        boxes.push({ p: [px, h - 0.25, pz], s: [sx, 0.5, sz], color: paint.color, glow: paint.glow });
    }
    return boxes;
}
// ---------------------------------------------------------------------------
// MAP 4 — "DOWNTOWN": bright daytime city block. Four enterable buildings of
// varying height around a central plaza, streets between them, rooftops
// reachable by external staircases, roof-to-roof lines. Big and vertical.
// ---------------------------------------------------------------------------
const DT = {
    ground: "#9aa7b4",
    road: "#5c6470",
    b1: "#c07a55",
    b2: "#7f95a8",
    b3: "#c9b06a",
    b4: "#8a7d9a",
    roof: "#3f4650",
    accent: "#d94f3d",
};
function downtown() {
    const boxes = [
        { p: [0, -0.5, 0], s: [90, 1, 90], color: DT.ground },
        // cross streets (darker)
        { p: [0, -0.48, 0], s: [10, 1, 90], color: DT.road },
        { p: [0, -0.48, 0], s: [90, 1, 10], color: DT.road },
        // perimeter low walls
        { p: [0, 1, -44.5], s: [90, 4, 1], color: DT.b2 },
        { p: [0, 1, 44.5], s: [90, 4, 1], color: DT.b2 },
        { p: [-44.5, 1, 0], s: [1, 4, 90], color: DT.b2 },
        { p: [44.5, 1, 0], s: [1, 4, 90], color: DT.b2 },
    ];
    // four corner buildings, doors facing the central plaza (all -Z door → rotate by placement)
    boxes.push(...building(-22, -22, 16, 16, 2, { color: DT.b1, glow: undefined, roof: DT.roof }));
    boxes.push(...building(22, -22, 16, 16, 3, { color: DT.b2, roof: DT.roof }));
    boxes.push(...building(-22, 22, 16, 16, 3, { color: DT.b3, roof: DT.roof }));
    boxes.push(...building(22, 22, 16, 16, 2, { color: DT.b4, roof: DT.roof }));
    // external staircases up to each rooftop (placed on the plaza-facing corner)
    boxes.push(...staircase(-13, -30, 6, "px", 2.2, { color: DT.roof }));
    boxes.push(...staircase(31, -30, 9, "nx", 2.2, { color: DT.roof }));
    boxes.push(...staircase(-13, 30, 9, "px", 2.2, { color: DT.roof }));
    boxes.push(...staircase(31, 30, 6, "nx", 2.2, { color: DT.roof }));
    // central plaza cover
    boxes.push({ p: [0, 1, 0], s: [4, 2, 4], color: DT.accent }, { p: [-6, 0.75, 0], s: [1.5, 1.5, 3], color: DT.b2 }, { p: [6, 0.75, 0], s: [1.5, 1.5, 3], color: DT.b2 }, { p: [0, 0.75, -8], s: [3, 1.5, 1.5], color: DT.b3 }, { p: [0, 0.75, 8], s: [3, 1.5, 1.5], color: DT.b3 });
    return {
        id: "downtown",
        name: "Downtown",
        modes: ["gungame", "burnout", "plunder"],
        theme: "day",
        killZ: -12,
        bounds: { min: [-46, -14, -46], max: [46, 30, 46] },
        boxes,
        spawns: [
            { p: [-6, 0.1, -6], yaw: Math.PI * 0.25 },
            { p: [6, 0.1, -6], yaw: Math.PI * 0.75 },
            { p: [6, 0.1, 6], yaw: Math.PI * 1.25 },
            { p: [-6, 0.1, 6], yaw: Math.PI * 1.75 },
            { p: [-30, 0.1, 0], yaw: Math.PI * 0.5 },
            { p: [30, 0.1, 0], yaw: Math.PI * 1.5 },
            { p: [0, 0.1, -30], yaw: Math.PI },
            { p: [0, 0.1, 30], yaw: 0 },
            { p: [-22, 6.2, -22], yaw: Math.PI * 0.25 }, // rooftops
            { p: [22, 9.2, -22], yaw: Math.PI * 0.75 },
            { p: [-22, 9.2, 22], yaw: Math.PI * 1.75 },
            { p: [22, 6.2, 22], yaw: Math.PI * 1.25 },
        ],
    };
}
// ---------------------------------------------------------------------------
// MAP 5 — "NEON DISTRICT": night city. Three tall neon towers linked by a
// mid-level catwalk ring, ground-level alleys, rooftops. Vertical fights.
// ---------------------------------------------------------------------------
const ND = {
    ground: "#0e1220",
    tower: "#1a2136",
    catwalk: "#222a44",
    accentC: "#00f0ff",
    accentM: "#ff2ec4",
    accentL: "#9dff00",
};
function neonDistrict() {
    const boxes = [
        { p: [0, -0.5, 0], s: [70, 1, 70], color: ND.ground, glow: ND.accentC },
        // perimeter
        { p: [0, 6, -34.5], s: [70, 14, 1], color: ND.tower, glow: ND.accentM },
        { p: [0, 6, 34.5], s: [70, 14, 1], color: ND.tower, glow: ND.accentM },
        { p: [-34.5, 6, 0], s: [1, 14, 70], color: ND.tower, glow: ND.accentM },
        { p: [34.5, 6, 0], s: [1, 14, 70], color: ND.tower, glow: ND.accentM },
    ];
    // three towers (enterable, 3 stories) in a triangle. `side` = which face the
    // roof switchback sits on (+1 = +X, -1 = -X): the two north towers face
    // OUTWARD so their stairs clear the central z=14 catwalk.
    const towers = [
        [0, -16, 1],
        [-18, 14, -1],
        [18, 14, 1],
    ];
    for (const [tx, tz, side] of towers) {
        // no parapet: the bridge lands flush on the roof edge (a parapet would
        // wall it off)
        boxes.push(...building(tx, tz, 12, 12, 3, { color: ND.tower, glow: ND.accentC, roof: ND.catwalk, noParapet: true }));
        // SWITCHBACK up to the y≈9.3 roof. A single 0.5-riser flight to 9 m needs
        // ~18 m of run (the tower is only 12 m), so TWO flights sit SIDE BY SIDE
        // outside the +X wall (offset in x so neither stacks over the other) and
        // turn back on a landing; a flat bridge then crosses ABOVE the wall onto
        // the roof. Outer flight A climbs to the landing, inner flight B to roof.
        const xA = tx + side * 11, xB = tx + side * 8;
        // flight A (outer): ground → landing (y=4.5), 9 steps running +z
        boxes.push(...staircase(xA, tz - 4, 4.5, "pz", 2.2, { color: ND.catwalk, glow: ND.accentL }));
        // landing slab at the +z end bridging outer→inner flight (y=4.5)
        boxes.push({ p: [(xA + xB) / 2, 4.5, tz + 4.9], s: [Math.abs(xA - xB) + 2.4, 0.4, 3.4], color: ND.catwalk, glow: ND.accentL });
        // flight B (inner): landing (y=4.5) → y≈9.5, 10 steps running -z back
        // toward the tower. staircase() builds from y=0 — lift it onto the landing.
        boxes.push(...staircase(xB, tz + 4, 5.0, "nz", 2.2, { color: ND.catwalk, glow: ND.accentL })
            .map((b) => ({ ...b, p: [b.p[0], b.p[1] + 4.5, b.p[2]] })));
        // bridge: flat slab at roof height from the flight-B top (xB, z≈tz-5)
        // ACROSS the side wall onto the roof interior
        boxes.push({ p: [tx + side * 4, 9.3, tz - 5], s: [9, 0.3, 3], color: ND.catwalk, glow: ND.accentL });
    }
    // mid-level catwalk ring at y≈4.5 linking the towers
    const cw = 4.75;
    boxes.push({ p: [-9, cw, -1], s: [22, 0.4, 3], color: ND.catwalk, glow: ND.accentL }, { p: [9, cw, -1], s: [22, 0.4, 3], color: ND.catwalk, glow: ND.accentL }, { p: [0, cw, 14], s: [40, 0.4, 3], color: ND.catwalk, glow: ND.accentL });
    // ramp from ground UP to the z=-1 catwalk (lands flush on its +z edge; the
    // old one stopped in the gap between catwalk segments). Offset to x=6 to
    // clear the central ground-cover cube.
    boxes.push(...staircase(6, -9.5, 5.0, "pz", 3, { color: ND.catwalk, glow: ND.accentL }));
    // ground cover
    boxes.push({ p: [-10, 1, -8], s: [2.5, 2, 2.5], color: ND.tower, glow: ND.accentM }, { p: [10, 1, -8], s: [2.5, 2, 2.5], color: ND.tower, glow: ND.accentC }, { p: [0, 1, 24], s: [3, 2, 2.5], color: ND.tower, glow: ND.accentL });
    return {
        id: "neon_district",
        name: "Neon District",
        modes: ["gungame", "burnout", "plunder"],
        theme: "neon",
        killZ: -12,
        bounds: { min: [-36, -14, -36], max: [36, 30, 36] },
        boxes,
        spawns: [
            { p: [-28, 0.1, -28], yaw: Math.PI * 1.25 },
            { p: [28, 0.1, -28], yaw: Math.PI * 0.75 },
            { p: [28, 0.1, 28], yaw: Math.PI * 0.25 },
            { p: [-28, 0.1, 28], yaw: Math.PI * 1.75 },
            { p: [0, 0.1, -28], yaw: Math.PI },
            { p: [0, 0.1, 28], yaw: 0 },
            { p: [-28, 0.1, 0], yaw: Math.PI * 0.5 },
            { p: [28, 0.1, 0], yaw: Math.PI * 1.5 },
            { p: [0, 9.2, -16], yaw: Math.PI }, // tower rooftops
            { p: [-18, 9.2, 14], yaw: 0 },
            { p: [18, 9.2, 14], yaw: 0 },
            { p: [0, 5, 8], yaw: Math.PI }, // catwalk
        ],
        // SCRAP KARTS (climb-anything, like the other maps) — the map had no
        // hand-placed vehicles, so it only got the non-climbing scattered ones
        vehicles: [
            { p: [-26, 0.4, 26], yaw: Math.PI * 1.75, kind: "kart" },
            { p: [26, 0.4, -26], yaw: Math.PI * 0.75, kind: "kart" },
            { p: [26, 0.4, 26], yaw: Math.PI * 0.25, kind: "heli" },
        ],
    };
}
// ---------------------------------------------------------------------------
// MAP — "COMPOUND": a bright industrial yard. Two enterable warehouses, a
// grid of shipping containers (some stacked for verticality) as cover, and
// rooftop access via container steps. Normal (day) theme, lots of angles.
// ---------------------------------------------------------------------------
const CO = {
    ground: "#8f9298",
    lot: "#6f7176",
    warehouse: "#a8a29a",
    roof: "#54585f",
    c1: "#c0603f", // rust orange container
    c2: "#3f6ea8", // blue container
    c3: "#5a8a52", // green container
    c4: "#c8a83f", // yellow container
    fence: "#7a7d84",
};
/** A shipping container (single box) at a position/size. */
function container(cx, cy, cz, color) {
    return { p: [cx, cy + 1.3, cz], s: [6, 2.6, 2.5], color };
}
function compound() {
    const boxes = [
        { p: [0, -0.5, 0], s: [84, 1, 74], color: CO.ground },
        { p: [0, -0.48, 0], s: [40, 1, 30], color: CO.lot },
        // perimeter fence walls
        { p: [0, 1.5, -37], s: [84, 5, 1], color: CO.fence },
        { p: [0, 1.5, 37], s: [84, 5, 1], color: CO.fence },
        { p: [-42, 1.5, 0], s: [1, 5, 74], color: CO.fence },
        { p: [42, 1.5, 0], s: [1, 5, 74], color: CO.fence },
    ];
    // two warehouses (enterable, 2 stories) on the short ends
    boxes.push(...building(-30, 0, 18, 20, 2, { color: CO.warehouse, roof: CO.roof }));
    boxes.push(...building(30, 0, 18, 20, 2, { color: CO.warehouse, roof: CO.roof }));
    // container steps up to each warehouse roof (roof at ~6m)
    boxes.push(container(-30, 0, 13, CO.c1));
    boxes.push(container(-30, 2.6, 13, CO.c2));
    boxes.push({ p: [-30, 5.15, 11.5], s: [6, 0.3, 2], color: CO.roof }); // bridge to roof
    boxes.push(container(30, 0, -13, CO.c3));
    boxes.push(container(30, 2.6, -13, CO.c4));
    boxes.push({ p: [30, 5.15, -11.5], s: [6, 0.3, 2], color: CO.roof });
    // central yard container maze (some stacked)
    boxes.push(container(-6, 0, -8, CO.c2));
    boxes.push(container(6, 0, 8, CO.c1));
    boxes.push(container(-8, 0, 10, CO.c4));
    boxes.push(container(8, 0, -10, CO.c3));
    boxes.push(container(0, 0, 0, CO.c1));
    boxes.push(container(0, 2.6, 0, CO.c3)); // stacked centerpiece
    // low pallets / cover
    boxes.push({ p: [-14, 0.5, 0], s: [2, 1, 3], color: CO.c4 }, { p: [14, 0.5, 0], s: [2, 1, 3], color: CO.c2 }, { p: [0, 0.5, -16], s: [3, 1, 2], color: CO.c3 }, { p: [0, 0.5, 16], s: [3, 1, 2], color: CO.c1 });
    return {
        id: "compound",
        name: "Compound",
        modes: ["gungame", "burnout", "plunder"],
        theme: "day",
        killZ: -12,
        bounds: { min: [-44, -14, -39], max: [44, 24, 39] },
        boxes,
        spawns: [
            { p: [-20, 0.1, -14], yaw: Math.PI * 0.3 },
            { p: [20, 0.1, 14], yaw: Math.PI * 1.3 },
            { p: [-20, 0.1, 14], yaw: Math.PI * 0.7 },
            { p: [20, 0.1, -14], yaw: Math.PI * 1.7 },
            { p: [0, 0.1, -28], yaw: Math.PI },
            { p: [0, 0.1, 28], yaw: 0 },
            { p: [-38, 0.1, 0], yaw: Math.PI * 0.5 },
            { p: [38, 0.1, 0], yaw: Math.PI * 1.5 },
            { p: [0, 5.3, 0], yaw: Math.PI * 0.25 }, // atop the stacked centerpiece
            { p: [-12, 0.1, -20], yaw: Math.PI * 0.2 },
            { p: [12, 0.1, 20], yaw: Math.PI * 1.2 },
            { p: [-30, 6.2, 0], yaw: Math.PI * 0.5 }, // warehouse roof
        ],
        vehicles: [
            { p: [-34, 0.9, -30], yaw: Math.PI * 0.25, kind: "heli" },
            { p: [34, 0.5, 30], yaw: Math.PI * 1.25, kind: "miniplane" },
        ],
    };
}
// ---------------------------------------------------------------------------
// MAP — "ROOFTOPS": daytime skyline. Building rooftops at varied heights with
// jumpable gaps, staircases up from a ground alley. Vertical, flanky.
// ---------------------------------------------------------------------------
const RF = {
    sky: "#c2b8aa",
    wall: "#b7ada0",
    roof: "#8f8578",
    roof2: "#a59a8b",
    trim: "#6e6357",
    ac: "#5f6670", // AC units / vents
    accent: "#c85a3d",
};
function rooftops() {
    const boxes = [
        { p: [0, -0.5, 0], s: [80, 1, 80], color: "#7c7266" }, // ground alley floor
    ];
    // building blocks: [cx, cz, w, d, height]
    const blocks = [
        [-18, -18, 14, 14, 6],
        [16, -16, 16, 12, 8],
        [-16, 16, 12, 16, 5],
        [18, 18, 14, 14, 7],
        [0, 0, 12, 12, 9], // central tower
        [-22, 2, 8, 10, 4],
        [22, -2, 8, 10, 4],
    ];
    for (const [cx, cz, w, d, h] of blocks) {
        boxes.push({ p: [cx, h / 2, cz], s: [w, h, d], color: RF.wall });
        boxes.push({ p: [cx, h + 0.15, cz], s: [w, 0.3, d], color: RF.roof }); // roof slab
        // low parapet
        for (const [px, pz, sw, sd] of [
            [cx, cz + d / 2 - 0.2, w, 0.4],
            [cx, cz - d / 2 + 0.2, w, 0.4],
            [cx + w / 2 - 0.2, cz, 0.4, d],
            [cx - w / 2 + 0.2, cz, 0.4, d],
        ]) {
            boxes.push({ p: [px, h + 0.8, pz], s: [sw, 1, sd], color: RF.trim });
        }
        // an AC unit as roof cover
        boxes.push({ p: [cx + w / 4, h + 0.9, cz - d / 4], s: [2, 1.4, 2], color: RF.ac });
    }
    // staircases from ground up to the two low side buildings (h=4)
    boxes.push(...staircase(-22, 8, 4, "nz", 2.2, { color: RF.roof2 }));
    boxes.push(...staircase(22, -8, 4, "pz", 2.2, { color: RF.roof2 }));
    // planks bridging some roofs (thin walkable slabs across gaps)
    boxes.push({ p: [-9, 4.8, -18], s: [4, 0.3, 2.5], color: RF.accent }); // low→? bridge
    boxes.push({ p: [0, 6.15, -9], s: [2.5, 0.3, 4], color: RF.accent });
    return {
        id: "rooftops",
        name: "Rooftops",
        modes: ["gungame", "arena", "ffa", "relay", "onegun", "treasure", "purge", "horde", "burnout", "plunder"],
        theme: "day",
        killZ: -8,
        bounds: { min: [-42, -10, -42], max: [42, 24, 42] },
        boxes,
        spawns: [
            { p: [-18, 6.4, -18], yaw: Math.PI * 1.25 },
            { p: [16, 8.4, -16], yaw: Math.PI * 0.75 },
            { p: [-16, 5.4, 16], yaw: Math.PI * 1.75 },
            { p: [18, 7.4, 18], yaw: Math.PI * 0.25 },
            { p: [0, 9.4, 0], yaw: 0 },
            { p: [-22, 0.1, 12], yaw: Math.PI },
            { p: [22, 0.1, -12], yaw: 0 },
            { p: [-30, 0.1, -30], yaw: Math.PI * 1.25 },
            { p: [30, 0.1, 30], yaw: Math.PI * 0.25 },
            { p: [0, 0.1, 30], yaw: 0 },
            { p: [0, 0.1, -30], yaw: Math.PI },
            { p: [22, 4.4, -8], yaw: Math.PI * 0.5 },
        ],
        // ORANGE high-jump beams in the ground alley — launch you up onto the roofs
        jumpPads: [
            { p: [-8, 0.1, -8], power: 22 },
            { p: [8, 0.1, -6], power: 23 },
            { p: [-8, 0.1, 10], power: 21 },
            { p: [10, 0.1, 10], power: 22 },
            { p: [0, 0.1, -14], power: 24 }, // reaches the tall central tower
        ],
    };
}
// ---------------------------------------------------------------------------
// MAP — "ARENA XL": the training arena at 4x the AREA — a big open day
// arena with scattered cover blocks, ramps, a raised centre platform.
// ---------------------------------------------------------------------------
function arenaXL() {
    // FOUR full copies of the original arena interior (center cross, pillar,
    // corner L-covers, raised platforms + stairs, crates) stamped as quadrants
    // around open seam lanes — same cover density as the original, 4x the area.
    const boxes = [
        { p: [0, -0.5, 0], s: [100, 1, 100], color: C.floor, glow: C.accentC }, // floor
        { p: [0, 2.5, -50.5], s: [102, 6, 1], color: C.wall, glow: C.accentM },
        { p: [0, 2.5, 50.5], s: [102, 6, 1], color: C.wall, glow: C.accentM },
        { p: [-50.5, 2.5, 0], s: [1, 6, 102], color: C.wall, glow: C.accentM },
        { p: [50.5, 2.5, 0], s: [1, 6, 102], color: C.wall, glow: C.accentM },
        // central plaza: big pillar + ring crates where the four quadrants meet
        { p: [0, 2.5, 0], s: [3.2, 5, 3.2], color: C.block, glow: C.accentL },
        { p: [-5, 0.75, 0], s: [1.6, 1.5, 3], color: C.block, glow: C.accentC },
        { p: [5, 0.75, 0], s: [1.6, 1.5, 3], color: C.block, glow: C.accentM },
        { p: [0, 0.75, -5], s: [3, 1.5, 1.6], color: C.block, glow: C.accentM },
        { p: [0, 0.75, 5], s: [3, 1.5, 1.6], color: C.block, glow: C.accentC },
    ];
    // the original arena's interior features, relative to its own centre
    const cell = [
        { p: [0, 1.5, -6], s: [2, 3, 8], color: C.block, glow: C.accentC },
        { p: [0, 1.5, 6], s: [2, 3, 8], color: C.block, glow: C.accentC },
        { p: [-6, 1.5, 0], s: [8, 3, 2], color: C.block, glow: C.accentC },
        { p: [6, 1.5, 0], s: [8, 3, 2], color: C.block, glow: C.accentC },
        { p: [0, 2, 0], s: [2.4, 4, 2.4], color: C.block, glow: C.accentL },
        { p: [-15, 1.25, -15], s: [6, 2.5, 1.5], color: C.block, glow: C.accentM },
        { p: [-15, 1.25, -12], s: [1.5, 2.5, 7.5], color: C.block, glow: C.accentM },
        { p: [15, 1.25, 15], s: [6, 2.5, 1.5], color: C.block, glow: C.accentM },
        { p: [15, 1.25, 12], s: [1.5, 2.5, 7.5], color: C.block, glow: C.accentM },
        { p: [15, 1.25, -15], s: [6, 2.5, 1.5], color: C.block, glow: C.accentC },
        { p: [15, 1.25, -12], s: [1.5, 2.5, 7.5], color: C.block, glow: C.accentC },
        { p: [-15, 1.25, 15], s: [6, 2.5, 1.5], color: C.block, glow: C.accentC },
        { p: [-15, 1.25, 12], s: [1.5, 2.5, 7.5], color: C.block, glow: C.accentC },
        { p: [-20, 1, 0], s: [7, 2, 10], color: C.block, glow: C.accentL },
        { p: [20, 1, 0], s: [7, 2, 10], color: C.block, glow: C.accentL },
        { p: [-13.5, 0.25, 0], s: [1.2, 0.5, 6], color: C.block },
        { p: [-14.7, 0.5, 0], s: [1.2, 1.0, 6], color: C.block },
        { p: [-15.9, 0.75, 0], s: [1.2, 1.5, 6], color: C.block },
        { p: [13.5, 0.25, 0], s: [1.2, 0.5, 6], color: C.block },
        { p: [14.7, 0.5, 0], s: [1.2, 1.0, 6], color: C.block },
        { p: [15.9, 0.75, 0], s: [1.2, 1.5, 6], color: C.block },
        { p: [-8, 0.75, -10], s: [1.6, 1.5, 1.6], color: C.block, glow: C.accentC },
        { p: [8, 0.75, 10], s: [1.6, 1.5, 1.6], color: C.block, glow: C.accentC },
        { p: [-8, 0.75, 10], s: [1.6, 1.5, 1.6], color: C.block, glow: C.accentM },
        { p: [8, 0.75, -10], s: [1.6, 1.5, 1.6], color: C.block, glow: C.accentM },
        { p: [0, 0.75, -16], s: [3, 1.5, 1.6], color: C.block, glow: C.accentL },
        { p: [0, 0.75, 16], s: [3, 1.5, 1.6], color: C.block, glow: C.accentL },
    ];
    for (const [qx, qz] of [[-25, -25], [25, -25], [-25, 25], [25, 25]]) {
        for (const b of cell) {
            boxes.push({ ...b, p: [b.p[0] + qx, b.p[1], b.p[2] + qz] });
        }
    }
    // seam-lane crates so the connecting roads aren't naked sightlines
    for (const [x, z] of [[-25, 0], [25, 0], [0, -25], [0, 25], [-12, -25], [12, 25], [-25, 12], [25, -12]]) {
        boxes.push({ p: [x, 0.75, z], s: [2, 1.5, 2], color: C.block, glow: C.accentL });
    }
    const spawns = [];
    for (const [qx, qz] of [[-25, -25], [25, -25], [-25, 25], [25, 25]]) {
        spawns.push({ p: [qx - 21, 0.1, qz - 21 < -49 ? qz - 15 : qz - 21], yaw: Math.atan2(qx, qz) }, { p: [qx + 11, 0.1, qz + 5], yaw: Math.PI * 0.5 }, { p: [qx - 11, 0.1, qz - 5], yaw: Math.PI * 1.5 });
    }
    spawns.push({ p: [0, 0.1, -46], yaw: Math.PI }, { p: [0, 0.1, 46], yaw: 0 }, { p: [-46, 0.1, 0], yaw: Math.PI * 0.5 }, { p: [46, 0.1, 0], yaw: Math.PI * 1.5 });
    return {
        id: "arena_xl",
        name: "Arena XL",
        modes: ["gungame", "burnout", "plunder"],
        theme: "day",
        killZ: -12,
        bounds: { min: [-52, -14, -52], max: [52, 22, 52] },
        boxes,
        spawns,
        // (2026-11-04) (±8, ±25) sat INSIDE the 7×2×10 quadrant blocks (beam in
        // the wall) — now beside them; (±25, ∓8) kissed the 3 m block's face.
        jumpPads: [
            { p: [-25, 0.1, -7.5], power: 16 }, { p: [25, 0.1, 7.5], power: 16 },
            { p: [-5, 0.1, 31.5], power: 16 }, { p: [5, 0.1, -31.5], power: 16 },
        ],
        vehicles: [
            { p: [0, 0.4, -37], yaw: Math.PI, kind: "kart" },
            { p: [0, 0.4, 37], yaw: 0, kind: "kart" },
            { p: [-37, 0.9, 0], yaw: Math.PI * 0.5, kind: "heli" },
            { p: [37, 0.5, 0], yaw: Math.PI * 1.5, kind: "miniplane" },
            { p: [-25, 0.9, -25], yaw: Math.PI * 0.25, kind: "miniufo" },
            { p: [25, 0.9, 25], yaw: Math.PI * 1.25, kind: "miniufo" },
        ],
    };
}
// ---------------------------------------------------------------------------
// MAP — "ROOFTOPS XL": the rooftops skyline scaled to 4x the AREA (160x160)
// with more towers, longer plank runs, vehicles, and extra jump beams.
// ---------------------------------------------------------------------------
function rooftopsXL() {
    const boxes = [
        { p: [0, -0.5, 0], s: [172, 1, 172], color: "#7c7266" }, // ground alleys
    ];
    const blocks = [
        { cx: -64, cz: -64, w: 18, d: 18, h: 10, access: "pad" },
        { cx: -32, cz: -64, w: 14, d: 12, h: 5, access: "stair" },
        { cx: 0, cz: -64, w: 12, d: 16, h: 7, access: "pad" },
        { cx: 32, cz: -64, w: 16, d: 14, h: 4, access: "stair" },
        { cx: 64, cz: -64, w: 16, d: 16, h: 11, access: "pad" },
        { cx: -64, cz: -32, w: 12, d: 14, h: 6, access: "stair" },
        { cx: -32, cz: -32, w: 16, d: 16, h: 8, access: "pad" },
        { cx: 0, cz: -32, w: 12, d: 12, h: 9, access: "pad" },
        { cx: 32, cz: -32, w: 18, d: 14, h: 5, access: "stair" },
        { cx: 64, cz: -32, w: 12, d: 20, h: 7, access: "pad" },
        { cx: -64, cz: 0, w: 12, d: 20, h: 6, access: "stair" },
        { cx: -32, cz: 0, w: 14, d: 12, h: 12, access: "pad" },
        { cx: 0, cz: 0, w: 14, d: 14, h: 13, access: "pad" }, // central spire
        { cx: 32, cz: 0, w: 14, d: 12, h: 10, access: "pad" },
        { cx: 64, cz: 0, w: 12, d: 20, h: 7, access: "pad" },
        { cx: -64, cz: 32, w: 16, d: 16, h: 8, access: "pad" },
        { cx: -32, cz: 32, w: 14, d: 18, h: 5, access: "stair" },
        { cx: 0, cz: 32, w: 12, d: 12, h: 6, access: "stair" },
        { cx: 32, cz: 32, w: 16, d: 16, h: 9, access: "pad" },
        { cx: 64, cz: 32, w: 18, d: 18, h: 11, access: "pad" },
        { cx: -64, cz: 64, w: 16, d: 16, h: 8, access: "pad" },
        { cx: -32, cz: 64, w: 16, d: 12, h: 4, access: "stair" },
        { cx: 0, cz: 64, w: 12, d: 16, h: 7, access: "pad" },
        { cx: 32, cz: 64, w: 14, d: 14, h: 6, access: "stair" },
        { cx: 64, cz: 64, w: 18, d: 18, h: 12, access: "pad" },
        // infill towers at the alley crossings — denser skyline, tighter lanes
        { cx: -48, cz: -48, w: 9, d: 9, h: 5, access: "stair" },
        { cx: 48, cz: -48, w: 10, d: 9, h: 7, access: "pad" },
        { cx: -48, cz: 48, w: 9, d: 10, h: 6, access: "stair" },
        { cx: 48, cz: 48, w: 10, d: 10, h: 8, access: "pad" },
        { cx: -16, cz: -48, w: 8, d: 9, h: 4, access: "stair" },
        { cx: 16, cz: 48, w: 8, d: 9, h: 4, access: "stair" },
        { cx: -48, cz: -16, w: 9, d: 8, h: 6, access: "pad" },
        { cx: 48, cz: 16, w: 9, d: 8, h: 5, access: "stair" },
        { cx: 16, cz: -16, w: 8, d: 8, h: 9, access: "pad" },
        { cx: -16, cz: 16, w: 8, d: 8, h: 7, access: "pad" },
        { cx: 78, cz: 0, w: 8, d: 12, h: 5, access: "stair" },
        { cx: -78, cz: 0, w: 8, d: 12, h: 6, access: "pad" },
    ];
    const jumpPads = [];
    for (const b of blocks) {
        const { cx, cz, w, d, h } = b;
        boxes.push({ p: [cx, h / 2, cz], s: [w, h, d], color: RF.wall });
        boxes.push({ p: [cx, h + 0.15, cz], s: [w, 0.3, d], color: RF.roof });
        // parapet — the SOUTH face gets a 3.4-wide gap when a staircase lands there
        const gap = b.access === "stair" ? 3.4 : 0;
        if (gap) {
            const seg = (w - gap) / 2;
            boxes.push({ p: [cx - gap / 2 - seg / 2, h + 0.8, cz + d / 2 - 0.2], s: [seg, 1, 0.4], color: RF.trim });
            boxes.push({ p: [cx + gap / 2 + seg / 2, h + 0.8, cz + d / 2 - 0.2], s: [seg, 1, 0.4], color: RF.trim });
        }
        else {
            boxes.push({ p: [cx, h + 0.8, cz + d / 2 - 0.2], s: [w, 1, 0.4], color: RF.trim });
        }
        boxes.push({ p: [cx, h + 0.8, cz - d / 2 + 0.2], s: [w, 1, 0.4], color: RF.trim });
        boxes.push({ p: [cx + w / 2 - 0.2, h + 0.8, cz], s: [0.4, 1, d], color: RF.trim });
        boxes.push({ p: [cx - w / 2 + 0.2, h + 0.8, cz], s: [0.4, 1, d], color: RF.trim });
        boxes.push({ p: [cx + w / 4, h + 0.9, cz - d / 4], s: [2, 1.4, 2], color: RF.ac });
        if (b.access === "stair") {
            // outside staircase walking NORTH (-z): its top step ends flush against
            // the south wall, in line with the parapet gap → step onto the roof
            const steps = Math.ceil(h / 0.5);
            const zStart = cz + d / 2 + 0.45 + 0.9 * steps;
            // tagged "stair" → removed in Arena (kart) mode (karts climb roofs directly)
            boxes.push(...staircase(cx, zStart, h, "nz", 2.6, { color: RF.roof2 }).map((b) => ({ ...b, tag: "stair" })));
        }
        else {
            // jump beam hugging the EAST wall — launches you straight up this roof
            jumpPads.push({ p: [cx + w / 2 + 1.7, 0.1, cz], power: Math.ceil(Math.sqrt(52 * (h + 2.8))) });
        }
    }
    // plank bridges between same-height neighbors
    boxes.push({ p: [-64, 6.15, -17.5], s: [2.5, 0.3, 17], color: RF.accent }); // h6 pair on the west file
    boxes.push({ p: [64, 7.15, -16], s: [2.5, 0.3, 14], color: RF.accent }); // h7 pair on the east file
    boxes.push({ p: [-64, 8.15, 48], s: [2.5, 0.3, 18], color: RF.accent }); // h8 pair, west
    boxes.push({ p: [16, 6.35, 32.5], s: [18, 0.3, 2.5], color: RF.accent }); // h6↔h9 balcony reach
    boxes.push({ p: [-16, 12.15, 0], s: [20, 0.3, 2.5], color: RF.accent }); // h12 → spire approach
    // violet MEGA beam at the heart of the map (Skyhaven-style): sails you
    // above the spire for paraglider drops onto any roof
    jumpPads.push({ p: [0, 0.1, 10], power: 62, color: "#a54dff" });
    return {
        id: "rooftops_xl",
        name: "Rooftops XL",
        modes: ["gungame", "arena", "ffa", "relay", "onegun", "treasure", "purge", "horde", "burnout", "plunder"],
        theme: "day",
        killZ: -8,
        bounds: { min: [-88, -10, -88], max: [88, 32, 88] },
        boxes,
        spawns: [
            { p: [-64, 10.4, -64], yaw: Math.PI * 1.25 },
            { p: [64, 11.4, -64], yaw: Math.PI * 0.75 },
            { p: [-64, 8.4, 64], yaw: Math.PI * 1.75 },
            { p: [64, 12.4, 64], yaw: Math.PI * 0.25 },
            { p: [0, 13.4, 0], yaw: 0 },
            { p: [-32, 8.4, -32], yaw: Math.PI * 1.25 },
            { p: [32, 9.4, 32], yaw: Math.PI * 0.25 },
            { p: [0, 7.4, -64], yaw: Math.PI },
            { p: [-16, 0.1, -16], yaw: Math.PI * 0.5 },
            { p: [16, 0.1, 16], yaw: Math.PI * 1.5 },
            { p: [-48, 0.1, 16], yaw: 0 },
            { p: [48, 0.1, -16], yaw: Math.PI },
            { p: [16, 0.1, -48], yaw: Math.PI * 0.5 },
            { p: [-16, 0.1, 48], yaw: Math.PI * 1.5 },
            { p: [48, 0.1, 48], yaw: Math.PI * 1.25 },
            { p: [-48, 0.1, -48], yaw: Math.PI * 0.25 },
            { p: [0, 0.1, 80], yaw: 0 },
            { p: [0, 0.1, -80], yaw: Math.PI },
        ],
        jumpPads,
        vehicles: [
            { p: [24, 0.4, -24], yaw: Math.PI * 0.5, kind: "kart" },
            { p: [-24, 0.4, 24], yaw: Math.PI * 1.5, kind: "kart" },
            { p: [-80, 0.9, -80], yaw: Math.PI * 0.25, kind: "heli" },
            { p: [80, 0.9, -80], yaw: Math.PI * 0.75, kind: "heli" },
            { p: [-80, 0.9, 80], yaw: Math.PI * 1.75, kind: "heli" },
            { p: [80, 0.5, 80], yaw: Math.PI * 1.25, kind: "miniplane" },
            { p: [0, 0.5, -80], yaw: Math.PI, kind: "miniplane" },
            { p: [0, 0.9, 80], yaw: 0, kind: "miniufo" },
            { p: [-80, 0.9, 0], yaw: Math.PI * 0.5, kind: "miniufo" },
        ],
    };
}
// ---------------------------------------------------------------------------
// MAP — "VILLAGE": dense low houses around a central plaza. Tight CQC lanes,
// enterable houses, a couple of low roofs. Warm daytime palette.
// ---------------------------------------------------------------------------
const VL = {
    ground: "#b3a488",
    path: "#9a8b6f",
    house: "#c9b79a",
    house2: "#b7a07f",
    roof: "#8a5a3f",
    wall: "#a8946f",
    well: "#7d8a92",
};
function village() {
    const boxes = [
        { p: [0, -0.5, 0], s: [70, 1, 70], color: VL.ground },
        { p: [0, -0.48, 0], s: [8, 1, 70], color: "#9a8b6f" },
        { p: [0, -0.48, 0], s: [70, 1, 8], color: "#9a8b6f" },
        // perimeter
        { p: [0, 1.5, -34.5], s: [70, 5, 1], color: VL.wall },
        { p: [0, 1.5, 34.5], s: [70, 5, 1], color: VL.wall },
        { p: [-34.5, 1.5, 0], s: [1, 5, 70], color: VL.wall },
        { p: [34.5, 1.5, 0], s: [1, 5, 70], color: VL.wall },
    ];
    // ring of small houses (2-story, enterable) around the plaza
    const houses = [
        [-20, -20, 1], [20, -20, 2], [-20, 20, 2], [20, 20, 1],
        [-24, 2, 1], [24, -2, 1], [0, -24, 2], [2, 24, 1],
    ];
    for (const [cx, cz, style] of houses) {
        boxes.push(...building(cx, cz, 9, 9, style, { color: style === 1 ? VL.house : VL.house2, roof: VL.roof }));
    }
    // central plaza well + crates
    boxes.push({ p: [0, 0.75, 0], s: [3, 1.5, 3], color: VL.well }, { p: [-5, 0.5, 3], s: [1.6, 1, 1.6], color: VL.house2 }, { p: [5, 0.5, -3], s: [1.6, 1, 1.6], color: VL.house2 }, { p: [4, 0.5, 4], s: [1.4, 1, 1.4], color: VL.house }, { p: [-4, 0.5, -4], s: [1.4, 1, 1.4], color: VL.house });
    return {
        id: "village",
        name: "Village",
        modes: ["gungame", "arena", "ffa", "relay", "onegun", "treasure", "purge", "horde", "burnout", "plunder"],
        theme: "day",
        killZ: -10,
        bounds: { min: [-36, -12, -36], max: [36, 20, 36] },
        boxes,
        spawns: [
            { p: [-10, 0.1, -10], yaw: Math.PI * 1.25 },
            { p: [10, 0.1, -10], yaw: Math.PI * 0.75 },
            { p: [-10, 0.1, 10], yaw: Math.PI * 1.75 },
            { p: [10, 0.1, 10], yaw: Math.PI * 0.25 },
            { p: [-30, 0.1, 0], yaw: Math.PI * 0.5 },
            { p: [30, 0.1, 0], yaw: Math.PI * 1.5 },
            { p: [0, 0.1, -30], yaw: Math.PI },
            { p: [0, 0.1, 30], yaw: 0 },
            { p: [-20, 3.2, -20], yaw: Math.PI * 0.25 }, // house roof
            { p: [20, 3.2, 20], yaw: Math.PI * 1.25 },
            { p: [-16, 0.1, 16], yaw: Math.PI * 1.6 },
            { p: [16, 0.1, -16], yaw: Math.PI * 0.4 },
        ],
        // orange high-jump beams to reach the house rooftops
        jumpPads: [
            { p: [-13, 0.1, -13], power: 20 },
            { p: [13, 0.1, -13], power: 20 },
            { p: [-13, 0.1, 13], power: 20 },
            { p: [13, 0.1, 13], power: 20 },
        ],
    };
}
// ---------------------------------------------------------------------------
// MAP — "CANYON": open desert canyon. Big rock formations (varied sizes) as
// cover + a couple of stepped high perches. Longer sightlines with cover.
// ---------------------------------------------------------------------------
const CY = {
    sand: "#c8a86a",
    rock: "#a67c52",
    rock2: "#8f6a45",
    rockHi: "#c19a6b",
    shade: "#7a5a3a",
};
function canyon() {
    const boxes = [
        { p: [0, -0.5, 0], s: [90, 1, 80], color: CY.sand },
        // canyon walls (tall, irregular via segments)
        { p: [0, 4, -39], s: [90, 12, 2], color: CY.rock2 },
        { p: [0, 4, 39], s: [90, 12, 2], color: CY.rock2 },
        { p: [-44, 4, 0], s: [2, 12, 80], color: CY.rock2 },
        { p: [44, 4, 0], s: [2, 12, 80], color: CY.rock2 },
    ];
    // rock formations: [cx, cz, w, h, d]
    // NB the four tallest rocks (tops 3.5–4.0) are FLOOD ISLANDS: they must
    // clear the 3.0 m peak flood surface (see tide below) — one per quadrant.
    const rocks = [
        [-20, -12, 6, 3.6, 6], [-14, -14, 4, 2, 4],
        [18, 10, 7, 4, 5], [24, 6, 4, 2.5, 4],
        [-22, 14, 5, 3.5, 7], [22, -14, 6, 3.5, 6],
        [0, 0, 5, 2.5, 5], [4, -4, 3, 1.5, 3], [-4, 4, 3, 1.6, 3],
        [-10, 18, 4, 2, 4], [10, -18, 4, 2, 4],
    ];
    for (const [cx, cz, w, h, d] of rocks) {
        boxes.push({ p: [cx, h / 2, cz], s: [w, h, d], color: CY.rock });
    }
    // two stepped high perches (mesa) reachable by rock steps
    const mesa = (cx, cz) => {
        boxes.push({ p: [cx, 2.5, cz], s: [9, 5, 9], color: CY.rockHi });
        boxes.push(...staircase(cx, cz + 5.5, 5, "nz", 3, { color: CY.rock2 }));
    };
    mesa(-30, -20);
    mesa(30, 20);
    // FLASH FLOOD basin: a swimmable volume BURIED under the canyon floor at
    // rest (surface −0.7, invisible inside the floor slab) that the storm tide
    // (tide config below) raises up to ~3 m — rocks ≥3 m become islands and the
    // two 5 m mesas are the only dry ground. Colour must stay in the renderer's
    // WATER_COLORS set; h > 2.5 so it renders as a surface plane, not a box.
    boxes.push({ p: [0, -2.25, 0], s: [88, 3.1, 76], color: "#2d6774", tag: "water" });
    return {
        id: "canyon",
        name: "Canyon",
        modes: ["gungame", "burnout", "plunder"],
        theme: "day",
        killZ: -10,
        bounds: { min: [-46, -12, -41], max: [46, 22, 41] },
        boxes,
        // Desert thunderstorm: pow 6 makes it a FLASH flood — dry ~2/3 of the
        // cycle, then the water races from wet-feet to full flood in ~5 s and
        // crests (tide > 2.2) for ~14 s per 75 s cycle. Peak surface = 3.7 − 0.7
        // = 3.0 m: four rocks stay out as islands, the mesas are the dry ground.
        // Lightning included — the flood herds everyone onto the same few perches;
        // the storm keeps them moving once they're there.
        tide: { periodS: 75, amp: 3.7, highAt: 2.2, pow: 6, label: "⛈️ FLASH FLOOD — get to high ground!" }, // lightning removed 2026-07-23 (owner: "laser out of nowhere")
        spawns: [
            { p: [-30, 0.1, 8], yaw: Math.PI * 0.5 },
            { p: [30, 0.1, -8], yaw: Math.PI * 1.5 },
            { p: [-8, 0.1, -30], yaw: Math.PI },
            { p: [8, 0.1, 30], yaw: 0 },
            { p: [0, 0.1, 0], yaw: Math.PI * 0.25 },
            { p: [-38, 0.1, -30], yaw: Math.PI * 0.4 },
            { p: [38, 0.1, 30], yaw: Math.PI * 1.4 },
            { p: [38, 0.1, -30], yaw: Math.PI * 1.6 },
            { p: [-38, 0.1, 30], yaw: Math.PI * 0.6 },
            { p: [-30, 5.2, -20], yaw: Math.PI * 0.25 }, // mesa top
            { p: [30, 5.2, 20], yaw: Math.PI * 1.25 },
            { p: [0, 0.1, 20], yaw: 0 },
        ],
    };
}
// ---------------------------------------------------------------------------
// MAP — "ZARYA ISLAND": a huge abandoned Soviet island. Brutalist concrete
// apartment blocks (enterable), a ruined monument, a radar tower, bunkers,
// roads for karts, a helipad, all ringed by water. Vehicles + pickups spawn.
// ---------------------------------------------------------------------------
const IS = {
    water: "#2f5f77",
    shore: "#b0a074",
    ground: "#8c8a7c",
    concrete: "#8f8d84",
    concreteDk: "#6b6960",
    concreteLt: "#a6a49a",
    roof: "#57554e",
    road: "#54534c",
    rust: "#8a5533",
    red: "#a1382c",
    glassDk: "#3a4048",
};
/**
 * A flat floor slab built as per-row segments so it can have rectangular
 * holes punched in it (for stairwell shafts down to a basement). Keeps the box
 * count low (~one box per row, split around holes) vs. tiling the whole area.
 */
function floorWithHoles(size, y, thick, color, holes) {
    const out = [];
    const half = size / 2;
    const rowH = 10;
    for (let z0 = -half; z0 < half - 0.01; z0 += rowH) {
        const zc = z0 + rowH / 2;
        const hs = holes
            .filter((h) => Math.abs(h[1] - zc) < h[3] / 2 + rowH / 2)
            .map((h) => [h[0] - h[2] / 2, h[0] + h[2] / 2])
            .sort((a, b) => a[0] - b[0]);
        let x = -half;
        for (const [hx0, hx1] of hs) {
            if (hx0 > x + 0.1) {
                const w = hx0 - x;
                out.push({ p: [x + w / 2, y, zc], s: [w, thick, rowH], color });
            }
            x = Math.max(x, hx1);
        }
        if (x < half - 0.1) {
            const w = half - x;
            out.push({ p: [x + w / 2, y, zc], s: [w, thick, rowH], color });
        }
    }
    return out;
}
function zaryaIsland() {
    const B = [];
    // huge water plane (low) — with the central island footprint punched out so
    // the underground metro tunnels have head-room (the sea only shows at edges).
    B.push(...floorWithHoles(320, -3.0, 1, IS.water, [[0, 0, 138, 138]]));
    // ground floor with stairwell holes down to the underground metro tunnels
    const holes = [
        [-16, 8, 5, 5], // Paris-metro surface entrance (plaza)
        [-42, -46, 4, 4], [42, -46, 4, 4], // apartment-block basements
        [-44, 46, 4, 4], [44, 46, 4, 4],
        [-52, 8, 4, 4], [52, 8, 4, 4],
    ];
    B.push(...floorWithHoles(150, -0.5, 1, IS.ground, holes));
    // sandy shore ring (slightly lower than ground) — holed too so the stairwell
    // shafts pass through; it doubles as the underground tunnels' ceiling.
    B.push(...floorWithHoles(176, -1.2, 1, IS.shore, holes));
    // low sea walls around the island edge (concrete)
    for (const [px, pz, sx, sz] of [
        [0, -74, 150, 2], [0, 74, 150, 2], [-74, 0, 2, 150], [74, 0, 2, 150],
    ]) {
        B.push({ p: [px, 0.4, pz], s: [sx, 2.4, sz], color: IS.concreteDk });
    }
    // main road (cross) for karts + a ring road
    B.push({ p: [0, -0.44, 0], s: [10, 1, 150], color: IS.road });
    B.push({ p: [0, -0.44, 0], s: [150, 1, 10], color: IS.road });
    // ring roads at z=±40 — split with gaps at x=±42 so the apartment-block
    // basement stairwells there aren't pinched by the low road slab overhead.
    for (const rz of [-40, 40]) {
        B.push({ p: [0, -0.43, rz], s: [64, 1, 8], color: IS.road }); // centre span
        B.push({ p: [-51, -0.43, rz], s: [8, 1, 8], color: IS.road }); // left stub
        B.push({ p: [51, -0.43, rz], s: [8, 1, 8], color: IS.road }); // right stub
    }
    // ---- Soviet apartment blocks (brutalist, enterable, varied heights) ----
    const blocks = [
        [-42, -42, 20, 14, 4], [42, -42, 16, 16, 5], [-44, 40, 18, 16, 5],
        [44, 42, 20, 14, 3], [-52, 0, 12, 22, 6], [52, 0, 12, 22, 4],
    ];
    for (const [cx, cz, w, d, st] of blocks) {
        B.push(...building(cx, cz, w, d, st, { color: IS.concrete, roof: IS.roof }));
        // window bands (dark stripe per floor) on the +/-x faces
        for (let f = 0; f < st; f++) {
            B.push({ p: [cx, f * STORY + 1.6, cz - d / 2 - 0.05], s: [w * 0.8, 0.9, 0.12], color: IS.glassDk });
        }
        // a rusted external staircase to the roof
        B.push(...staircase(cx + w / 2 + 1, cz - d / 2, st * STORY, "pz", 2, { color: IS.rust }));
    }
    // ---- central ruined monument: a tall concrete obelisk on a stepped base ----
    B.push({ p: [0, 0.6, 0], s: [14, 1.2, 14], color: IS.concreteLt });
    B.push({ p: [0, 1.6, 0], s: [9, 1.0, 9], color: IS.concrete });
    B.push({ p: [0, 7, 0], s: [3, 12, 3], color: IS.concreteLt }); // obelisk
    B.push({ p: [0, 13.4, 0], s: [1.4, 1.6, 1.4], color: IS.red }); // red star block on top
    // toppled slabs / rubble around it (cover)
    B.push({ p: [-8, 0.6, 6], s: [4, 1.2, 1.4], color: IS.concreteDk });
    B.push({ p: [7, 0.9, -7], s: [1.4, 1.8, 5], color: IS.concreteDk });
    B.push({ p: [8, 0.5, 7], s: [3, 1.0, 3], color: IS.concreteDk });
    // ---- radar / antenna tower (tall) with a platform ----
    const tx = -20, tz = 22;
    B.push({ p: [tx, 5, tz], s: [3, 10, 3], color: IS.rust });
    B.push({ p: [tx, 10.2, tz], s: [6, 0.4, 6], color: IS.concreteDk }); // platform
    B.push({ p: [tx, 11.6, tz], s: [4.5, 2.4, 0.3], color: IS.concreteLt }); // dish backboard
    B.push(...staircase(tx + 2, tz - 4, 10, "pz", 1.8, { color: IS.rust }));
    // ---- bunkers (low concrete, roof walkable) ----
    for (const [bx, bz] of [[20, -18], [-18, -16], [22, 18], [-22, 16]]) {
        B.push({ p: [bx, 1, bz], s: [8, 2, 6], color: IS.concreteDk });
        B.push({ p: [bx, 2.15, bz], s: [8, 0.3, 6], color: IS.roof });
        // entrance lip
        B.push({ p: [bx, 0.6, bz - 3.2], s: [2, 1.2, 0.4], color: IS.concrete });
    }
    // ---- helipad (low, walkable pad) for the helicopter ----
    const hx = 34, hz = -20;
    B.push({ p: [hx, 0.15, hz], s: [14, 0.3, 14], color: IS.concreteDk }); // top ≈0.3 < stepHeight
    B.push({ p: [hx, 0.32, hz], s: [8, 0.05, 1], color: IS.red }); // H mark
    B.push({ p: [hx, 0.32, hz], s: [1, 0.05, 6], color: IS.red });
    // ---- scattered debris / cargo (cover) ----
    for (const [cx, cz, c] of [
        [-30, 10, IS.rust], [30, -8, IS.red], [10, 26, IS.rust],
        [-12, -30, IS.red], [14, -30, IS.rust], [-30, -8, IS.concreteDk],
    ]) {
        B.push({ p: [cx, 0.9, cz], s: [3, 1.8, 2.4], color: c });
    }
    // ---- UNDERGROUND METRO: an undercroft connecting every building basement ----
    const TUN = { floor: "#22262f", wall: "#2c313c", pillar: "#3a4150", tile: "#3f4a5a", metroGreen: "#0f7a4a", metroGold: "#e8c341" };
    const FLOOR_Y = -3.6; // walkable tunnel floor top
    const CEIL_Y = -1.7; // tunnel ceiling underside (= bottom of the shore slab)
    const area = 130;
    // tunnel floor (solid slab); the holed shore slab above serves as the ceiling
    B.push({ p: [0, FLOOR_Y - 0.3, 0], s: [area, 0.6, area], color: TUN.floor });
    // perimeter walls so the undercroft is enclosed
    const wallH = CEIL_Y - FLOOR_Y + 0.4, wallCy = (CEIL_Y + FLOOR_Y) / 2;
    for (const [px, pz, sx, sz] of [
        [0, area / 2, area, 0.6], [0, -area / 2, area, 0.6], [area / 2, 0, 0.6, area], [-area / 2, 0, 0.6, area],
    ])
        B.push({ p: [px, wallCy, pz], s: [sx, wallH, sz], color: TUN.wall });
    // support pillars in a grid (tunnel columns) + guiding corridor walls
    for (let gx = -50; gx <= 50; gx += 25)
        for (let gz = -50; gz <= 50; gz += 25) {
            B.push({ p: [gx, wallCy, gz], s: [2, wallH, 2], color: TUN.pillar });
        }
    // low tiled corridor walls forming lanes between the pillars (metro feel)
    for (let gx = -37.5; gx <= 37.5; gx += 25)
        B.push({ p: [gx, FLOOR_Y + 0.9, 0], s: [0.5, 1.8, 90], color: TUN.tile, glow: "#9fe0ff" });
    // descending stairs inside each shaft (surface hole → tunnel floor at -3.6).
    // Kept steep/short (4.8 m run) so they bottom out fast and don't run under
    // low road slabs (which would pinch head-room mid-descent).
    for (const [hx, hz] of holes) {
        for (let i = 0; i <= 8; i++) {
            const y = -0.3 - i * (3.3 / 8);
            const z = hz - 2.4 + i * (4.8 / 8);
            B.push({ p: [hx, y, z], s: [3.6, 0.5, 0.9], color: TUN.tile, glow: i === 0 ? "#9fe0ff" : undefined });
        }
    }
    // ---- Paris "MÉTROPOLITAIN" entrance over the plaza shaft (art-nouveau) ----
    const [mx, , mz] = [holes[0][0], 0, holes[0][1]];
    // green railings around the hole (open on the -z entry side)
    for (const [px, pz, sx, sz] of [
        [mx, mz + 3.4, 6, 0.3], [mx - 3.1, mz, 0.3, 7], [mx + 3.1, mz, 0.3, 7],
    ])
        B.push({ p: [px, 0.6, pz], s: [sx, 1.2, sz], color: TUN.metroGreen, glow: TUN.metroGreen });
    // two ornate posts + an arch sign board reading METROPOLITAIN (gold on green)
    for (const sx of [-3.1, 3.1]) {
        B.push({ p: [mx + sx, 2.2, mz + 3.4], s: [0.5, 3.2, 0.5], color: TUN.metroGreen, glow: TUN.metroGreen });
        B.push({ p: [mx + sx, 3.9, mz + 3.4], s: [0.9, 0.5, 0.9], color: TUN.metroGold, glow: TUN.metroGold }); // lamp finial
    }
    B.push({ p: [mx, 4.1, mz + 3.4], s: [7, 1.1, 0.35], color: TUN.metroGreen, glow: TUN.metroGold }); // sign board
    B.push({ p: [mx, 4.1, mz + 3.4], s: [6, 0.5, 0.4], color: TUN.metroGold, glow: TUN.metroGold }); // "MÉTROPOLITAIN" plate
    return {
        id: "zarya_island",
        name: "Zarya Island",
        modes: ["gungame", "ffa", "onegun", "treasure", "purge", "horde", "burnout", "plunder"],
        theme: "day",
        killZ: -6,
        bounds: { min: [-78, -8, -78], max: [78, 24, 78] },
        boxes: B,
        spawns: [
            { p: [-30, 0.1, -30], yaw: Math.PI * 1.25 },
            { p: [30, 0.1, -30], yaw: Math.PI * 0.75 },
            { p: [30, 0.1, 30], yaw: Math.PI * 0.25 },
            { p: [-30, 0.1, 30], yaw: Math.PI * 1.75 },
            { p: [0, 0.1, -55], yaw: Math.PI },
            { p: [0, 0.1, 55], yaw: 0 },
            { p: [-30, 0.1, 0], yaw: Math.PI * 0.5 }, // open ground (was inside a building)
            { p: [30, 0.1, 0], yaw: Math.PI * 1.5 }, // open ground (was inside a building)
            { p: [20, 2.4, -18], yaw: Math.PI }, // bunker roof (easy hop down)
            { p: [10, 0.1, 10], yaw: Math.PI * 0.6 }, // near monument
            { p: [-14, 0.1, -8], yaw: 0 }, // open ground
            { p: [14, 0.1, 8], yaw: Math.PI }, // open ground
        ],
        pickups: [
            { p: [0, 0.2, 20], kind: "health" },
            { p: [0, 0.2, -20], kind: "health" },
            { p: [-42, 0.2, 0], kind: "armor" },
            { p: [42, 0.2, 0], kind: "armor" },
            { p: [0, 2.4, 0], kind: "health" }, // atop monument base
            { p: [20, 2.5, 18], kind: "armor" }, // bunker roof
            { p: [-20, 0.2, -20], kind: "grenade" },
            { p: [20, 0.2, 20], kind: "grenade" },
            { p: [-10, 0.2, 14], kind: "antigrav" }, // one-shot map-wide moon gravity
            { p: [-12, 0.2, 12], kind: "grenade" },
            { p: [-34, 0.3, 20], kind: "jetpack" },
            { p: [34, 0.3, 20], kind: "rocket" },
        ],
        jumpPads: [
            { p: [-14, 0.1, 8] },
            { p: [14, 0.1, -8] },
            { p: [-20, 10.4, 22] }, // atop the radar platform, launch to rooftops
            // ORANGE high-jump beams to reach the apartment-block roofs
            { p: [-42, 0.1, -30], power: 22 },
            { p: [42, 0.1, -30], power: 22 },
            { p: [-44, 0.1, 28], power: 22 },
            { p: [44, 0.1, 30], power: 22 },
        ],
        vehicles: [
            { p: [-6, 0.4, 30], yaw: 0, kind: "kart" },
            { p: [6, 0.4, -30], yaw: Math.PI, kind: "kart" },
            { p: [34, 0.9, -20], yaw: Math.PI * 0.5, kind: "heli" },
        ],
    };
}
export const MAP_DOWNTOWN = downtown();
export const MAP_COMPOUND = compound();
/**
 * SKYHAVEN — a huge futuristic battle-royale island (Chongqing-esque density):
 * skyscrapers with internal stairs + grav-lift "elevators", sky bridges, a
 * train on elevated rails you can fight in/on, an airport with a boardable
 * A380-style jet, neon everywhere, and beaches ringing the island.
 */
function neoIsland() {
    const B = [];
    const NI = {
        water: "#12506e", sand: "#d8c088", ground: "#7f8a7a", plaza: "#5b6270",
        tower: "#39435a", tower2: "#2d3547", glass: "#57d6ff", neon: "#ff2ec4",
        rail: "#3a404c", train: "#c33a3a", trainDk: "#7a2420", plane: "#e9edf2",
        planeDk: "#b7c0cc", runway: "#2b313c", stripe: "#e8c341", roof: "#454f63",
    };
    const jumpPads = [];
    const pickups = [];
    // ocean + island landmass + beach apron — the sand AND water slabs are
    // built with four 7 m drop-holes at the edge midpoints: the entrances to
    // the underground neon tunnel network (the water slab is the tunnel roof).
    const HOLES = [[104, 0], [-104, 0], [0, 104], [0, -104]];
    const HW = 7;
    const holedSlab = (y, t, size, color, holes = HOLES) => {
        // decompose a square slab into z-bands, skipping hole spans
        const zBreaks = [-size / 2, ...holes.flatMap(([, hz]) => [hz - HW / 2, hz + HW / 2]), size / 2]
            .filter((v) => v >= -size / 2 && v <= size / 2).sort((a, b) => a - b);
        for (let i = 0; i + 1 < zBreaks.length; i++) {
            const z0 = zBreaks[i], z1 = zBreaks[i + 1];
            if (z1 - z0 < 0.01)
                continue;
            const zm = (z0 + z1) / 2;
            const inBand = holes.filter(([, hz]) => zm > hz - HW / 2 && zm < hz + HW / 2);
            const xBreaks = [-size / 2, ...inBand.flatMap(([hx]) => [hx - HW / 2, hx + HW / 2]), size / 2]
                .filter((v) => v >= -size / 2 && v <= size / 2).sort((a, b) => a - b);
            for (let j = 0; j + 1 < xBreaks.length; j++) {
                const x0 = xBreaks[j], x1 = xBreaks[j + 1];
                if (x1 - x0 < 0.01)
                    continue;
                const xm = (x0 + x1) / 2;
                if (inBand.some(([hx]) => xm > hx - HW / 2 && xm < hx + HW / 2))
                    continue; // the hole
                B.push({ p: [xm, y, zm], s: [x1 - x0, t, z1 - z0], color });
            }
        }
    };
    // the arena SKYLIGHT: every layer over the centre gets a (0,0) hole so the
    // violet mega-beam can fire you clean through to the sky
    holedSlab(-1.2, 1, 420, NI.water, [...HOLES, [0, 0]]);
    holedSlab(-0.2, 0.8, 230, NI.sand, [...HOLES, [0, 0]]); // beach base
    holedSlab(-0.1, 0.8, 186, NI.ground, [[0, 0]]); // inland
    holedSlab(-0.05, 0.8, 120, NI.plaza, [[0, 0]]); // central plaza/city floor
    // ---- THE UNDERCITY: four neon tunnels from the edge holes to a central
    // underground arena (floor at −6.35; the water slab overhead seals it) ----
    const TUN_Y = -6.6, TUN_W = 8, ARENA = 36;
    for (const [hx, hz] of HOLES) {
        const ax = Math.sign(hx), az = Math.sign(hz); // tunnel direction (unit)
        const horiz = ax !== 0;
        const inner = ARENA / 2 - 2; // overlap the arena floor edge
        const outer = (horiz ? Math.abs(hx) : Math.abs(hz)) + HW / 2 + 1;
        const len = outer - inner, mid = (outer + inner) / 2;
        const fx = horiz ? ax * mid : 0, fz = horiz ? 0 : az * mid;
        // floor
        B.push({ p: [fx, TUN_Y, fz], s: [horiz ? len : TUN_W, 0.5, horiz ? TUN_W : len], color: NI.runway });
        // side walls (up to the water-slab ceiling)
        for (const side of [-1, 1]) {
            B.push({
                p: [fx + (horiz ? 0 : side * (TUN_W / 2 + 0.25)), -4, fz + (horiz ? side * (TUN_W / 2 + 0.25) : 0)],
                s: [horiz ? len : 0.5, 5.2, horiz ? 0.5 : len], color: NI.tower2,
            });
            // neon floor trim running the whole tunnel — the LED gutter look
            B.push({
                p: [fx + (horiz ? 0 : side * (TUN_W / 2 - 0.4)), TUN_Y + 0.32, fz + (horiz ? side * (TUN_W / 2 - 0.4) : 0)],
                s: [horiz ? len : 0.3, 0.14, horiz ? 0.3 : len], color: side > 0 ? NI.neon : NI.glass, glow: side > 0 ? NI.neon : NI.glass,
            });
        }
        // dead-end wall behind the drop hole
        B.push({ p: [horiz ? ax * outer : 0, -4, horiz ? 0 : az * outer], s: [horiz ? 0.5 : TUN_W + 1, 5.2, horiz ? TUN_W + 1 : 0.5], color: NI.tower2 });
        // jump pad under the hole — ride it back up to the surface
        jumpPads.push({ p: [hx, TUN_Y + 0.3, hz], power: Math.sqrt(2 * 24 * 12) });
        // surface gate: two glowing pylons + lintel marking the entrance
        for (const side of [-1, 1]) {
            B.push({
                p: [hx + (horiz ? 0 : side * (HW / 2 + 0.8)), 1.3, hz + (horiz ? side * (HW / 2 + 0.8) : 0)],
                s: [0.8, 2.6, 0.8], color: NI.tower2, glow: NI.neon,
            });
        }
        B.push({ p: [hx, 2.9, hz], s: [horiz ? 0.8 : HW + 2.4, 0.5, horiz ? HW + 2.4 : 0.8], color: NI.neon, glow: NI.neon });
    }
    // the arena: CHIBRE STATION — a glowing underground fight pit
    B.push({ p: [0, TUN_Y, 0], s: [ARENA, 0.5, ARENA], color: NI.plaza });
    for (const side of [-1, 1]) {
        for (const horiz of [true, false]) {
            for (const seg of [-1, 1]) {
                // wall segments leaving an 8 m gap at each tunnel mouth
                const c = seg * (ARENA / 4 + 2.5);
                B.push({
                    p: [horiz ? side * (ARENA / 2 + 0.25) : c, -4, horiz ? c : side * (ARENA / 2 + 0.25)],
                    s: [horiz ? 0.5 : ARENA / 2 - 5, 5.2, horiz ? ARENA / 2 - 5 : 0.5], color: NI.tower2,
                });
            }
        }
    }
    // glowing pillars + a neon ring floor inlay
    for (const px of [-9, 9])
        for (const pz of [-9, 9]) {
            B.push({ p: [px, -4, pz], s: [1.2, 5.2, 1.2], color: NI.tower2, glow: NI.neon });
        }
    B.push({ p: [0, TUN_Y + 0.3, 0], s: [10, 0.16, 10], color: NI.glass, glow: NI.glass });
    // the VIOLET MEGA-BEAM: fires you through the skylight ABOVE the towers
    jumpPads.push({ p: [0, TUN_Y + 0.35, 0], power: 62, color: "#a54dff" });
    pickups.push({ p: [-9, TUN_Y + 0.8, 0], kind: "armor" });
    pickups.push({ p: [9, TUN_Y + 0.8, 0], kind: "dmg" });
    // one LANCE-ROQUETTE chest guards the west tunnel
    pickups.push({ p: [-42, TUN_Y + 0.8, 0], kind: "rocket" });
    pickups.push({ p: [42, TUN_Y + 0.8, 0], kind: "imperion" }); // beam rifle at the other tunnel end
    pickups.push({ p: [0, TUN_Y + 0.8, 24], kind: "antigrav" }); // one-shot map-wide moon gravity
    // ring road + cross avenue for karts — split around the skylight
    for (const rd of [-1, 1]) {
        B.push({ p: [0, 0.0, rd * 45.75], s: [12, 0.82, 84.5], color: NI.runway });
        B.push({ p: [rd * 45.75, 0.0, 0], s: [84.5, 0.82, 12], color: NI.runway });
    }
    // ---- skyscrapers: [cx, cz, w, d, stories] ----
    const towers = [
        [-44, -34, 15, 15, 12],
        [-14, -50, 13, 13, 9],
        [22, -34, 17, 17, 15], // tallest
        [48, 6, 13, 13, 8],
        [8, 16, 15, 15, 11],
        [-40, 22, 13, 13, 7],
    ];
    const towerTop = {};
    const holeW = 2.4;
    for (const [cx, cz, w, d, st] of towers) {
        const H = st * STORY;
        towerTop[`${cx},${cz}`] = H;
        // build the shell, then swap the SOLID roof for a holed one so the
        // stairwell/grav-lift column opens onto the roof (the roof hole lines up
        // with the interior floor holes on the +X side).
        const shell = building(cx, cz, w, d, st, { color: NI.tower, glow: NI.glass, roof: NI.roof, hollow: true });
        const ri = shell.findIndex((b) => Math.abs(b.p[1] - (H + 0.15)) < 0.01 && Math.abs(b.s[0] - w) < 0.01 && b.s[1] < 0.5);
        if (ri >= 0) {
            shell.splice(ri, 1);
            shell.push({ p: [cx - holeW / 2, H + 0.15, cz], s: [w - WALL_T - holeW, 0.3, d - WALL_T], color: NI.roof });
        }
        B.push(...shell);
        // grav-lift up the +X stairwell column, through every floor hole AND the
        // roof hole, landing you on the roof.
        const liftX = cx + w / 2 - 1.4;
        // violet beam, PURE vertical (fling 0): the column is a tight shaft
        // a VIOLENT launch: rocket ~30m past the roof — higher AND faster (power
        // IS launch velocity, so more height = a snappier ride up)
        jumpPads.push({ p: [liftX, 0.2, cz], power: Math.sqrt(2 * 24 * (H + 30)), color: "#a54dff", fling: 0 });
        // neon crown + a reward pickup on the roof (away from the hole)
        B.push({ p: [cx, H + 1.6, cz], s: [w * 0.5, 0.5, w * 0.5], color: NI.neon, glow: NI.neon });
        // LED facade: glowing corner pillars + two horizontal bands per tower —
        // the neon-futuristic skyline reads from anywhere on the island
        for (const sx of [-1, 1])
            for (const sz of [-1, 1]) {
                B.push({ p: [cx + sx * (w / 2 + 0.15), H / 2, cz + sz * (d / 2 + 0.15)], s: [0.3, H, 0.3], color: NI.neon, glow: sx * sz > 0 ? NI.neon : NI.glass });
            }
        // light bands are RINGS hugging the facade — the old full-width slab cut
        // a solid "blue floor" straight through the hollow atrium
        for (const bandY of [H * 0.35, H * 0.7]) {
            B.push({ p: [cx, bandY, cz + d / 2 + 0.18], s: [w + 0.35, 0.25, 0.12], color: NI.glass, glow: NI.glass });
            B.push({ p: [cx, bandY, cz - d / 2 - 0.18], s: [w + 0.35, 0.25, 0.12], color: NI.glass, glow: NI.glass });
            B.push({ p: [cx + w / 2 + 0.18, bandY, cz], s: [0.12, 0.25, d + 0.35], color: NI.glass, glow: NI.glass });
            B.push({ p: [cx - w / 2 - 0.18, bandY, cz], s: [0.12, 0.25, d + 0.35], color: NI.glass, glow: NI.glass });
        }
        pickups.push({ p: [cx - w / 2 + 1.5, H + 0.6, cz], kind: st > 10 ? "armor" : "health" });
    }
    // sky bridges (thin walkable slabs) between nearby towers at a shared
    // height. TRIMMED to the gap between the facades — the old center-to-center
    // span sliced straight through the tower interiors at mid-height (the
    // "platform blocking inside the building" report).
    const bridge = (x1, z1, x2, z2, y, trimA = 8.5, trimB = 8.5) => {
        const mx = (x1 + x2) / 2, mz = (z1 + z2) / 2;
        const horiz = Math.abs(x2 - x1) > Math.abs(z2 - z1);
        const len = Math.max(4, Math.hypot(x2 - x1, z2 - z1) - trimA - trimB);
        B.push({ p: [mx, y, mz], s: [horiz ? len : 2.6, 0.3, horiz ? 2.6 : len], color: NI.tower2, glow: NI.glass });
        // low railings
        if (horiz) {
            B.push({ p: [mx, y + 0.6, mz + 1.2], s: [len, 1, 0.12], color: NI.tower2 });
            B.push({ p: [mx, y + 0.6, mz - 1.2], s: [len, 1, 0.12], color: NI.tower2 });
        }
        else {
            B.push({ p: [mx + 1.2, y + 0.6, mz], s: [0.12, 1, len], color: NI.tower2 });
            B.push({ p: [mx - 1.2, y + 0.6, mz], s: [0.12, 1, len], color: NI.tower2 });
        }
    };
    bridge(-44, -34, -14, -50, 21, 9.5, 8); // link two towers up high (gap-only)
    bridge(22, -34, 8, 16, 24, 10, 9);
    bridge(8, 16, 48, 6, 18);
    bridge(-40, 22, 8, 16, 18);
    // ---- CIRCULAR TRAIN: wagons loop the island on an elevated ring track and
    // carry riders. The wagons themselves are a moving platform (rendered +
    // simulated dynamically); here we place the boarding station + support ring.
    const railY = 2.2, ringR = 30; // small central loop — clear of towers + airport
    // support pylons dotted around the ring (client draws the rail itself)
    for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        B.push({ p: [ringR * Math.cos(a), railY / 2 - 0.3, ringR * Math.sin(a)], s: [1.4, railY, 1.4], color: NI.rail });
    }
    // boarding station at angle 0 (x=+ringR, z=0): a platform + stairs up to it
    const stX = ringR;
    // just the low boarding platform — the station BUILDING (wall + canopy)
    // was removed per feedback; the train itself still loops and carries
    B.push({ p: [stX, railY - 0.2, 0], s: [7, 0.4, 10], color: NI.rail }); // platform
    B.push(...staircase(stX - 5, 0, railY, "px", 3.5, { color: NI.rail }));
    pickups.push({ p: [stX, railY + 0.3, 0], kind: "grenade" });
    // ---- AIRPORT: runway + a big boardable A380-style jet ----
    const rwX = 66;
    B.push({ p: [rwX, 0.02, 0], s: [34, 0.84, 150], color: NI.runway }); // runway
    for (let z = -66; z <= 66; z += 12)
        B.push({ p: [rwX, 0.05, z], s: [1.4, 0.86, 5], color: NI.stripe }); // centreline
    // terminal building
    B.push(...building(rwX + 22, -30, 16, 20, 2, { color: "#4a5568", glow: NI.glass, roof: NI.roof, hollow: true }));
    // (the A380 itself is a moving/flying platform — see the `plane` config below;
    // it dips down onto the runway here so you can board, then takes off + tours)
    pickups.push({ p: [rwX, 0.4, 10], kind: "armor" });
    pickups.push({ p: [rwX, 0.4, -20], kind: "health" });
    // open ARENA zone (north inland, clear of the runway + train ring): mostly
    // empty ground for open fights, dotted with cover crates + low walls to hide
    // behind. Everything sits between z≈44..78, x≈-38..38 — away from the airport
    // (x≈66) and the central train loop (r=30).
    const arenaCover = [
        [-26, 52, 4, 2.4, 4], [-10, 60, 6, 1.4, 2], [8, 50, 2, 1.8, 6],
        [24, 58, 4, 2.4, 4], [0, 68, 5, 1.2, 2], [-20, 70, 3, 3, 3],
        [18, 72, 2.5, 2.2, 2.5], [-34, 62, 2, 1.6, 8], [34, 64, 2, 1.6, 8],
        [-4, 44, 8, 1, 2],
    ];
    for (const [cx, cz, w, hh, d] of arenaCover)
        B.push({ p: [cx, hh / 2, cz], s: [w, hh, d], color: NI.tower2, glow: NI.glass });
    pickups.push({ p: [0, 0.4, 60], kind: "armor" }, { p: [-24, 0.4, 66], kind: "health" }, { p: [24, 0.4, 52], kind: "grenade" });
    // orange high-jump beams on the plaza to reach low roofs / sky bridges
    jumpPads.push({ p: [-8, 0.2, -8], power: 20 }, { p: [8, 0.2, 8], power: 20 }, { p: [0, 0.2, -20], power: 22 });
    // scattered pickups + jetpack/rocket chests + more grenades on the ground
    pickups.push({ p: [-8, 0.4, 8], kind: "health" }, { p: [8, 0.4, -8], kind: "armor" }, { p: [-20, 0.4, 0], kind: "grenade" }, { p: [20, 0.4, 0], kind: "grenade" }, { p: [0, 0.4, 30], kind: "health" }, { p: [0, 0.4, -30], kind: "armor" }, { p: [-30, 0.3, -10], kind: "jetpack" }, { p: [34, 0.3, 26], kind: "rocket" }, { p: [-30, 0.3, 40], kind: "jetpack" }, { p: [40, 0.3, -40], kind: "rocket" });
    // COLLISION for the giant boombox + turntable (their detailed visuals live in
    // decor.ts). tag:"nodraw" → CollisionWorld keeps them (client+server, so you
    // can stand on them) but the renderer/preview skip drawing these plain boxes.
    B.push({ p: [0, 8, -94], s: [30, 14, 8], color: "#20242e", tag: "nodraw" }, // boombox body
    { p: [0, 2, 86], s: [28, 4, 22], color: "#20242e", tag: "nodraw" });
    return {
        id: "neo_island",
        name: "Skyhaven (Battle Royale)",
        modes: ["gungame", "ffa", "relay", "onegun", "treasure", "purge", "horde", "burnout", "plunder"],
        theme: "day",
        killZ: -8,
        bounds: { min: [-115, -12, -115], max: [115, 90, 115] },
        boxes: B,
        train: { cx: -1, cz: 5, radius: ringR, y: railY, wagons: 5, omega: 0.16, arc: 0.2, half: [2.6, 2.6] }, // nudged off the (8,16)/(22,-34) towers
        // A380 circuit: passes low over the runway (x≈66,z0) to board, climbs to
        // tour the island at altitude, then descends back. Deck pads are rideable.
        // the saucer CRUISES above the skyline (38–48 m) — it used to dip to
        // ground level and carve straight through the towers
        // deckR 18: the SAUCER VISUAL is 19m — at 15 there was a 4m ring at the
        // rim where you stood on the visible deck but fell through (no collider)
        plane: { cx: 0, cz: 0, radius: 66, baseY: 38, climbY: 10, omega: 0.09, padHalf: 4, fuselage: [-13, 0, 13], wing: 9, deckR: 18 },
        spawns: [
            { p: [-8, 0.6, -8], yaw: Math.PI * 0.25 }, { p: [8, 0.6, 8], yaw: Math.PI * 1.25 },
            { p: [-8, 0.6, 8], yaw: Math.PI * 1.75 }, { p: [8, 0.6, -8], yaw: Math.PI * 0.75 },
            { p: [-30, 0.6, -30], yaw: Math.PI * 0.25 }, { p: [30, 0.6, 30], yaw: Math.PI * 1.25 },
            { p: [40, 0.6, -20], yaw: Math.PI }, { p: [-40, 0.6, 40], yaw: 0 },
            { p: [0, 0.6, 46], yaw: 0 }, { p: [0, 0.6, -46], yaw: Math.PI },
            { p: [56, 0.6, 40], yaw: Math.PI * 1.5 }, { p: [-56, 0.6, -20], yaw: Math.PI * 0.5 },
            { p: [rwX, 0.6, -40], yaw: Math.PI }, { p: [ringR + 6, 0.6, 0], yaw: Math.PI },
        ],
        jumpPads,
        pickups,
        vehicles: [
            // r=20 / r=40 — the elevated train ring runs at r=30, keep karts clear
            { p: [-6, 0.6, 19], yaw: 0, kind: "kart" }, { p: [6, 0.6, -19], yaw: Math.PI, kind: "kart" },
            { p: [41, 0.6, 6], yaw: Math.PI * 0.5, kind: "kart" },
            // NOT at (0,0) — that's the arena skylight now; it used to drop in
            { p: [72, 0.6, 34], yaw: Math.PI * 0.75, kind: "heli" },
            { p: [-74, 0.6, -46], yaw: Math.PI * 0.25, kind: "heli" },
        ],
    };
}
export const MAP_ROOFTOPS = rooftops();
export const MAP_ROOFTOPS_XL = rooftopsXL();
/** Rooftops XL's exact city plan, stretched into a Skyhaven-scale synthwave
 * skyline. Ground stairs are replaced by violet grav-beams so every roof stays
 * reachable despite the 5x vertical scale. */
function rooftopSpiderweb() {
    const src = rooftopsXL();
    const verticalScale = 5;
    const violet = "#a54dff", glass = "#57d6ff", tower = "#2d3547", roof = "#454f63";
    const boxes = src.boxes
        .filter((b) => b.tag !== "stair")
        .map((b) => {
        if (b.p[1] <= 0 && b.s[1] <= 1.1)
            return { ...b, color: "#17132a", glow: violet };
        const accent = b.glow || b.color === RF.accent || b.color === RF.ac ? violet : undefined;
        return {
            ...b,
            p: [b.p[0], b.p[1] * verticalScale, b.p[2]],
            s: [b.s[0], Math.max(0.25, b.s[1] * verticalScale), b.s[2]],
            color: accent ? violet : (b.s[1] < 0.6 ? roof : tower),
            glow: accent ?? (b.s[1] >= 1 ? glass : undefined),
        };
    });
    // Keep a sparse, readable violet lift network. The first version copied all
    // tower lifts and added a dense grid, filling the streets with purple light.
    const jumpPads = src.jumpPads
        .filter((_p, i) => i % 3 === 0)
        .map((p) => ({ ...p, color: violet, power: Math.max(70, (p.power ?? 18) * 1.75), fling: 0 }));
    for (const [x, z] of [[-48, -48], [48, -48], [-48, 48], [48, 48], [0, 0]]) {
        jumpPads.push({ p: [x + 10, 0.1, z + 10], power: 82, color: violet, fling: 0 });
    }
    // Every actual tower roof gets an ORANGE outward-launch pad. Unlike the
    // violet vertical lifts below, these retain the standard forward fling so a
    // player can aim toward another building and escape any rooftop.
    for (const b of src.boxes) {
        const roofCap = b.p[1] > 3 && b.s[1] <= 0.31 && b.s[0] >= 8 && b.s[2] >= 8;
        if (roofCap)
            jumpPads.push({ p: [b.p[0], b.p[1] * verticalScale + 0.35, b.p[2]], power: 48, color: "#ff8a2a" });
    }
    // Ground-level jetpacks throughout the alley network. Derive clear points
    // from the original collision plan so none are buried inside a skyscraper.
    const pickups = [...(src.pickups ?? [])];
    const jetpacks = [];
    const clearGround = (x, z) => !src.boxes.some((b) => {
        const bottom = b.p[1] - b.s[1] / 2, top = b.p[1] + b.s[1] / 2;
        return bottom < 2 && top > 0 && b.s[1] > 1.1 &&
            Math.abs(x - b.p[0]) < b.s[0] / 2 + 1.8 && Math.abs(z - b.p[2]) < b.s[2] / 2 + 1.8;
    });
    for (let x = -72; x <= 72 && jetpacks.length < 12; x += 8)
        for (let z = -72; z <= 72 && jetpacks.length < 12; z += 8) {
            if (!clearGround(x, z) || jetpacks.some(([jx, jz]) => Math.hypot(jx - x, jz - z) < 24))
                continue;
            jetpacks.push([x, z]);
        }
    for (const [x, z] of jetpacks)
        pickups.push({ p: [x, 0.3, z], kind: "jetpack" });
    return {
        ...src,
        id: "rooftop_spiderweb",
        name: "Rooftop Spiderweb",
        theme: "day",
        bounds: { min: [-88, -10, -88], max: [88, 170, 88] },
        boxes,
        jumpPads,
        pickups,
        spawns: src.spawns.map((s) => ({ ...s, p: [s.p[0], s.p[1] > 1 ? s.p[1] * verticalScale : s.p[1], s.p[2]] })),
    };
}
export const MAP_ROOFTOP_SPIDERWEB = rooftopSpiderweb();
export const MAP_ARENA_XL = arenaXL();
export const MAP_VILLAGE = village();
export const MAP_CANYON = canyon();
export const MAP_ZARYA = zaryaIsland();
export const MAP_NEON_DISTRICT = neonDistrict();
export const MAP_NEO_ISLAND = neoIsland();
function holedSlab(out, cx, cz, w, d, y, t, color, holes) {
    const x0 = cx - w / 2, x1 = cx + w / 2;
    const z0 = cz - d / 2, z1 = cz + d / 2;
    const xs = [x0, x1], zs = [z0, z1];
    for (const [hx, hz, hw, hd] of holes) {
        xs.push(Math.max(x0, hx - hw / 2), Math.min(x1, hx + hw / 2));
        zs.push(Math.max(z0, hz - hd / 2), Math.min(z1, hz + hd / 2));
    }
    xs.sort((a, b) => a - b);
    zs.sort((a, b) => a - b);
    for (let xi = 0; xi + 1 < xs.length; xi++) {
        for (let zi = 0; zi + 1 < zs.length; zi++) {
            const ax = xs[xi], bx = xs[xi + 1], az = zs[zi], bz = zs[zi + 1];
            if (bx - ax < 0.1 || bz - az < 0.1)
                continue;
            const mx = (ax + bx) / 2, mz = (az + bz) / 2;
            if (holes.some(([hx, hz, hw, hd]) => mx > hx - hw / 2 && mx < hx + hw / 2 && mz > hz - hd / 2 && mz < hz + hd / 2))
                continue;
            out.push({ p: [mx, y, mz], s: [bx - ax, t, bz - az], color });
        }
    }
}
function stairDown(x, z, fromY, toY, dir, width, color) {
    const boxes = [];
    const steps = Math.max(2, Math.ceil(Math.abs(fromY - toY) / 0.45));
    const run = 0.85;
    const dx = dir === "px" ? 1 : dir === "nx" ? -1 : 0;
    const dz = dir === "pz" ? 1 : dir === "nz" ? -1 : 0;
    for (let i = 0; i < steps; i++) {
        const top = fromY + (toY - fromY) * ((i + 1) / steps);
        boxes.push({
            p: [x + dx * run * i, top - 0.2, z + dz * run * i],
            s: [dx ? run : width, 0.4, dz ? run : width],
            color,
            tag: "stair",
        });
    }
    return boxes;
}
function medievalCastle() {
    const B = [];
    const MC = {
        grass: "#5f8a55", darkGrass: "#4f744a", dirt: "#9a7048", stone: "#77736a",
        stoneDark: "#57554f", stoneLight: "#9d978c", wood: "#6d4a2f", woodDark: "#3d281b",
        thatch: "#b69b55", water: "#b5330f", iron: "#2f343a", field: "#8da95b",
    };
    const holes = [
        [0, 64, 122, 16], [0, -64, 122, 16], [-64, 0, 16, 122], [64, 0, 16, 122],
        [0, 4, 34, 34], [44, 46, 11, 14],
    ];
    holedSlab(B, 0, 0, 320, 300, -0.5, 1, MC.grass, holes);
    B.push({ p: [0, -3.4, 64], s: [122, 0.5, 16], color: MC.water }, { p: [0, -3.4, -64], s: [122, 0.5, 16], color: MC.water }, { p: [-64, -3.4, 0], s: [16, 0.5, 122], color: MC.water }, { p: [64, -3.4, 0], s: [16, 0.5, 122], color: MC.water }, 
    // gate road, SPLIT at the moat so it never bridges it — the old single
    // slab (z -164..-32) laid a permanent dirt causeway across the moat, so
    // the drawbridge was pointless (crossing stayed open while it "raised").
    // North approach stops at the outer moat edge; a short inner apron sits on
    // the castle side. The moat gap between them is the drawbridge's alone.
    { p: [0, -0.45, -118.5], s: [16, 0.35, 91], color: MC.dirt }, // outside → drawbridge edge (z -164..-73)
    { p: [0, -0.45, -42], s: [16, 0.35, 24], color: MC.dirt }, // inner apron (z -54..-30)
    { p: [-70, -0.44, -104], s: [80, 0.35, 10], color: MC.dirt }, { p: [70, -0.44, -104], s: [80, 0.35, 10], color: MC.dirt }, { p: [0, -0.43, 108], s: [12, 0.35, 66], color: MC.dirt });
    // BASE SLAB: a dark foundation under the entire map — any gap between
    // floor patches shows honest dark earth instead of the void/skybox
    // (below the undercroft level so no room or stair gets sealed)
    B.push({ p: [0, -5.95, 0], s: [330, 0.7, 310], color: MC.stoneDark });
    // Finished moat edges: low retaining stones make the moat read as intentional,
    // and shallow beds + escape stairs prevent players from getting trapped in
    // "holes" while preserving the drawbridge as the clean crossing.
    B.push({ p: [0, -1.15, 64], s: [122, 0.35, 16], color: MC.water }, { p: [0, -1.15, -64], s: [122, 0.35, 16], color: MC.water }, { p: [-64, -1.15, 0], s: [16, 0.35, 122], color: MC.water }, { p: [64, -1.15, 0], s: [16, 0.35, 122], color: MC.water }, 
    // the moat is a DEADLY LAVA river — nothing sits in it but the drawbridge:
    // no retaining stones, escape stairs, beds or ground (all removed so the
    // whole deadly ring reads as clean lava). Cross by the drawbridge, the
    // violet beams or a jetpack, or burn.
    { p: [0, -0.36, -49], s: [18, 0.32, 10], color: MC.dirt }, { p: [0, -0.35, -34], s: [86, 0.3, 18], color: MC.darkGrass }, { p: [0, -0.35, 34], s: [86, 0.3, 18], color: MC.darkGrass }, { p: [-34, -0.35, 0], s: [18, 0.3, 50], color: MC.darkGrass }, { p: [34, -0.35, 0], s: [18, 0.3, 50], color: MC.darkGrass }, { p: [-80, -0.38, 82], s: [44, 0.28, 9], color: MC.dirt }, // outer dirt, clear of the lava
    { p: [80, -0.38, 82], s: [44, 0.28, 9], color: MC.dirt });
    // (the escape stairs that used to descend into the moat are gone — it's
    // lava now, nothing to climb out of)
    // Castle curtain walls, wall walks, towers and battlements.
    B.push({ p: [0, 5, 48], s: [96, 10, 4], color: MC.stone }, { p: [-48, 5, 0], s: [4, 10, 96], color: MC.stone }, { p: [48, 5, 0], s: [4, 10, 96], color: MC.stone }, { p: [-30, 5, -48], s: [36, 10, 4], color: MC.stone }, { p: [30, 5, -48], s: [36, 10, 4], color: MC.stone }, { p: [0, 9.1, 48], s: [96, 0.6, 5.8], color: MC.stoneLight }, { p: [-48, 9.1, 0], s: [5.8, 0.6, 96], color: MC.stoneLight }, { p: [48, 9.1, 0], s: [5.8, 0.6, 96], color: MC.stoneLight }, { p: [-30, 9.1, -48], s: [36, 0.6, 5.8], color: MC.stoneLight }, { p: [30, 9.1, -48], s: [36, 0.6, 5.8], color: MC.stoneLight });
    const merlons = [];
    for (let x = -44; x <= 44; x += 8) {
        merlons.push([x, 48, 3, 2.4, 2], [x, -48, 3, 2.4, 2]);
    }
    for (let z = -40; z <= 40; z += 8) {
        merlons.push([-48, z, 2, 2.4, 3], [48, z, 2, 2.4, 3]);
    }
    for (const [x, z, sx, sy, sz] of merlons)
        B.push({ p: [x, 11, z], s: [sx, sy, sz], color: MC.stoneDark });
    for (const [x, z] of [[-48, -48], [48, -48], [-48, 48], [48, 48]]) {
        B.push({ p: [x, 7, z], s: [13, 14, 13], color: MC.stoneDark }, { p: [x, 14.2, z], s: [14, 0.6, 14], color: MC.stoneLight }, { p: [x, 16.1, z], s: [10, 3.2, 10], color: MC.stone });
        B.push(...staircase(x - Math.sign(x) * 7, z, 9, Math.sign(x) < 0 ? "px" : "nx", 2.3, { color: MC.stoneDark }));
    }
    B.push({ p: [-9, 8, -48], s: [10, 16, 9], color: MC.stoneDark }, { p: [9, 8, -48], s: [10, 16, 9], color: MC.stoneDark }, { p: [0, 14.8, -48], s: [28, 2.5, 8], color: MC.stone });
    // Keep, great hall, barracks, armory, stables, kitchens and detailed props.
    // cornice band under the keep floor edge: the wall/floor seam let you see
    // straight down into the hall from the upper edge — sealed all around
    B.push({ p: [0, 3.5, 16.2], s: [33.6, 1.4, 1.3], color: MC.stone }, { p: [0, 3.5, -16.2], s: [33.6, 1.4, 1.3], color: MC.stone }, { p: [16.2, 3.5, 0], s: [1.3, 1.4, 33.6], color: MC.stone }, { p: [-16.2, 3.5, 0], s: [1.3, 1.4, 33.6], color: MC.stone });
    holedSlab(B, 0, 4, 32, 32, 0.05, 0.3, MC.stoneLight, [[7, -7.2, 5.0, 6.8]]);
    // Keep with a roof hole over the +X stairwell strip: building() cuts its
    // floor holes on the +X side, so the up-staircase MUST run in that strip —
    // anywhere else it slams into the story slabs ("no hole in the ceiling").
    // roof hole starts at z=6.4: the step-up onto the h=10 step lifts the body
    // to y 12.2 while it still trails to z~6.8 — a shorter hole blocks the head
    B.push(...building(0, 4, 32, 32, 4, { color: MC.stone, roof: MC.stoneDark, roofHoles: [[14.6, 9.5, 2.6, 6.2]] }));
    // continuous stair up the east stairwell shaft: step off onto each story
    // (y=3/6/9) as it passes, then out the roof hole onto the battlement roof.
    // Tops out at 12.5 — ABOVE the roof slab (12.3) — so the exit is a step
    // DOWN onto the roof, never an up-step squeezed under geometry.
    B.push(...staircase(14.6, -10, 12.5, "pz", 2.2, { color: MC.stoneDark }));
    // dungeon stairs: top step flush with the ground-floor hole's north edge
    // (z=-10.6) so walking in from the door-side landing links cleanly
    B.push(...stairDown(7, -10.2, 0.2, -5, "pz", 2.3, MC.stoneDark));
    B.push(
    // Ground seal around the keep: the terrain cutout is wider than the
    // wall footprint, so these close the outside floor gap beside the keep.
    { p: [0, -0.03, -13.0], s: [36.4, 0.22, 2.2], color: MC.stoneLight }, { p: [0, -0.03, 21.0], s: [36.4, 0.22, 2.2], color: MC.stoneLight }, { p: [-17.0, -0.03, 4], s: [2.2, 0.22, 36.4], color: MC.stoneLight }, { p: [17.0, -0.03, 4], s: [2.2, 0.22, 36.4], color: MC.stoneLight }, { p: [7, 0.06, -11.0], s: [5.4, 0.28, 1.6], color: MC.stoneLight }, { p: [0, 3.2, 0], s: [10, 0.4, 4], color: MC.wood }, // throne dais
    { p: [0, 4.5, -1.4], s: [2.8, 2.6, 1.2], color: "#8b2030", glow: "#d0a040" }, { p: [0, 1, 13], s: [18, 1, 4], color: MC.wood }, // great table
    { p: [-10, 1, 13], s: [1, 1, 8], color: MC.woodDark }, { p: [10, 1, 13], s: [1, 1, 8], color: MC.woodDark }, { p: [-11, 7.4, 8], s: [6, 0.7, 3], color: "#71413a" }, // royal bed
    { p: [10.5, 7.4, -3], s: [5, 0.7, 3], color: "#274c7a" }, // (clear of the stairwell strip)
    { p: [-7, 10.3, -8], s: [7, 1.4, 3], color: MC.woodDark });
    // Stairwell privacy screen: the keep doorway no longer gives a straight
    // outside view down into the dungeon, while the descending stairs stay open.
    B.push({ p: [3.55, 0.2, -9.3], s: [0.5, 5.0, 13.4], color: MC.stoneDark });
    B.push({ p: [7.2, 0.35, -12.95], s: [7.4, 5.1, 0.5], color: MC.stoneDark });
    B.push(...building(-28, 16, 24, 18, 2, { color: MC.stone, roof: MC.woodDark })); // barracks
    B.push(...building(28, 14, 20, 16, 2, { color: MC.stone, roof: MC.woodDark })); // armory
    B.push(...building(-28, -22, 20, 14, 1, { color: MC.wood, roof: MC.thatch })); // stables
    B.push(...building(28, -22, 20, 14, 1, { color: MC.wood, roof: MC.thatch })); // kitchens
    for (const [x, z] of [[-31, 16], [-25, 16], [25, 15], [31, 15], [22, -22], [28, -22], [34, -22]]) {
        B.push({ p: [x, 0.65, z], s: [3.5, 1.3, 1.2], color: MC.woodDark });
    }
    for (const [x, z] of [[24, 11], [28, 11], [32, 11], [24, 18], [28, 18], [32, 18]]) {
        B.push({ p: [x, 1.1, z], s: [0.45, 2.2, 3], color: MC.iron }); // weapon racks
    }
    for (const [x, z] of [[-34, -24], [-29, -24], [-24, -24], [-34, -18], [-29, -18], [-24, -18]]) {
        B.push({ p: [x, 1.0, z], s: [1.8, 1.3, 3.3], color: MC.woodDark }); // horse stalls
    }
    // Dungeon and secret tunnel under the keep.
    B.push({ p: [0, -5.3, 4], s: [34, 0.5, 34], color: MC.stoneDark }, { p: [0, -2.8, 21], s: [34, 5, 1], color: MC.stone }, { p: [0, -2.8, -13], s: [34, 5, 1], color: MC.stone }, { p: [-17, -2.8, 4], s: [1, 5, 34], color: MC.stone }, { p: [17, -2.8, 4], s: [1, 5, 34], color: MC.stone });
    for (const x of [-11, -5, 5, 11]) {
        B.push({ p: [x, -2.6, 18], s: [0.5, 4.8, 5], color: MC.iron });
        B.push({ p: [x, -3.2, 15.3], s: [5, 0.4, 0.4], color: MC.iron });
    }
    B.push({ p: [24, -5.3, 31], s: [44, 0.45, 5], color: MC.stoneDark }, { p: [24, -3.0, 28.2], s: [44, 4.6, 0.5], color: MC.stone }, { p: [24, -3.0, 33.8], s: [44, 4.6, 0.5], color: MC.stone }, { p: [44, -5.3, 46], s: [10, 0.45, 14], color: MC.stoneDark });
    B.push(...stairDown(44, 46, 0, -5, "nz", 2.6, MC.stoneDark));
    // Village, farms, forest markers and roads.
    const village = [
        [-54, -102, 16, 13, 1], [-31, -116, 14, 12, 1], [-10, -105, 18, 14, 2],
        [34, -112, 16, 12, 1], [56, -96, 20, 14, 2], [78, -118, 14, 12, 1],
    ];
    for (const [x, z, w, d, st] of village)
        B.push(...building(x, z, w, d, st, { color: MC.wood, roof: MC.thatch }));
    for (let i = 0; i < 7; i++) {
        const x = -110 + i * 13;
        // skip rows that would clip the west lava moat (|x| 56-72) or land inside
        // the castle (|x| < 50) — keep the fields as clean western farmland
        if (Math.abs(x) < 50 || (Math.abs(x) > 54 && Math.abs(x) < 74))
            continue;
        B.push({ p: [x, -0.43, -38], s: [9, 0.28, 24], color: i % 2 ? MC.field : "#a2b56a" });
        B.push({ p: [x, 0.2, -52], s: [10, 0.4, 0.35], color: MC.woodDark });
    }
    for (const [x, z] of [[-116, 44], [-100, 58], [-88, 28], [92, 34], [112, 58], [120, 18], [-120, 94], [110, 96]]) {
        B.push({ p: [x, 1.4, z], s: [1.2, 2.8, 1.2], color: "#5a3a22" });
        B.push({ p: [x, 4.0, z], s: [5.5, 4.2, 5.5], color: "#315b34" });
    }
    B.push({ p: [-108, 1, -90], s: [4, 2, 12], color: MC.stoneDark }, { p: [-104, 2.6, -90], s: [4, 1.2, 12], color: MC.thatch }, { p: [104, 1, -88], s: [4, 2, 12], color: MC.stoneDark }, { p: [108, 2.6, -88], s: [4, 1.2, 12], color: MC.thatch });
    const spawns = [
        { p: [0, 0.2, -90], yaw: Math.PI }, { p: [-18, 0.2, -30], yaw: Math.PI * 0.2 },
        // (the two south wall-walk spawns sat at z=48 directly over the moat —
        // "between the castle and the water" — moved onto the barracks/armory
        // roofs, inside the curtain walls)
        { p: [18, 0.2, -30], yaw: Math.PI * 1.8 }, { p: [-28, 6.5, 16], yaw: Math.PI },
        { p: [28, 6.5, 14], yaw: Math.PI }, { p: [-48, 14.7, -48], yaw: Math.PI * 0.75 },
        { p: [48, 14.7, -48], yaw: Math.PI * 1.25 }, { p: [0, 12.5, 4], yaw: Math.PI },
        { p: [-80, 0.2, -102], yaw: Math.PI * 0.5 }, { p: [82, 0.2, -104], yaw: Math.PI * 1.5 },
        { p: [44, -4.8, 46], yaw: Math.PI }, { p: [0, -4.8, 14], yaw: 0 },
    ];
    // ---- FARMLAND around the castle: tilled fields, barns, pens, haystacks —
    // the countryside the villages always implied (western-style dressing)
    const farm = (fx, fz, fw, fd) => {
        B.push({ p: [fx, -0.38, fz], s: [fw, 0.3, fd], color: "#8a6a34" }); // tilled earth
        const rows = Math.floor(fd / 3);
        for (let i = 0; i < rows; i++) {
            B.push({ p: [fx, -0.28, fz - fd / 2 + 1.5 + i * 3], s: [fw - 1.5, 0.22, 1.1], color: "#a58a3c" }); // crop rows
        }
        // split-rail fence posts on the corners + midpoints
        for (const [px, pz] of [[-fw / 2, -fd / 2], [fw / 2, -fd / 2], [-fw / 2, fd / 2], [fw / 2, fd / 2], [0, -fd / 2], [0, fd / 2]]) {
            B.push({ p: [fx + px, 0.55, fz + pz], s: [0.3, 1.1, 0.3], color: MC.woodDark });
        }
    };
    farm(105, 10, 30, 22);
    farm(-108, 8, 28, 20);
    farm(-24, 126, 26, 16); // (was at (2,106) — straddled the south road, now town land)
    farm(118, 84, 26, 18); // south-east pasture
    farm(-120, 90, 24, 16); // south-west pasture
    farm(-34, -124, 22, 14); // north field by the west village
    farm(96, -128, 22, 14); // north field by the east village
    // barns (enterable, timber) + haystacks + a well for each farmstead
    B.push(...building(130, 4, 16, 12, 1, { color: "#6a4a2a", roof: "#7a3c28" }));
    B.push(...building(-132, 18, 16, 12, 1, { color: "#6a4a2a", roof: "#7a3c28" }));
    for (const [hx, hz] of [[118, 26], [122, 23], [-118, 24], [-10, 100], [30, 120], [104, 78], [-108, 84], [-22, -118], [86, -120]]) {
        B.push({ p: [hx, 0.85, hz], s: [2.6, 1.7, 2.2], color: "#b09548" }); // haystack
        B.push({ p: [hx, 1.9, hz], s: [1.6, 0.7, 1.3], color: "#c2a856" });
    }
    B.push({ p: [112, 0.5, 2], s: [2.4, 1.0, 2.4], color: MC.stone }, // farm well east
    { p: [-116, 0.5, -2], s: [2.4, 1.0, 2.4], color: MC.stone });
    // ---- BOURG: a market town along the south pilgrim road, filling the empty
    // grass between the moat and the outer farms. All houses enterable; a
    // packed-dirt square with stalls and a well straddles the road.
    const bourg = [
        [-20, 81, 12, 10, 2, "stone"], [20, 81, 12, 10, 2, "stone"], // north row on the square
        [-22, 95, 12, 9, 1, "wood"], [22, 95, 12, 9, 1, "wood"], // west/east cottages
        [-16, 110, 12, 9, 1, "wood"], [16, 111, 12, 9, 2, "stone"], // south edge of town
        [32, 88, 12, 14, 2, "stone"], // guild hall east of the square
    ];
    for (const [x, z, w, d, st, kind] of bourg) {
        B.push(...building(x, z, w, d, st, kind === "stone"
            ? { color: MC.stone, roof: MC.woodDark }
            : { color: MC.wood, roof: MC.thatch }));
    }
    B.push({ p: [0, -0.34, 88], s: [56, 0.26, 26], color: MC.dirt }); // market square
    // market stalls: counter + thatch canopy on posts
    for (const [sx, sz] of [[11, 85], [-11, 86], [12, 92]]) {
        B.push({ p: [sx, 0.55, sz], s: [3.2, 1.1, 1.4], color: MC.woodDark }, { p: [sx - 1.5, 1.3, sz], s: [0.25, 2.6, 0.25], color: MC.woodDark }, { p: [sx + 1.5, 1.3, sz], s: [0.25, 2.6, 0.25], color: MC.woodDark }, { p: [sx, 2.5, sz], s: [3.8, 0.25, 2.0], color: MC.thatch });
    }
    // town well, off the road on the square's west half
    B.push({ p: [-8, 0.5, 90], s: [2.4, 1.0, 2.4], color: MC.stone }, { p: [-9.1, 1.7, 90], s: [0.25, 2.4, 0.25], color: MC.woodDark }, { p: [-6.9, 1.7, 90], s: [0.25, 2.4, 0.25], color: MC.woodDark }, { p: [-8, 2.9, 90], s: [3.0, 0.4, 3.0], color: MC.woodDark });
    const FW = { town: "#b8834d", wood: "#7a4f2e", darkWood: "#4a2f1f", roof: "#9d6a3d", trough: "#2d6774" };
    const frontierHamlet = [
        [-138, -56, 18, 13, 1], [-128, -82, 22, 14, 2], [126, -78, 22, 14, 2],
        [140, -50, 18, 13, 1], [-82, 124, 18, 12, 1], [78, 124, 18, 12, 1],
        [-140, 126, 16, 12, 1], [138, 124, 16, 12, 1],
        // mid-ring infill: the stretches between the moat and the outer farms
        // read as empty grass — western houses by the east/west farms + south road
        [-90, 45, 18, 13, 1], [102, 48, 18, 13, 1],
        [-46, 110, 16, 12, 2], [44, 106, 16, 12, 1],
    ];
    for (const [x, z, w, d, st] of frontierHamlet) {
        B.push(...building(x, z, w, d, st, { color: FW.town, roof: FW.roof }));
        const frontZ = z - d / 2;
        B.push({ p: [x, 0.18, frontZ - 2.0], s: [w + 4, 0.36, 4.0], color: FW.darkWood }, { p: [x, 2.78, frontZ - 2.0], s: [w + 3, 0.28, 3.4], color: FW.roof }, { p: [x, 3.45, frontZ - 0.25], s: [w - 4, 1.0, 0.3], color: FW.darkWood }, { p: [x - w / 2 + 2, 1.15, frontZ - 4.2], s: [0.3, 1.2, 0.3], color: FW.darkWood }, { p: [x + w / 2 - 2, 1.15, frontZ - 4.2], s: [0.3, 1.2, 0.3], color: FW.darkWood }, { p: [x, 1.45, frontZ - 4.2], s: [w - 2, 0.25, 0.25], color: FW.wood }, { p: [x + w / 2 + 2.6, 0.55, z + 1], s: [4.0, 1.1, 1.3], color: FW.darkWood }, { p: [x + w / 2 + 2.6, 1.12, z + 1], s: [3.6, 0.18, 1.0], color: FW.trough });
    }
    return {
        id: "medieval_castle",
        name: "Medieval Castle",
        modes: ["gungame", "ffa", "relay", "onegun", "treasure", "burnout", "plunder"],
        theme: "day",
        killZ: -12,
        bounds: { min: [-160, -14, -150], max: [160, 36, 150] },
        boxes: B,
        // VIOLET grav-beams: leap the walls and towers instead of hunting stairs
        jumpPads: [
            // VIOLET beams all across the castle, launching sky-high
            { p: [-32, 0.1, -30], power: 42, color: "#b46bff" }, // courtyard → over the ramparts
            { p: [32, 0.1, -30], power: 42, color: "#b46bff" },
            { p: [0, 0.1, -84], power: 40, color: "#b46bff" }, // gate approach
            { p: [-66, 0.1, -104], power: 38, color: "#b46bff" }, // west village
            { p: [70, 0.1, -100], power: 38, color: "#b46bff" }, // east village
            // (the old indoor "keep court" beam fired straight into the story
            // ceiling — replaced by a powerup on every keep floor instead)
            { p: [-70, 0.1, -30], power: 40, color: "#b46bff" }, // west field
            { p: [74, 0.1, -26], power: 40, color: "#b46bff" }, // east field
            { p: [0, 0.1, 44], power: 40, color: "#b46bff" }, // behind the keep
            { p: [-36, 0.1, 96], power: 38, color: "#b46bff" }, // south meadow
            { p: [40, 0.1, 92], power: 38, color: "#b46bff" }, // south-east meadow
            { p: [0, 0.1, -120], power: 40, color: "#b46bff" }, // north approach
        ],
        spawns,
        pickups: [
            { p: [0, 0.4, 18], kind: "armor" }, { p: [28, 0.4, 14], kind: "grenade" },
            { p: [-28, 0.4, 16], kind: "health" }, { p: [0, 3.6, 0], kind: "imperion" },
            { p: [0, -4.7, 16], kind: "rocket" }, { p: [-70, 0.4, -110], kind: "health" },
            { p: [74, 0.4, -106], kind: "armor" }, { p: [44, -4.7, 46], kind: "cloak" },
            { p: [0, 0.4, -70], kind: "jetpack" }, // storm the walls from above
            { p: [-36, 9.8, 48], kind: "dmg" }, { p: [36, 9.8, 48], kind: "speed" }, // tower tops
            { p: [-48, 14.9, -48], kind: "grenade" }, { p: [0, 0.4, -50], kind: "health" },
            // keep interior: a prize on every storey rewards climbing the shaft
            // (ground: armor · dais: imperion · then these · roof: health)
            { p: [0, 6.55, 12], kind: "dmg" }, { p: [0, 9.55, -6], kind: "cloak" },
            { p: [7, 12.7, 10], kind: "health" },
        ],
        vehicles: [
            { p: [-35, 0.7, -24], yaw: 0, kind: "horse" }, { p: [-26, 0.7, -24], yaw: 0, kind: "horse" },
            { p: [70, 0.7, -102], yaw: Math.PI * 1.5, kind: "horse" }, { p: [-72, 0.7, -106], yaw: Math.PI * 0.5, kind: "horse" },
            { p: [-112, 0.7, 44], yaw: Math.PI * 0.5, kind: "horse" }, { p: [112, 0.7, 34], yaw: Math.PI * 1.5, kind: "horse" },
        ],
        drawbridges: [
            { id: "castle_gate", p: [0, 0.18, -61], s: [12, 0.5, 24], yaw: 0, lever: [-12, 0.5, -40], lever2: [11, 0.5, -80], initiallyLowered: true },
        ],
    };
}
function wildWestTrainPursuit() {
    const B = [];
    const WW = {
        sand: "#c9a96b", sand2: "#b98d54", rock: "#8a6245", rockDark: "#5c3c2e",
        wood: "#7a4f2e", darkWood: "#4a2f1f", roof: "#9d6a3d", rail: "#34383d",
        metal: "#555b62", water: "#3d7d8f", cactus: "#3f7546", town: "#b8834d",
    };
    B.push({ p: [0, -0.5, 0], s: [360, 1, 320], color: WW.sand }, { p: [0, -0.46, 0], s: [190, 0.4, 170], color: WW.sand2 }, 
    // (the buried river slab + its dam are gone — it sat under the sand,
    // invisible, but its deadly zone drowned players on dry ground)
    { p: [24, -0.42, -112], s: [130, 0.35, 10], color: WW.sand2 });
    const ringR = 74; // 86 clipped the town walls — the ring hugs the plaza now
    const railY = 2.4;
    for (let i = 0; i < 36; i++) {
        const a = (i / 36) * Math.PI * 2;
        const x = Math.cos(a) * ringR, z = Math.sin(a) * ringR;
        B.push({ p: [x, 0.35, z], s: [0.8, 1.4, 0.8], color: WW.rail });
    }
    for (const [x, z] of [[ringR, 0], [-ringR, 0], [0, ringR], [0, -ringR]]) {
        // Low depot platforms only. The old canopy/mast/connector read as an arch
        // over the train path, so the ring has no overhead station geometry.
        const outX = x !== 0 ? Math.sign(x) : 0;
        const outZ = z !== 0 ? Math.sign(z) : 0;
        const px = x + outX * 16;
        const pz = z + outZ * 16;
        B.push({ p: [px, railY - 0.2, pz], s: [16, 0.4, 10], color: WW.darkWood });
        // stairs START past the platform's outer edge (half-size 8 + the 4.5m
        // run) and climb back toward it — at +5 they rose UNDER the slab and the
        // first step already head-blocked ("platform we can't go up")
        const sx = px + outX * 13;
        const sz = pz + outZ * 13;
        B.push(...staircase(sx, sz, railY, x ? (x > 0 ? "nx" : "px") : (z > 0 ? "nz" : "pz"), 3.0, { color: WW.darkWood }));
    }
    // Main town, all enterable with roofs and interior combat cover.
    const townBuildings = [
        [88, -70, 22, 14, 2, "SALOON"], [116, -70, 18, 14, 2, "BANK"], [63, -70, 18, 12, 1, "SHERIFF"],
        [91, -45, 20, 14, 2, "HOTEL"], [116, -45, 18, 12, 1, "STORE"], [38, -42, 18, 12, 1, "GUNSMITH"],
        [139, -58, 18, 24, 1, "STABLE"],
    ];
    for (const [x, z, w, d, st] of townBuildings) {
        B.push(...building(x, z, w, d, st, { color: WW.town, roof: WW.roof }));
        B.push({ p: [x, 1.0, z + d / 2 - 2], s: [w - 4, 1, 1.4], color: WW.darkWood }); // bar/counters
        B.push({ p: [x - w / 2 + 2, 3.3, z - d / 2 - 0.6], s: [2, 1.2, 0.3], color: WW.darkWood });
    }
    B.push({ p: [102, -0.44, -57], s: [100, 0.35, 12], color: WW.darkWood }, // main street
    { p: [88, 1.2, -34], s: [10, 2.4, 8], color: WW.darkWood }, // jail cells
    { p: [87, 1.4, -37.8], s: [9, 2.6, 0.4], color: WW.metal });
    // Ranches, farms, mines, bandit camps and canyon cover.
    for (const [x, z] of [[-118, -78], [-132, -96], [-102, -102], [-126, 84], [128, 72], [144, 92]]) {
        B.push(...building(x, z, 18, 12, 1, { color: WW.wood, roof: WW.roof }));
        B.push({ p: [x + 16, 0.7, z], s: [18, 1.4, 10], color: WW.darkWood });
    }
    for (let i = 0; i < 9; i++) {
        B.push({ p: [-150 + i * 8, -0.42, 42], s: [6, 0.35, 24], color: i % 2 ? "#c5aa62" : "#a88f52" });
        B.push({ p: [-150 + i * 8, 0.3, 55], s: [7, 0.5, 0.4], color: WW.darkWood });
    }
    B.push({ p: [-36, 2.5, 118], s: [44, 5, 12], color: WW.rockDark }, { p: [-36, 0.2, 108], s: [9, 0.4, 24], color: WW.rail }, { p: [-36, 4.8, 112], s: [12, 4.2, 2], color: "#1f1713" }, // mine mouth
    { p: [-18, 0.5, 123], s: [28, 1, 8], color: WW.darkWood }, // mine cart track
    { p: [42, 0.5, 116], s: [20, 1, 16], color: WW.darkWood }, // bandit camp deck
    { p: [38, 2.3, 116], s: [10, 3.4, 2], color: WW.wood }, { p: [50, 2.0, 108], s: [8, 3, 8], color: WW.roof });
    const canyonBlocks = [
        [-112, 34, 18, 18, 18], [-132, 10, 16, 16, 14], [-118, -18, 22, 18, 20],
        [10, 106, 18, 16, 18], [34, 92, 14, 18, 15], [62, 106, 20, 16, 20],
        [-58, -126, 18, 16, 16], [-28, -132, 26, 14, 18], [4, -128, 18, 20, 15],
        [150, 6, 22, 28, 18], [138, 28, 18, 16, 14], [154, -26, 16, 18, 13],
    ];
    for (const [x, z, w, d, h] of canyonBlocks) {
        B.push({ p: [x, h / 2, z], s: [w, h, d], color: h > 17 ? WW.rockDark : WW.rock });
        B.push({ p: [x, h + 0.3, z], s: [w + 2, 0.6, d + 2], color: WW.rock });
    }
    // NB: no bridges across the RIVER — it is deadly water, crossable only by
    // flying over (jetpack / beams / heli); canyon shortcuts keep theirs
    for (const [x, z] of [[0, -112], [72, 86], [95, -4]]) {
        B.push({ p: [x, 2.4, z], s: [26, 0.6, 7], color: WW.darkWood }); // wooden shortcut bridges
        B.push({ p: [x, 3.2, z - 3], s: [26, 1.0, 0.4], color: WW.wood });
        B.push({ p: [x, 3.2, z + 3], s: [26, 1.0, 0.4], color: WW.wood });
    }
    for (let i = 0; i < 40; i++) {
        const a = (i * 37) % 360 * Math.PI / 180;
        const r = 38 + ((i * 19) % 120);
        const x = Math.cos(a) * r, z = Math.sin(a) * r;
        const rr = Math.hypot(x, z);
        if (rr < 66 || (rr > 66 && rr < 82))
            continue; // clear of the plaza AND the train's path
        if (x > -91 && x < -69 && z > -60 && z < 104)
            continue; // not in the river
        B.push({ p: [x, 1.7, z], s: [0.8, 3.4, 0.8], color: WW.cactus });
        B.push({ p: [x + 0.7, 2.0, z], s: [0.5, 1.4, 0.5], color: WW.cactus });
    }
    for (let z = -120; z <= 118; z += 24) {
        if (Math.abs(Math.hypot(26, z) - 74) < 7)
            continue; // never inside the train's path
        B.push({ p: [26, 3.2, z], s: [0.35, 6.4, 0.35], color: WW.darkWood });
        B.push({ p: [26, 6.3, z], s: [4.5, 0.3, 0.3], color: WW.darkWood });
    }
    // ---- CENTRAL TOWN SQUARE: the heart of the map (inside the train ring).
    // A packed-dirt plaza with a steepled church, cantina, trading post,
    // telegraph office, water tower, windmill, well, corral and wagons.
    B.push({ p: [0, -0.41, 6], s: [78, 0.32, 66], color: WW.sand2 }); // plaza dirt
    B.push(...building(-2, -26, 18, 24, 2, { color: WW.town, roof: WW.roof })); // CHURCH
    // steeple: stacked tapering tower + cross, in front of the church
    B.push({ p: [-2, 5.0, -41.5], s: [5, 10, 5], color: WW.town }, { p: [-2, 10.8, -41.5], s: [4, 1.8, 4], color: WW.darkWood }, { p: [-2, 12.4, -41.5], s: [2.6, 1.6, 2.6], color: WW.roof }, { p: [-2, 13.7, -41.5], s: [1.2, 1.2, 1.2], color: WW.roof }, { p: [-2, 15.2, -41.5], s: [0.25, 1.9, 0.25], color: WW.darkWood }, { p: [-2, 15.6, -41.5], s: [1.1, 0.25, 0.25], color: WW.darkWood });
    B.push(...building(2, 40, 18, 14, 2, { color: WW.town, roof: WW.roof })); // CANTINA
    B.push(...building(-34, -16, 16, 12, 1, { color: WW.wood, roof: WW.roof })); // TRADING POST
    B.push(...building(34, -14, 14, 10, 1, { color: WW.town, roof: WW.roof })); // TELEGRAPH
    // water tower on stilts
    for (const [lx, lz] of [[-2.2, -2.2], [2.2, -2.2], [-2.2, 2.2], [2.2, 2.2]]) {
        B.push({ p: [16 + lx, 3.5, 18 + lz], s: [0.5, 7, 0.5], color: WW.darkWood });
    }
    B.push({ p: [16, 9.3, 18], s: [7, 4.6, 7], color: WW.wood }, { p: [16, 12.0, 18], s: [8, 0.8, 8], color: WW.roof });
    // windmill: tower + cross blades
    B.push({ p: [-24, 5.5, 24], s: [1.6, 11, 1.6], color: WW.darkWood }, { p: [-24, 11.4, 24], s: [3, 2.2, 3], color: WW.wood }, { p: [-24, 11.4, 22.2], s: [11, 0.7, 0.3], color: WW.wood }, { p: [-24, 11.4, 22.2], s: [0.7, 11, 0.3], color: WW.wood });
    // stone well with a little roof
    B.push({ p: [0, 0.55, 6], s: [3, 1.1, 3], color: WW.rock }, { p: [-1.2, 1.8, 6], s: [0.3, 2.6, 0.3], color: WW.darkWood }, { p: [1.2, 1.8, 6], s: [0.3, 2.6, 0.3], color: WW.darkWood }, { p: [0, 3.2, 6], s: [3.6, 0.5, 3.6], color: WW.roof });
    // corral (fenced pen) with a water trough
    {
        const cx = 36, cz = 26, cw = 18, cd = 13;
        for (let fx = -cw / 2; fx <= cw / 2; fx += 4.5) {
            B.push({ p: [cx + fx, 0.9, cz - cd / 2], s: [0.35, 1.8, 0.35], color: WW.darkWood });
            B.push({ p: [cx + fx, 0.9, cz + cd / 2], s: [0.35, 1.8, 0.35], color: WW.darkWood });
        }
        for (let fz = -cd / 2; fz <= cd / 2; fz += 4.33) {
            B.push({ p: [cx + cw / 2, 0.9, cz + fz], s: [0.35, 1.8, 0.35], color: WW.darkWood });
        }
        B.push({ p: [cx, 1.45, cz - cd / 2], s: [cw, 0.28, 0.22], color: WW.wood }, { p: [cx, 0.85, cz - cd / 2], s: [cw, 0.28, 0.22], color: WW.wood }, { p: [cx, 1.45, cz + cd / 2], s: [cw, 0.28, 0.22], color: WW.wood }, { p: [cx, 0.85, cz + cd / 2], s: [cw, 0.28, 0.22], color: WW.wood }, { p: [cx + cw / 2, 1.45, cz], s: [0.22, 0.28, cd], color: WW.wood }, { p: [cx + cw / 2, 0.85, cz], s: [0.22, 0.28, cd], color: WW.wood }, { p: [cx - cw / 2, 1.45, cz + 4], s: [0.22, 0.28, 5], color: WW.wood }, { p: [cx - cw / 2, 0.85, cz + 4], s: [0.22, 0.28, 5], color: WW.wood }, { p: [cx - 2, 0.5, cz - 3], s: [4, 1, 1.4], color: WW.darkWood }, { p: [cx - 2, 0.95, cz - 3], s: [3.6, 0.2, 1.1], color: WW.water });
    }
    // covered wagons + barrel stacks + campfire
    for (const [wx, wz, wy] of [[-16, -4, 0], [24, 30, Math.PI * 0.3]]) {
        void wy;
        B.push({ p: [wx, 1.1, wz], s: [3, 1.4, 6.4], color: WW.wood }, { p: [wx, 2.6, wz], s: [3.4, 1.9, 5.2], color: "#d8cdb4" }, { p: [wx - 1.7, 0.6, wz - 2], s: [0.3, 1.2, 1.2], color: WW.darkWood }, { p: [wx + 1.7, 0.6, wz - 2], s: [0.3, 1.2, 1.2], color: WW.darkWood }, { p: [wx - 1.7, 0.6, wz + 2], s: [0.3, 1.2, 1.2], color: WW.darkWood }, { p: [wx + 1.7, 0.6, wz + 2], s: [0.3, 1.2, 1.2], color: WW.darkWood });
    }
    for (const [bx, bz] of [[24, 20], [25.6, 20.8], [24.8, 19], [-11, -32]]) {
        B.push({ p: [bx, 0.8, bz], s: [1.1, 1.6, 1.1], color: WW.wood });
    }
    B.push({ p: [-8, 0.25, 12], s: [2.2, 0.5, 2.2], color: WW.rockDark }, // campfire ring
    { p: [-8, 0.55, 12], s: [1.0, 0.7, 1.0], color: "#ff7a26" });
    // ===== FRONTIER SET PIECES: skyline buttes, a climbable sniper mesa, Boot
    // Hill, the gallows, a stagecoach, giant saguaros and a rock arch. All out on
    // the open flats, clear of the train ring (r 66-82) and the town. =====
    // tapered monument-valley butte: stacked shrinking slabs + a boulder skirt
    const butte = (x, z, w, d, h) => {
        const layers = 5;
        for (let i = 0; i < layers; i++) {
            const t = i / (layers - 1);
            const lw = w * (1 - t * 0.34), ld = d * (1 - t * 0.34);
            B.push({ p: [x, (h / layers) * (i + 0.5), z], s: [lw, h / layers + 0.05, ld], color: i % 2 ? WW.rock : WW.rockDark });
        }
        B.push({ p: [x, h + 0.4, z], s: [w * 0.7, 0.8, d * 0.7], color: WW.rock }); // cap
        for (const [ox, oz] of [[w * 0.6, d * 0.1], [-w * 0.55, d * 0.4], [w * 0.15, -d * 0.6]]) {
            B.push({ p: [x + ox, 1.2, z + oz], s: [3.5, 2.4, 3.6], color: WW.rockDark });
        }
    };
    butte(-152, -136, 20, 20, 30);
    butte(158, 140, 18, 22, 28);
    butte(-158, 132, 16, 16, 26);
    butte(152, -138, 22, 18, 27);
    // climbable flat-topped MESA east of the plaza — a sniper's roost with a
    // tumbled-boulder stair up the north face and low parapet cover on top
    {
        const mx = 96, mz = 46;
        B.push({ p: [mx, 4, mz], s: [22, 8, 20], color: WW.rock }, { p: [mx, 8.2, mz], s: [24, 0.6, 22], color: WW.rockDark }, // rim
        { p: [mx - 9.5, 9.1, mz], s: [3, 1.8, 7], color: WW.rock }, // parapet
        { p: [mx + 9.5, 9.1, mz], s: [3, 1.8, 7], color: WW.rock }, { p: [mx, 9.1, mz - 8.5], s: [8, 1.8, 3], color: WW.rock }, { p: [mx, 2, mz - 11], s: [6, 4, 4], color: WW.rockDark }, // boulder stair
        { p: [mx, 4, mz - 13.5], s: [6, 4, 3.5], color: WW.rock }, { p: [mx, 6, mz - 15], s: [6, 4, 3], color: WW.rockDark });
    }
    // BOOT HILL: a fenced graveyard mound with headstones, wooden crosses, a
    // gnarled dead tree and an entrance sign
    {
        const gx = -125, gz = -45;
        B.push({ p: [gx, 0.3, gz], s: [32, 0.6, 26], color: "#6f5a3c" }); // raised dirt
        for (let fx = -15; fx <= 15; fx += 3) {
            B.push({ p: [gx + fx, 1.1, gz - 13], s: [0.18, 1.6, 0.18], color: WW.rail });
            if (Math.abs(fx) > 3)
                B.push({ p: [gx + fx, 1.1, gz + 13], s: [0.18, 1.6, 0.18], color: WW.rail }); // gap = gate
        }
        for (let fz = -12; fz <= 12; fz += 3) {
            B.push({ p: [gx - 15, 1.1, gz + fz], s: [0.18, 1.6, 0.18], color: WW.rail });
            B.push({ p: [gx + 15, 1.1, gz + fz], s: [0.18, 1.6, 0.18], color: WW.rail });
        }
        B.push({ p: [gx, 1.85, gz - 13], s: [30.2, 0.12, 0.12], color: WW.rail }); // top rail
        const graves = [[-9, -6], [-3, -8], [5, -5], [10, -2], [-11, 3], [-2, 4], [6, 6], [11, 9], [0, 0], [-6, 9]];
        graves.forEach(([ox, oz], i) => {
            if (i % 4 === 3) {
                B.push({ p: [gx + ox, 1.2, gz + oz], s: [0.2, 2, 0.2], color: WW.darkWood }); // cross
                B.push({ p: [gx + ox, 1.7, gz + oz], s: [1.0, 0.2, 0.2], color: WW.darkWood });
            }
            else {
                B.push({ p: [gx + ox, 0.85, gz + oz], s: [1.2, 1.3, 0.32], color: WW.rock }); // headstone
                B.push({ p: [gx + ox, 1.55, gz + oz], s: [0.72, 0.5, 0.34], color: WW.rockDark });
            }
        });
        B.push({ p: [gx - 12, 2.2, gz - 10], s: [0.9, 4.4, 0.9], color: WW.darkWood }, // dead tree
        { p: [gx - 13.6, 4.2, gz - 10], s: [2.6, 0.4, 0.4], color: WW.darkWood }, { p: [gx - 10.4, 4.8, gz - 9], s: [0.4, 0.4, 2.4], color: WW.darkWood }, { p: [gx - 12, 5.1, gz - 10], s: [0.4, 1.8, 0.4], color: WW.darkWood }, { p: [gx - 2, 1.6, gz + 13], s: [0.2, 3.2, 0.2], color: WW.darkWood }, // sign posts
        { p: [gx + 2, 1.6, gz + 13], s: [0.2, 3.2, 0.2], color: WW.darkWood }, { p: [gx, 3.4, gz + 13], s: [4.8, 0.6, 0.3], color: WW.wood });
    }
    // HANGMAN'S GALLOWS on the south edge of town
    {
        const hx = 44, hz = -80;
        B.push({ p: [hx, 0.6, hz], s: [7, 0.3, 6], color: WW.rockDark }, // shadow
        { p: [hx, 1.2, hz], s: [7, 2.4, 6], color: WW.darkWood }, // scaffold platform
        { p: [hx, 0.8, hz + 4], s: [4, 0.5, 2.4], color: WW.darkWood }, // steps up
        { p: [hx, 1.5, hz + 2.4], s: [4, 0.5, 2.0], color: WW.darkWood }, { p: [hx - 2, 4.4, hz - 1], s: [0.35, 6, 0.35], color: WW.wood }, // posts
        { p: [hx + 2, 4.4, hz - 1], s: [0.35, 6, 0.35], color: WW.wood }, { p: [hx, 7.2, hz - 1], s: [4.7, 0.4, 0.4], color: WW.wood }, // crossbeam
        { p: [hx, 5.8, hz - 1], s: [0.08, 2.4, 0.08], color: WW.darkWood }, // noose rope
        { p: [hx, 4.7, hz - 1], s: [0.5, 0.5, 0.5], color: WW.darkWood });
    }
    // parked STAGECOACH in the open lot south of the stable (clear of the
    // boardwalk posts that line the main-street building fronts)
    {
        const sx2 = 139, sz2 = -30;
        B.push({ p: [sx2, 1.7, sz2], s: [3.2, 2.0, 5.2], color: "#5b3a22" }, // cab
        { p: [sx2, 2.9, sz2], s: [3.4, 0.4, 5.6], color: WW.darkWood }, // roof
        { p: [sx2, 2.2, sz2 - 3.3], s: [3.0, 1.2, 1.2], color: WW.darkWood }, // driver bench
        { p: [sx2, 3.2, sz2 + 2.2], s: [2.8, 0.5, 1.2], color: "#3a2717" }, // luggage rack
        { p: [sx2 - 1.65, 1.9, sz2], s: [0.1, 0.9, 1.6], color: "#1a1410" }, // windows
        { p: [sx2 + 1.65, 1.9, sz2], s: [0.1, 0.9, 1.6], color: "#1a1410" });
        for (const [wx, wz, wr] of [[-1.7, -1.9, 0.85], [1.7, -1.9, 0.85], [-1.7, 2.0, 1.1], [1.7, 2.0, 1.1]]) {
            B.push({ p: [sx2 + wx, wr, sz2 + wz], s: [0.3, wr * 2, wr * 2], color: WW.darkWood });
        }
    }
    // giant hero SAGUAROS as landmark cacti
    const saguaro = (x, z, h) => {
        B.push({ p: [x, h / 2, z], s: [1.6, h, 1.6], color: WW.cactus }, { p: [x, h + 0.3, z], s: [1.3, 0.6, 1.3], color: WW.cactus }, { p: [x + 1.5, h * 0.55, z], s: [1.6, 0.9, 0.9], color: WW.cactus }, { p: [x + 2.3, h * 0.72, z], s: [0.9, h * 0.34, 0.9], color: WW.cactus }, { p: [x - 1.4, h * 0.45, z], s: [1.4, 0.85, 0.85], color: WW.cactus }, { p: [x - 2.1, h * 0.62, z], s: [0.85, h * 0.3, 0.85], color: WW.cactus });
    };
    saguaro(-150, 96, 9);
    saguaro(152, 110, 10);
    saguaro(-58, 136, 8);
    // natural ROCK ARCH out in the far flats — ride a horse or beam through it
    {
        const ax2 = -166, az2 = 102;
        B.push({ p: [ax2 - 6, 6, az2], s: [5, 12, 7], color: WW.rockDark }, // legs
        { p: [ax2 + 6, 6, az2], s: [5, 12, 7], color: WW.rockDark }, { p: [ax2, 13, az2], s: [17, 4, 7], color: WW.rock }, // span
        { p: [ax2, 15.5, az2], s: [13, 2, 6], color: WW.rockDark });
    }
    const spawns = [
        { p: [0, 0.2, -6], yaw: Math.PI }, // plaza
        { p: [14, 0.2, 30], yaw: Math.PI * 0.5 }, // by the water tower
        { p: [92, 0.2, -58], yaw: Math.PI * 1.5 }, { p: [124, 0.2, -58], yaw: Math.PI * 1.25 },
        { p: [64, 0.2, -58], yaw: Math.PI * 1.75 }, { p: [-118, 0.2, -86], yaw: Math.PI * 0.25 },
        { p: [-126, 0.2, 84], yaw: Math.PI * 0.75 }, { p: [132, 0.2, 80], yaw: Math.PI * 1.4 },
        { p: [-36, 0.2, 104], yaw: 0 }, { p: [44, 0.2, 108], yaw: Math.PI },
        { p: [ringR + 12, 2.7, 0], yaw: Math.PI * 1.5 }, { p: [-ringR - 12, 2.7, 0], yaw: Math.PI * 0.5 },
        { p: [0, 2.7, ringR + 12], yaw: Math.PI }, { p: [0, 2.7, -ringR - 12], yaw: 0 },
        { p: [-92, 0.2, 20], yaw: Math.PI * 0.5 }, { p: [150, 0.2, -12], yaw: Math.PI * 1.5 },
    ];
    return {
        id: "wild_west_train_pursuit",
        name: "Wild West Train Pursuit",
        modes: ["gungame", "ffa", "relay", "onegun", "treasure", "burnout", "plunder"],
        theme: "day",
        killZ: -12,
        bounds: { min: [-180, -14, -160], max: [180, 34, 160] },
        boxes: B,
        train: {
            cx: 0, cz: 0, radius: ringR, y: railY, wagons: 8, omega: 0.13, arc: 0.075, half: [3.4, 2.4],
            colliders: [
                // Wider deck: landing near an edge should still ride the car. Depth
                // capped so the slab bottom (railY+0.08-0.6 = 1.88) clears a standing
                // player's head (1.7) under the elevated ring — at 1.25 the wagon box
                // engulfed anyone walking beneath it and wedged them until it passed.
                { topOffset: 0.08, depth: 0.6, half: [4.25, 3.15] },
                // Closed cars have real roofs in the visual model; catch players there
                // instead of letting them fall through to the deck slab.
                { wagons: [0, 2, 3, 6], topOffset: 2.05, depth: 0.65, half: [3.55, 2.55] },
                { wagons: [1, 4], topOffset: 1.45, depth: 0.55, half: [3.35, 2.35] },
                { wagons: [7], topOffset: 2.42, depth: 0.7, half: [3.55, 2.55] },
            ],
        },
        // VIOLET grav-beams: ride them onto roofs, mesas and straight over the
        // circling train — the map's vertical layer
        jumpPads: [
            // VIOLET beams everywhere, launching sky-high like the other maps
            { p: [8, 0.1, 14], power: 42, color: "#b46bff" }, // plaza → way over the train
            { p: [-36, 0.1, 100], power: 40, color: "#b46bff" }, // mine
            { p: [46, 0.1, 124], power: 40, color: "#b46bff" }, // bandit camp
            { p: [-118, 0.1, -66], power: 38, color: "#b46bff" }, // west ranch
            { p: [128, 0.1, -4], power: 42, color: "#b46bff" }, // east canyon approach (open sky)
            { p: [88, 0.1, -52], power: 38, color: "#b46bff" }, // main street
            { p: [-24, 0.1, 34], power: 40, color: "#b46bff" }, // windmill
            { p: [-94, 0.1, -26], power: 38, color: "#b46bff" }, // river WEST BANK (was mid-river: touching it drowned you)
            { p: [0, 0.1, -108], power: 40, color: "#b46bff" }, // south flats
            { p: [-126, 0.1, 70], power: 38, color: "#b46bff" }, // south ranch
            { p: [126, 0.1, 60], power: 38, color: "#b46bff" }, // east ranch
            { p: [16, 0.1, 84], power: 40, color: "#b46bff" }, // north flats (open sky)
            { p: [96, 0.1, 62], power: 40, color: "#b46bff" }, // by the sniper mesa
            { p: [-125, 0.1, -60], power: 40, color: "#b46bff" }, // Boot Hill
        ],
        spawns,
        pickups: [
            { p: [-2, 0.4, -26], kind: "health" }, // church nave
            { p: [2, 0.4, 40], kind: "armor" }, // cantina
            { p: [-34, 0.4, -16], kind: "grenade" }, // trading post
            { p: [16, 0.4, 18], kind: "speed" }, // under the water tower
            { p: [116, 0.4, -70], kind: "armor" }, { p: [88, 0.4, -70], kind: "health" },
            { p: [38, 0.4, -42], kind: "grenade" }, { p: [-36, 0.8, 108], kind: "rocket" },
            { p: [42, 1.2, 116], kind: "imperion" }, { p: [-118, 0.4, -78], kind: "health" },
            // the three ring prizes hovered in mid-air over the rail posts (only a
            // train rider could touch them) — they sit on the raised depot
            // platforms now, on the track side (stairs up from the outside)
            { p: [-126, 0.4, 84], kind: "armor" }, { p: [0, railY + 0.3, -ringR - 12.5], kind: "grenade" },
            { p: [ringR + 11, railY + 0.3, 0], kind: "speed" }, { p: [-ringR - 11, railY + 0.3, 0], kind: "dmg" },
            { p: [96, 8.9, 46], kind: "dmg" }, // reward on the sniper mesa
            { p: [-125, 0.4, -45], kind: "health" }, // Boot Hill
            { p: [44, 0.4, -74.5], kind: "armor" }, // at the gallows' foot (its 1.75 m step can't be climbed)
        ],
        vehicles: [
            { p: [139, 0.7, -58], yaw: Math.PI, kind: "horse" }, { p: [145, 0.7, -58], yaw: Math.PI, kind: "horse" },
            { p: [151, 0.7, -58], yaw: Math.PI, kind: "horse" }, { p: [-112, 0.7, -78], yaw: Math.PI * 0.25, kind: "horse" },
            { p: [-128, 0.7, 84], yaw: Math.PI * 0.75, kind: "horse" }, { p: [126, 0.7, 72], yaw: Math.PI * 1.3, kind: "horse" },
            { p: [-16, 0.7, -102], yaw: 0, kind: "horse" }, { p: [54, 0.7, 108], yaw: Math.PI, kind: "horse" },
            { p: [ringR + 18, 2.9, 0], yaw: Math.PI * 1.5, kind: "horse" }, { p: [0, 2.9, ringR + 18], yaw: Math.PI, kind: "horse" },
            { p: [34, 0.7, 26], yaw: Math.PI * 0.6, kind: "horse" }, { p: [39, 0.7, 24], yaw: Math.PI * 1.1, kind: "horse" }, // corral
            { p: [-146, 0.8, 28], yaw: Math.PI * 0.35, kind: "camel" }, { p: [-132, 0.8, 44], yaw: Math.PI * 0.45, kind: "camel" },
            { p: [148, 0.8, 24], yaw: Math.PI * 1.55, kind: "camel" }, { p: [132, 0.8, -18], yaw: Math.PI * 1.4, kind: "camel" },
        ],
    };
}
export const MAP_MEDIEVAL_CASTLE = medievalCastle();
export const MAP_WILD_WEST_TRAIN = wildWestTrainPursuit();
/**
 * Enrich every map: add health/armor pickups (and karts on the larger maps) so
 * all maps have the new gameplay layer, plus a few extra cover crates so none
 * feel bare. Mutates the map defs in place at module load.
 */
/**
 * Enrich EVERY map with gameplay + interest: karts (all maps) and a few
 * helicopters, bounce/jump pads for verticality, extra cover crates, a raised
 * sniper platform on open maps, and health/armor pickups — all placed only
 * where the column is clear of existing geometry and away from spawns.
 */
/** Drop the 4 extra rides onto a map's OWN spawn points (guaranteed-clear
 *  ground), spread apart and away from existing vehicles. Skips maps too small
 *  to breathe. Arena mode strips all vehicles anyway, so this is safe. */
function scatterExtraVehicles(m) {
    if (m.id === "medieval_castle" || m.id === "wild_west_train_pursuit" || m.id === "mega_chibre")
        return;
    const span = Math.min(m.bounds.max[0] - m.bounds.min[0], m.bounds.max[2] - m.bounds.min[2]);
    if (span < 55)
        return; // too cramped — skip
    const kinds = ["speeder", "artillery", "gunship", "interceptor"];
    m.vehicles ??= [];
    const taken = m.vehicles.map((v) => [v.p[0], v.p[2]]);
    const MIN_SEP = 16;
    const clear = (x, z) => {
        // away from other placed vehicles AND not overlapping a tall obstacle box
        if (taken.some(([tx, tz]) => Math.hypot(tx - x, tz - z) < MIN_SEP))
            return false;
        for (const b of m.boxes) {
            if (b.s[1] < 1.2 || b.p[1] - b.s[1] / 2 > 3)
                continue; // ignore floor + high ledges
            if (Math.abs(x - b.p[0]) < b.s[0] / 2 + 2 && Math.abs(z - b.p[2]) < b.s[2] / 2 + 2)
                return false;
        }
        return true;
    };
    // candidate spots = the map's spawn points (valid ground), shuffled-ish by id hash
    const spots = m.spawns.filter((sp) => sp.p[1] < 6); // ground-level spawns only
    let ki = 0;
    for (const sp of spots) {
        if (ki >= kinds.length)
            break;
        const x = sp.p[0], z = sp.p[2];
        if (!clear(x, z))
            continue;
        taken.push([x, z]);
        m.vehicles.push({ p: [x, sp.p[1] + 0.6, z], yaw: sp.yaw, kind: kinds[ki++] });
    }
}
function enrichMaps() {
    for (const m of Object.values(MAPS)) {
        scatterExtraVehicles(m); // 4 extra rides on every roomy map (arena strips them)
        if (m.id === "zarya_island" || m.id === "neo_island" || m.id === "chibre_arena" || m.id === "plumbing_plaza" || m.id === "mega_chibre")
            continue; // hand-authored (platform-over-void; auto-scatter would float off the edge)
        // rooftops_xl: pads are hand-placed flush against tower walls — auto
        // pads would float in the open alleys (the exact complaint)
        const skipPads = m.id === "rooftops_xl" || m.id === "rooftop_spiderweb";
        m.pickups ??= [];
        m.vehicles ??= [];
        m.jumpPads ??= [];
        const cx = (m.bounds.min[0] + m.bounds.max[0]) / 2;
        const cz = (m.bounds.min[2] + m.bounds.max[2]) / 2;
        const rx = (m.bounds.max[0] - m.bounds.min[0]) / 2;
        const rz = (m.bounds.max[2] - m.bounds.min[2]) / 2;
        // floor level = the MODAL spawn height, not the minimum: a map with a few
        // below-ground spawns (Medieval Castle's dungeon at y=-4.8) otherwise gets
        // every auto-placed pad/chest buried under the terrain — and a live jump
        // pad inside the dungeon. Ties break low to keep the old behavior.
        // CRITICAL: gy must come from the RAW height inside the modal bucket, not
        // the ROUNDED bucket value — ground spawns sit at y≈0.1-0.2, and rounding
        // to 0 put gy at -0.1 INSIDE the floor slab, so clear() rejected every
        // candidate column and NO map got items/chests/crates at all (the
        // "powerups are gone again" regression).
        const hCount = new Map();
        for (const s of m.spawns) {
            const h = Math.round(s.p[1] * 2) / 2;
            const e = hCount.get(h) ?? { n: 0, minRaw: s.p[1] };
            e.n++;
            e.minRaw = Math.min(e.minRaw, s.p[1]);
            hCount.set(h, e);
        }
        let modeH = 0, modeN = -1, modeRaw = 0;
        for (const [h, e] of hCount) {
            if (e.n > modeN || (e.n === modeN && h < modeH)) {
                modeH = h;
                modeN = e.n;
                modeRaw = e.minRaw;
            }
        }
        const gy = modeRaw - 0.1;
        // does the column [x±hw, gy..gy+tall] avoid every existing box?
        const clear = (x, z, hw, tall) => {
            for (const b of m.boxes) {
                const bx = b.s[0] / 2, by = b.s[1] / 2, bz = b.s[2] / 2;
                if (x - hw < b.p[0] + bx && x + hw > b.p[0] - bx &&
                    gy < b.p[1] + by && gy + tall > b.p[1] - by &&
                    z - hw < b.p[2] + bz && z + hw > b.p[2] - bz)
                    return false;
            }
            return true;
        };
        const farSpawns = (x, z, min) => m.spawns.every((s) => Math.hypot(s.p[0] - x, s.p[2] - z) > min);
        const inBounds = (x, z) => x > m.bounds.min[0] + 3 && x < m.bounds.max[0] - 3 && z > m.bounds.min[2] + 3 && z < m.bounds.max[2] - 3;
        // candidate positions: a dense interior grid, ordered from the middle
        // outward so features cluster in playable space, not against the walls
        const cands = [];
        const step = Math.max(3.5, Math.min(rx, rz) / 5);
        for (let x = m.bounds.min[0] + 4; x <= m.bounds.max[0] - 4; x += step) {
            for (let z = m.bounds.min[2] + 4; z <= m.bounds.max[2] - 4; z += step) {
                cands.push([x, z]);
            }
        }
        cands.sort((a, b) => Math.hypot(a[0] - cx, a[1] - cz) - Math.hypot(b[0] - cx, b[1] - cz));
        const takeClear = (hw, tall, minSpawn, used, n, spacing = 5) => {
            const out = [];
            for (const [x, z] of cands) {
                if (out.length >= n)
                    break;
                if (!inBounds(x, z) || !clear(x, z, hw, tall) || !farSpawns(x, z, minSpawn))
                    continue;
                if (used.some(([ux, uz]) => Math.hypot(ux - x, uz - z) < spacing))
                    continue;
                out.push([x, z]);
                used.push([x, z]);
            }
            return out;
        };
        const used = [];
        // karts on EVERY map (2), + a heli on the larger ones
        if (m.vehicles.length === 0) {
            const kp = takeClear(1.4, 1.6, 4.5, used, 2, 8);
            kp.forEach(([x, z], i) => m.vehicles.push({ p: [x, gy + 0.5, z], yaw: i * Math.PI, kind: "kart" }));
            // a helicopter on every map (needs a bit of open room; falls back to a
            // looser spot on cramped maps so one always fits)
            let hp = takeClear(1.8, 2, 5, used, 1, 7);
            if (hp.length === 0)
                hp = takeClear(1.6, 1.6, 3.5, used, 1, 4);
            hp.forEach(([x, z]) => m.vehicles.push({ p: [x, gy + 0.9, z], yaw: 0, kind: "heli" }));
        }
        // bounce pads (3) — need clear headroom to launch
        const pads = takeClear(1.4, 4, 3.5, used, 3, 6);
        if (!skipPads)
            for (const [x, z] of pads)
                m.jumpPads.push({ p: [x, gy + 0.15, z] });
        // one IMPERION beam-rifle chest per map (the long-range counterweight)
        if (!m.pickups.some((pu) => pu.kind === "imperion")) {
            const spot = takeClear(1.2, 3, 9, used, 1, 6)[0];
            if (spot)
                m.pickups.push({ kind: "imperion", p: [spot[0], gy + 0.3, spot[1]] });
        }
        // one ANTIGRAV chest per map — a single-use, never-respawning map event
        if (!m.pickups.some((pu) => pu.kind === "antigrav")) {
            const spot = takeClear(1.2, 3, 8, used, 1, 6)[0];
            if (spot)
                m.pickups.push({ kind: "antigrav", p: [spot[0], gy + 0.3, spot[1]] });
        }
        // one BODY DOUBLE chest per map — drops a holo-clone of the grabber
        if (!m.pickups.some((pu) => pu.kind === "decoy")) {
            const spot = takeClear(1.2, 3, 7, used, 1, 6)[0];
            if (spot)
                m.pickups.push({ kind: "decoy", p: [spot[0], gy + 0.3, spot[1]] });
        }
        // pickups (keep any existing ITEMS, else place a mix of health / armor /
        // grenades). NB: must check item KINDS, not length — the imperion chest
        // pushed above already made length ≥ 1, which silently skipped this block
        // on every auto-enriched map (the "no health/grenades anywhere" bug).
        if (!m.pickups.some((pu) => pu.kind === "health" || pu.kind === "armor" || pu.kind === "grenade")) {
            const pk = takeClear(1, 2, 3.5, used, 6, 4.5);
            const kinds = ["health", "armor", "grenade", "health", "grenade", "armor"];
            pk.forEach(([x, z], i) => m.pickups.push({ p: [x, gy + 0.2, z], kind: kinds[i % kinds.length] }));
        }
        // chests: a jetpack chest + a rocket-launcher chest, placed in clear spots
        // (kind-guarded like imperion/antigrav — hand-authored maps ship their own)
        const chests = takeClear(1, 2, 5, used, 2, 12);
        let ci = 0;
        if (!m.pickups.some((pu) => pu.kind === "jetpack") && chests[ci]) {
            m.pickups.push({ p: [chests[ci][0], gy + 0.3, chests[ci][1]], kind: "jetpack" });
            ci++;
        }
        if (!m.pickups.some((pu) => pu.kind === "rocket") && chests[ci]) {
            m.pickups.push({ p: [chests[ci][0], gy + 0.3, chests[ci][1]], kind: "rocket" });
        }
        // extra cover crates at varied heights
        const crateCol = m.theme === "day" ? "#7a7f88" : "#3b2f5a";
        const crates = takeClear(1.2, 2, 3.5, used, 4, 5);
        crates.forEach(([x, z], i) => {
            const h = 1.4 + (i % 3) * 0.7;
            m.boxes.push({ p: [x, gy + h / 2, z], s: [2.2, h, 2.2], color: crateCol });
        });
        // raised sniper platform on open maps (only if the center column is clear)
        if (clear(cx, cz, 5, 6) && farSpawns(cx, cz, 8)) {
            const plat = m.theme === "day" ? "#8b8f98" : "#241a3a";
            m.boxes.push({ p: [cx, gy + 3.6, cz], s: [8, 0.4, 8], color: plat });
            m.boxes.push({ p: [cx, gy + 4.6, cz - 3.6], s: [8, 1.6, 0.3], color: plat }); // back rail
            // a bounce pad next to it flings you up onto the deck
            if (!skipPads && clear(cx + 6, cz, 1.5, 4))
                m.jumpPads.push({ p: [cx + 6, gy + 0.15, cz] });
            // health pickup as the reward on top
            m.pickups.push({ p: [cx, gy + 4.0, cz], kind: "health" });
        }
    }
}
// ---------------------------------------------------------------------------
// MAP — "CHIBRE ARENA": a faithful import of the BLOCK CITY layout from the
// photobooth "Chibre Kart Arena" bomb-battle (streets & alleys of blocky
// towers). The GameCube-era block spec is converted 1:1 into NEON PURGE boxes
// and re-skinned as a neon-lit city so it matches the rest of the roster; the
// day/night cycle runs on it too. Home of the "Arena" (deathmatch) mode.
// ---------------------------------------------------------------------------
function chibreArena() {
    // BLOCK CITY spec, verbatim from photobooth/app/public/chibre-arena.js
    // (track units on a 6400×6400 square; block = top-left corner + w/h + height).
    const WORLD = 6400;
    const blocks = [
        { x: 1150, y: 1150, w: 1500, h: 1100, hgt: 2.6 },
        { x: 3750, y: 1050, w: 1450, h: 1250, hgt: 3.1 },
        { x: 1050, y: 3850, w: 1650, h: 1300, hgt: 2.2 },
        { x: 3800, y: 3800, w: 620, h: 1350, hgt: 2.8 },
        { x: 4740, y: 3800, w: 620, h: 1350, hgt: 2.8 },
        { x: 2950, y: 2900, w: 560, h: 520, hgt: 1.2 },
        { x: 0, y: 2850, w: 380, h: 660, hgt: 0.9 },
        { x: WORLD - 380, y: 2850, w: 380, h: 660, hgt: 0.9 },
    ];
    const boxSpawns = [
        [700, 700], [WORLD - 700, 700], [700, WORLD - 700], [WORLD - 700, WORLD - 700],
        [4580, 4470], [3220, 2250], [3200, 4650],
    ];
    const K = 0.011; // track units → world units (arena ≈ 70 m square)
    const HMUL = 3.0; // block height multiplier → 2.7–9.3 m towers
    const H = (WORLD / 2) * K; // arena half-extent ≈ 35.2
    const tx = (v) => (v - WORLD / 2) * K; // track → centered world
    const boxes = [];
    // ROOFTOPS-matching palette (warm beige, no neon glow — tall boxes get the
    // renderer's window facades automatically, so it reads as a real city)
    boxes.push({ p: [0, -0.5, 0], s: [H * 2 + 4, 1, H * 2 + 4], color: "#7c7266" }); // ground
    const wallH = 16, wallY = wallH / 2, wt = 1; // tall: climb-karts can't escape
    boxes.push({ p: [0, wallY, -H - wt / 2], s: [H * 2 + 4, wallH, wt], color: RF.wall });
    boxes.push({ p: [0, wallY, H + wt / 2], s: [H * 2 + 4, wallH, wt], color: RF.wall });
    boxes.push({ p: [-H - wt / 2, wallY, 0], s: [wt, wallH, H * 2 + 4], color: RF.wall });
    boxes.push({ p: [H + wt / 2, wallY, 0], s: [wt, wallH, H * 2 + 4], color: RF.wall });
    // the city towers (blocks) — beige walls + a capped roof slab, like Rooftops
    blocks.forEach((b) => {
        const cx = tx(b.x + b.w / 2), cz = tx(b.y + b.h / 2);
        const sx = b.w * K, sz = b.h * K, hy = b.hgt * HMUL;
        boxes.push({ p: [cx, hy / 2, cz], s: [sx, hy, sz], color: RF.wall });
        boxes.push({ p: [cx, hy + 0.15, cz], s: [sx, 0.3, sz], color: RF.roof }); // roof cap
    });
    // spawns: the 2 duel spawns + the open box-spawn corners, all facing centre
    const spawnPts = [
        [tx(WORLD * 0.12), 0], [tx(WORLD * 0.88), 0],
        ...boxSpawns.slice(0, 4).map(([x, y]) => [tx(x), tx(y)]),
        [0, tx(2250)], [0, tx(4650)],
    ];
    const spawns = spawnPts.map(([sx, sz]) => ({
        p: [sx, 0.1, sz],
        yaw: Math.atan2(sx, sz), // forward = (-sin,-cos) → points at the centre
    }));
    // pickups at the item-box spots (matches every other map's kit + a chest)
    const pkKinds = ["health", "armor", "grenade", "health", "armor"];
    const pickups = boxSpawns.slice(0, 5).map(([x, y], i) => ({
        p: [tx(x), 0.3, tx(y)],
        kind: pkKinds[i % pkKinds.length],
    }));
    pickups.push({ p: [tx(3220), 0.3, tx(2250)], kind: "imperion" });
    pickups.push({ p: [tx(3200), 0.3, tx(4650)], kind: "antigrav" });
    pickups.push({ p: [0, 0.3, 0], kind: "grenade" });
    return {
        id: "chibre_arena",
        name: "Block City",
        modes: ["arena", "ffa", "relay", "onegun", "treasure", "gungame", "burnout", "plunder"],
        theme: "day", // day → the day/night cycle runs on it
        killZ: -12,
        bounds: { min: [-H - 3, -14, -H - 3], max: [H + 3, 16, H + 3] },
        boxes,
        spawns,
        pickups,
    };
}
export const MAP_CHIBRE_ARENA = chibreArena();
// ---------------------------------------------------------------------------
// MAP — "PLUMBING PLAZA": the second Chibre Arena layout — an open plaza dotted
// with green pipe columns around a central fountain. Same 1:1 track-unit
// conversion + neon re-skin as the Block City arena; day/night cycle runs.
// ---------------------------------------------------------------------------
function plumbingPlaza() {
    const WORLD = 6400;
    // PLUMBING PLAZA spec, verbatim from photobooth/app/public/chibre-arena.js.
    // round pipes become square neon columns (NEON has no cylinder colliders).
    const pipes = [
        { x: 1050, y: 1050, w: 700, h: 700, hgt: 2.2 },
        { x: 4650, y: 1050, w: 700, h: 700, hgt: 2.6 },
        { x: 1050, y: 4650, w: 700, h: 700, hgt: 2.6 },
        { x: 4650, y: 4650, w: 700, h: 700, hgt: 2.2 },
        { x: 2850, y: 1500, w: 700, h: 700, hgt: 1.8 },
        { x: 2850, y: 4200, w: 700, h: 700, hgt: 1.8 },
    ];
    const fountain = { x: 2900, y: 2900, w: 600, h: 600, hgt: 0.9 };
    const boxSpawns = [
        [3200, 700], [3200, WORLD - 700], [700, 3200], [WORLD - 700, 3200],
        [1900, 1900], [4500, 4500], [3200, 2400],
    ];
    const K = 0.011, HMUL = 3.0;
    const H = (WORLD / 2) * K;
    const tx = (v) => (v - WORLD / 2) * K;
    // ROOFTOPS-matching palette (warm beige, no neon glow)
    const boxes = [];
    boxes.push({ p: [0, -0.5, 0], s: [H * 2 + 4, 1, H * 2 + 4], color: "#7c7266" }); // ground
    const wallH = 16, wallY = wallH / 2, wt = 1; // tall: climb-karts can't escape
    boxes.push({ p: [0, wallY, -H - wt / 2], s: [H * 2 + 4, wallH, wt], color: RF.wall });
    boxes.push({ p: [0, wallY, H + wt / 2], s: [H * 2 + 4, wallH, wt], color: RF.wall });
    boxes.push({ p: [-H - wt / 2, wallY, 0], s: [wt, wallH, H * 2 + 4], color: RF.wall });
    boxes.push({ p: [H + wt / 2, wallY, 0], s: [wt, wallH, H * 2 + 4], color: RF.wall });
    // pipe columns — beige with a terracotta trim ring (rooftop accent)
    for (const b of pipes) {
        const cx = tx(b.x + b.w / 2), cz = tx(b.y + b.h / 2), sx = b.w * K, sz = b.h * K, hy = b.hgt * HMUL;
        boxes.push({ p: [cx, hy / 2, cz], s: [sx, hy, sz], color: RF.wall });
        boxes.push({ p: [cx, hy - 0.5, cz], s: [sx + 0.3, 0.5, sz + 0.3], color: RF.accent }); // trim ring
    }
    // central fountain: a low platform + a small spout column
    const fcx = tx(fountain.x + fountain.w / 2), fcz = tx(fountain.y + fountain.h / 2);
    boxes.push({ p: [fcx, (fountain.hgt * HMUL) / 2, fcz], s: [fountain.w * K, fountain.hgt * HMUL, fountain.h * K], color: RF.roof2 });
    boxes.push({ p: [fcx, fountain.hgt * HMUL + 1.2, fcz], s: [1.2, 2.4, 1.2], color: RF.accent });
    const spawnPts = [
        [tx(WORLD * 0.12), 0], [tx(WORLD * 0.88), 0],
        [0, tx(700)], [0, tx(WORLD - 700)],
        [tx(1900), tx(1900)], [tx(4500), tx(4500)],
        [tx(700), 0], [tx(WORLD - 700), 0],
    ];
    const spawns = spawnPts.map(([sx, sz]) => ({
        p: [sx, 0.1, sz],
        yaw: Math.atan2(sx, sz),
    }));
    const pkKinds = ["health", "armor", "grenade", "health", "armor"];
    const pickups = boxSpawns.slice(0, 5).map(([x, y], i) => ({
        p: [tx(x), 0.3, tx(y)],
        kind: pkKinds[i % pkKinds.length],
    }));
    pickups.push({ p: [tx(4500), 0.3, tx(4500)], kind: "imperion" });
    pickups.push({ p: [tx(1900), 0.3, tx(1900)], kind: "antigrav" });
    // at the fountain's foot — "atop" it (2.7 m sheer rim, no steps) was out of
    // reach on foot, and deathmatch turns this spot into the weapon chest
    pickups.push({ p: [fcx + 4.6, 0.3, fcz], kind: "grenade" });
    return {
        id: "plumbing_plaza",
        name: "Plumbing Plaza",
        modes: ["arena", "ffa", "relay", "onegun", "treasure", "gungame", "burnout", "plunder"],
        theme: "day",
        killZ: -12,
        bounds: { min: [-H - 3, -14, -H - 3], max: [H + 3, 16, H + 3] },
        boxes,
        spawns,
        pickups,
    };
}
export const MAP_PLUMBING_PLAZA = plumbingPlaza();
// Map roster: mostly bright "normal" maps, plus a single neon map for flavor.
/** The map as it should be built for a given mode. Arena (kart) mode strips
 *  boxes tagged "stair" — karts climb roofs directly, so staircases are just
 *  clutter (e.g. Rooftops XL). Both server collision and client render use it. */
/** Arena blows the whole map up 2x so the little karts have a big playfield.
 *  (The renderer pushes its far plane + sky dome out to match — otherwise the
 *  far side of a big 2x map hides behind the sky = "outside the map".) */
// 1.5 (was 2): MKDD Bob-omb Blast is fun because the arena is CRAMPED — you
// are never more than a few seconds from a rival. At 2x, 2-4 karts spent
// their time DRIVING TOWARD the fight instead of being in it (encounter
// density is the whole mode).
const ARENA_SCALE = 1.5;
/** ARENA-ONLY launch pads for the two flat plaza maps that ship with NONE.
 *  Every other arena map already scatters jump pads (rooftops 8, rooftops_xl
 *  23, spiderweb 50, village 7, mega_chibre 5) so its karts get the hoppy MK
 *  Bob-omb-Blast verticality — you leap over a bomb-mine field, gain height to
 *  lob down onto the pack, and reach the contested central mount. `chibre_arena`
 *  and `plumbing_plaza` had ZERO, so they read as the "big flat field" the arena
 *  mission complains about. These pads bring them to parity.
 *
 *  Coordinates are in the ALREADY-SCALED arena frame (they are merged in after
 *  the 1.5x scale, NOT scaled again). Positions were probed clear of every
 *  existing collider + the bomb-box grid (a 4-fold-symmetric ring around each
 *  map's central platform, in the open cardinal/diagonal lanes). Power 26 with
 *  the arena comfort-kart 0.55 multiplier (entities.ts) gives a ~3.9 m apex — a
 *  readable hop, not a seasick launch — and the steep arenaFallGravity snaps the
 *  kart back down. `fling` is left undefined so these are arena LAUNCH pads, not
 *  the vertical lift-shafts karts skip. ARENA-ONLY: injected solely in the
 *  `mode === "arena"` branch below, so the ffa/gungame/onegun/... variants of
 *  these two maps stay byte-for-byte unchanged (verified in bench-arena-pads). */
const ARENA_LAUNCH_PADS = {
    // central platform: top ~5.4 m, half ~4.6 — 4 cardinal pads at r=18 (all clear)
    chibre_arena: [
        { p: [18, 0.4, 0], power: 26 },
        { p: [-18, 0.4, 0], power: 26 },
        { p: [0, 0.4, 18], power: 26 },
        { p: [0, 0.4, -18], power: 26 },
    ],
    // central platform: top ~4 m, half ~5 — 4 diagonal pads at r≈20 (all clear;
    // this map's ±Z cardinal lanes are blocked by the pipe runs past r=12)
    plumbing_plaza: [
        { p: [14, 0.4, 14], power: 26 },
        { p: [-14, 0.4, 14], power: 26 },
        { p: [14, 0.4, -14], power: 26 },
        { p: [-14, 0.4, -14], power: 26 },
    ],
};
export function mapForMode(map, mode) {
    // PURGE (zombies): on foot only — no karts/helis to cheese the horde from.
    // It ALSO gets the weapon chest (same rule as deathmatch below).
    // ROCKET chests are STRIPPED from their authored spots: in purge the
    // launcher is an EVENT — the server drops ONE chest at a random spawn
    // every 2nd round (GunGameRoom.placePurgeRocketChest) and it sits there
    // until someone claims it, instead of being farmable map furniture.
    if (mode === "purge") {
        const stripped = { ...map, vehicles: [] };
        return { ...stripped, pickups: withWeaponChest(stripped).filter((pu) => pu.kind !== "rocket") };
    }
    if (mode === "horde") {
        // HORDEBEARER: deathmatch gunplay + a roaming zombie horde. Vehicles are
        // stripped for the purge reason — a kart/heli rider can't be clawed, so
        // wheels would let players farm the horde risk-free. The deathmatch
        // weapon chest stays (rockets too: they're the crowd's zombie-clearing
        // tools, and unlike purge there is no rocket-event system running).
        const stripped = { ...map, vehicles: [] };
        return { ...stripped, pickups: withWeaponChest(stripped) };
    }
    if (mode === "ffa" || mode === "relay" || mode === "royale") {
        // DEATHMATCH gets ONE weapon chest (a big 3D gun): touch it for a random
        // gun-game ladder weapon, respawns 30s later. It takes over the most
        // central ground pickup spot so the placement is always valid.
        // IMPERION RELAY plays deathmatch gunplay underneath the relay, so it
        // gets the same chest; the map's IMPERION chest doubles as the relay.
        // BATTLE ROYALE is FFA-with-lives — same chest, same reasoning.
        return { ...map, pickups: withWeaponChest(map) };
    }
    if (mode === "onegun") {
        // ONE GUN: the weapon chest IS the gun — same central placement as the
        // deathmatch chest, but claiming it makes you the holder (GunGameRoom).
        // Every OTHER ranged-weapon source is stripped: imperion and rocket
        // chests would let the unarmed crowd shoot back, and the mode's whole
        // shape is "one gun, many knives". Grenades/jetpacks/power-ups stay —
        // they are the crowd's tools for closing on the holder.
        return { ...map, pickups: withWeaponChest(map).filter((pu) => pu.kind !== "imperion" && pu.kind !== "rocket") };
    }
    // BURNOUT is gun-game-shaped — same ladder, same maps, everything on. It
    // deliberately gets NO weapon chest: a chest gun would sidestep the rung
    // you're standing on, which is the entire mode. Falls through unchanged.
    if (mode !== "arena")
        return map;
    // Arena (kart bomb battle): strip staircases (karts climb roofs / are blocked
    // by walls), remove ALL powerups + loose map vehicles (the ONLY weapon is the
    // kart bomb), and SCALE the whole arena 2x for a big playfield.
    const k = ARENA_SCALE;
    const s3 = (v) => [v[0] * k, v[1] * k, v[2] * k];
    const scaled = {
        ...map,
        boxes: map.boxes.filter((b) => b.tag !== "stair").map((b) => ({ ...b, p: s3(b.p), s: s3(b.s) })),
        spawns: map.spawns.map((sp) => ({ ...sp, p: s3(sp.p) })),
        // pads must reach the 2x-taller roofs: apex ∝ power², so power scales √k
        jumpPads: (map.jumpPads ?? []).map((j) => ({ ...j, p: s3(j.p), power: j.power !== undefined ? j.power * Math.sqrt(k) : j.power })),
        train: undefined, // rideable circuits are 1x-absolute — never valid on a scaled arena
        plane: undefined,
        // STATIC world geometry with absolute coords, like boxes — scale it or it
        // floats detached in the 1.5x arena (a rider reeled to a 1x endpoint lands
        // on nothing). No built-in arena map declares these, but the custom/import
        // arena path (GunGameRoom map selection bypasses the modes gate) reaches
        // here, so keep the scaled MapDef self-consistent. `?.map` preserves
        // undefined when absent, matching the plain `...map` passthrough.
        ziplines: map.ziplines?.map((z) => ({ a: s3(z.a), b: s3(z.b) })),
        drawbridges: map.drawbridges?.map((d) => ({
            ...d, p: s3(d.p), s: s3(d.s), lever: s3(d.lever), lever2: d.lever2 ? s3(d.lever2) : d.lever2,
        })),
        killZ: map.killZ * k,
        bounds: { min: s3(map.bounds.min), max: s3(map.bounds.max) },
        // the floor edge scales with the map — the invisible wall must too, or
        // karts drive off the (scaled) floor 2m before the wall exists
        ringInset: (map.ringInset ?? 2) * k,
        vehicles: [],
    };
    // Bob-omb Blast: item boxes are the ONLY item, and every box is a bomb.
    // (The arena used to park a rocket chest at the centre — MKDD has no such
    // thing, so it is gone; the rocket code paths stay but are never stocked.)
    const arenaPickups = arenaBombBoxes(scaled);
    // Bring the two padless flat plaza arenas up to hoppy MK parity (see
    // ARENA_LAUNCH_PADS). Absolute scaled coords, so appended AFTER the scale.
    const extraPads = ARENA_LAUNCH_PADS[map.id];
    const jumpPads = extraPads ? [...(scaled.jumpPads ?? []), ...extraPads] : scaled.jumpPads;
    return { ...scaled, pickups: arenaPickups, jumpPads };
}
/** The most central ground pickup spot becomes the WEAPON CHEST (a giant 3D
 *  gun granting a random gun-game ladder weapon, 30s respawn). */
function withWeaponChest(map) {
    const pool = map.pickups ?? [];
    const cx = (map.bounds.min[0] + map.bounds.max[0]) / 2;
    const cz = (map.bounds.min[2] + map.bounds.max[2]) / 2;
    let best = null;
    let bd = Infinity;
    for (const pk of pool) {
        if (pk.kind !== "health" && pk.kind !== "armor" && pk.kind !== "grenade")
            continue;
        const d = (pk.p[0] - cx) ** 2 + (pk.p[2] - cz) ** 2;
        if (d < bd) {
            bd = d;
            best = pk;
        }
    }
    if (!best)
        return pool;
    return [...pool.filter((pk) => pk !== best), { p: best.p, kind: "weaponchest" }];
}
/** A distributed grid of bomb boxes across a map's open ground (Arena mode). */
function arenaBombBoxes(map) {
    const b = map.bounds;
    const span = Math.min(b.max[0] - b.min[0], b.max[2] - b.min[2]);
    // buildings/obstacles to avoid — boxes with real height that DON'T span the
    // whole map (excludes the ground/floor + long perimeter walls, which would
    // otherwise reject every candidate).
    const obst = map.boxes.filter((bx) => bx.s[1] > 1 &&
        bx.s[0] < span * 0.7 && bx.s[2] < span * 0.7 &&
        bx.p[1] + bx.s[1] / 2 > 1.5);
    const clear = (x, z) => !obst.some((bx) => Math.abs(x - bx.p[0]) < bx.s[0] / 2 + 1.6 && Math.abs(z - bx.p[2]) < bx.s[2] / 2 + 1.6);
    const out = [];
    const step = Math.max(16, span / 6); // ~more boxes on the big 2x map
    for (let x = b.min[0] + 9; x <= b.max[0] - 9; x += step) {
        for (let z = b.min[2] + 9; z <= b.max[2] - 9; z += step) {
            if (clear(x, z))
                out.push({ p: [x, 0.3, z], kind: "grenade" });
        }
    }
    return out;
}
// ---------------------------------------------------------------------------
// MAP — "PIRATE SHIP": one huge galleon adrift on the open sea. A full main
// deck with a raised forecastle (bow) and quarterdeck (helm, stern), an
// enclosed gun-deck cargo hold below, three masts with yards + canvas sails,
// cannons along the bulwarks, and boarding ramps from the water. Built
// entirely from the same greybox boxes as every other map, so it renders in
// the game, the lobby preview, and the server collision identically.
// Ship runs along Z: stern at -Z, bow (prow) at +Z. Width along X.
// ---------------------------------------------------------------------------
const PS = {
    sea: "#2d6774", // water surface (must be in the renderer's WATER_COLORS)
    hull: "#5a3a22", // dark hull planking
    hullDark: "#42291a", // waterline strake
    deck: "#8a6239", // sun-bleached deck planks
    deckDark: "#6f4a2a", // hold floor / shadowed timber
    rail: "#3f2a19", // bulwark + stair trim
    mast: "#4a3320", // masts, yards, spars
    sail: "#e8e0cf", // canvas
    sailDark: "#d3c7ad",
    gold: "#c9a53a", // figurehead + trim
    cannon: "#26262b", // iron cannon
    crate: "#7a5a34", // cargo
    seabed: "#b0985e", // sandy sea floor (walkable when you dive)
};
// SHIP TAGS (2026-11, realism pass): `shipSpar` / `shipSail` / `shipGunE|W` /
// `shipHull` / `shipStern` / `shipTop` mark galleon boxes the CLIENT redraws or dresses
// (shipDressing.ts: round spars, billowed canvas, cast-iron barrels, gunports,
// stern lights, standing rigging). Visual only — tags never reach collision
// (movement.ts only reads "water" / "practice_mover"), so the colliders here
// are exactly what they were.
/** Rideable rigging cables, filled in while the ship is built. */
const PIRATE_ZIPLINES = [];
function pirateShip() {
    const B = [];
    // ONE galleon's boxes, built relative to its own origin, then STAMPED across
    // the fleet (3 ships). The SEA + seabed are global (added once, below).
    const ship = [];
    // height-aware stair: a run of ~0.5 m risers from (x, baseY, z) climbing
    // `rise` metres along `dir`, base height set so the top lands FLUSH on the
    // upper deck edge (plain staircase() starts at y0 — wrong for raised decks).
    const stair = (x, baseY, z, rise, dir, width, run, color) => {
        const steps = Math.max(1, Math.round(rise / 0.5));
        const riser = rise / steps;
        const dx = dir === "px" ? 1 : dir === "nx" ? -1 : 0;
        const dz = dir === "pz" ? 1 : dir === "nz" ? -1 : 0;
        for (let i = 1; i <= steps; i++) {
            ship.push({ p: [x + dx * run * i, baseY + riser * i - riser / 2, z + dz * run * i], s: [dx ? run : width, riser, dz ? run : width], color });
        }
    };
    // keel closes the ship's underside so you can't swim up into it
    ship.push({ p: [0, -0.1, 2], s: [16, 0.7, 56], color: PS.hullDark });
    // HULL SIDES with a 3.4 m boarding GAP at z≈0 on each side (ramp climbs in)
    for (const sx of [-8, 8]) {
        ship.push({ p: [sx, 2.6, 15.7], s: [0.7, 5.2, 28.6], color: PS.hull, tag: "shipHull" });
        ship.push({ p: [sx, 2.6, -14.3], s: [0.7, 5.2, 23.4], color: PS.hull, tag: "shipHull" });
        ship.push({ p: [sx, 0.4, 15.7], s: [0.9, 1.2, 28.6], color: PS.hullDark });
        ship.push({ p: [sx, 0.4, -14.3], s: [0.9, 1.2, 23.4], color: PS.hullDark });
    }
    // BOW → prow + gold figurehead; STERN transom
    ship.push({ p: [0, 2.6, 31], s: [13, 5.2, 6], color: PS.hull });
    ship.push({ p: [0, 2.9, 35], s: [8, 4.6, 4], color: PS.hull });
    ship.push({ p: [0, 3.2, 38], s: [3.4, 4, 3], color: PS.hull });
    ship.push({ p: [0, 4.1, 40.5], s: [1.2, 1.2, 3], color: PS.gold });
    ship.push({ p: [0, 3.2, -27], s: [15, 6.4, 3], color: PS.hull, tag: "shipStern" });
    ship.push({ p: [0, 6.6, -27], s: [15, 1.6, 1], color: PS.gold });
    // MAIN DECK with one clean 4×4 cargo HATCH (x -2..2, z -6..-2)
    ship.push({ p: [0, 4.7, 14], s: [16, 0.6, 32], color: PS.deck });
    ship.push({ p: [0, 4.7, -16], s: [16, 0.6, 20], color: PS.deck });
    ship.push({ p: [-5, 4.7, -4], s: [6, 0.6, 4], color: PS.deck });
    ship.push({ p: [5, 4.7, -4], s: [6, 0.6, 4], color: PS.deck });
    // BULWARKS (gaps at ramps z0 + cannon ports: z12 port cannon, z-4 opens
    // the wood in front of the z-2 starboard cannon — see the mount comments)
    const railGaps = new Set([0, 12, -12, -4]);
    for (const sx of [-8, 8])
        for (let z = -24; z <= 28; z += 4) {
            if (railGaps.has(z))
                continue;
            ship.push({ p: [sx, 5.9, z], s: [0.3, 1.4, 3.4], color: PS.rail });
        }
    ship.push({ p: [0, 5.9, 29.5], s: [15, 1.4, 0.3], color: PS.rail });
    // CANNONS
    for (const sx of [-8.4, 8.4])
        for (const z of [-18, -6, 6, 18]) {
            ship.push({ p: [sx * 0.86, 5.4, z], s: [1, 0.8, 1.5], color: PS.cannon });
            ship.push({ p: [sx, 5.5, z], s: [1.4, 0.5, 0.5], color: PS.cannon, tag: sx > 0 ? "shipGunE" : "shipGunW" });
        }
    // FORECASTLE (top y6.9) + stair flush to its edge
    ship.push({ p: [0, 6.9, 26], s: [14, 0.5, 9], color: PS.deck });
    for (const sx of [-6.8, 6.8])
        ship.push({ p: [sx, 7.8, 26], s: [0.3, 1.4, 9], color: PS.rail });
    ship.push({ p: [0, 7.8, 30.2], s: [14, 1.4, 0.3], color: PS.rail });
    stair(0, 5, 18, 1.9, "pz", 4, 0.85, PS.deckDark);
    // QUARTERDECK helm (top y7.4) + stair + ship's wheel
    ship.push({ p: [0, 7.4, -19], s: [15, 0.5, 14], color: PS.deck });
    for (const sx of [-7.2, 7.2])
        ship.push({ p: [sx, 8.3, -19], s: [0.3, 1.4, 14], color: PS.rail });
    ship.push({ p: [0, 8.3, -12.2], s: [15, 1.4, 0.3], color: PS.rail });
    stair(0, 5, -8, 2.4, "nz", 4, 0.85, PS.deckDark);
    ship.push({ p: [0, 8.4, -16], s: [0.4, 1.4, 0.4], color: PS.rail });
    ship.push({ p: [0, 9.4, -15.4], s: [0.25, 1.8, 0.25], color: PS.gold });
    ship.push({ p: [0, 9.4, -15.4], s: [0.25, 0.25, 1.8], color: PS.gold });
    // GUN-DECK HOLD (floor y1.8) + hatch stair + crates
    ship.push({ p: [0, 1.55, 2], s: [15, 0.5, 44], color: PS.deckDark });
    ship.push({ p: [0, 2.6, 24.5], s: [15, 5.2, 0.5], color: PS.hull });
    ship.push({ p: [0, 2.6, -20.5], s: [15, 5.2, 0.5], color: PS.hull });
    stair(0, 1.8, -6, 3.2, "pz", 3, 0.55, PS.deckDark);
    for (const [cx, cz] of [[-4, 12], [4, 8], [-3, -14], [5, 18], [-5, 20]])
        ship.push({ p: [cx, 2.6, cz], s: [2, 1.6, 2], color: PS.crate });
    // BOARDING RAMPS: sea surface (y0) → deck edge (y5) through the hull gaps
    stair(-17, 0, 0, 5, "px", 3, 0.9, PS.deck);
    stair(17, 0, 0, 5, "nx", 3, 0.9, PS.deck);
    // MASTS + YARDS + SAILS
    const mast = (z, height, thick, yardW, sailH) => {
        const top = 5 + height;
        ship.push({ p: [0, 5 + height / 2, z], s: [thick, height, thick], color: PS.mast, tag: "shipSpar" });
        ship.push({ p: [0, 5 + height * 0.42, z], s: [yardW, 0.5, 0.5], color: PS.mast, tag: "shipSpar" });
        ship.push({ p: [0, 5 + height * 0.42 - sailH / 2 - 0.4, z + 0.3], s: [yardW - 1, sailH, 0.2], color: PS.sail, tag: "shipSail" });
        ship.push({ p: [0, 5 + height * 0.74, z], s: [yardW * 0.7, 0.4, 0.4], color: PS.mast, tag: "shipSpar" });
        ship.push({ p: [0, 5 + height * 0.74 - sailH * 0.4 - 0.3, z + 0.3], s: [yardW * 0.7 - 1, sailH * 0.8, 0.2], color: PS.sailDark, tag: "shipSail" });
        ship.push({ p: [0.9, top - 0.6, z], s: [1.6, 1, 0.1], color: PS.gold });
    };
    mast(22, 22, 1.2, 14, 6.5);
    mast(2, 28, 1.5, 17, 8);
    mast(-16, 18, 1.1, 11, 5.5);
    // ZIPLINES: rigging you can actually ride. Anchored just under each mast top
    // (mast tops are 5+height) and running down to the deck, so jumping into a
    // cable from the crow's nest carries you the length of the ship.
    // Offset OFF the x=0 centreline: the masts stand on x=0, and a rider's
    // position is written straight along the cable with no collision pass, so a
    // centreline cable flies you through solid mast and dumps you inside it.
    // ±3.2 clears the thickest mast (1.5 wide) with margin. Low ends sit at
    // y=10.2 — the rider hangs 1.15 below the cable, so an arrival at 7.2 put
    // their body INSIDE the deck slabs, where the collide-and-slide binary
    // search (which assumes the start point is clear) cannot depenetrate:
    // permanently stuck, unrecoverable except by dying. They now arrive in open
    // air above the deck and simply fall the last metre.
    PIRATE_ZIPLINES.push({ a: [3.2, 5 + 28 - 1.5, 2], b: [3.2, 10.2, 28] }, // main mast → bow (starboard)
    { a: [-3.2, 5 + 28 - 2.5, 2], b: [-3.2, 10.2, -20] }, // main mast → stern (port)
    { a: [3.2, 5 + 22 - 1.2, 22], b: [3.2, 10.2, -8] }, // fore mast → midships
    { a: [-3.2, 5 + 18 - 1.0, -16], b: [-3.2, 10.2, 12] });
    // CROW'S NEST (decorative)
    ship.push({ p: [0, 5 + 28 * 0.74 + 0.6, 2], s: [3.6, 0.4, 3.6], color: PS.deckDark, tag: "shipTop" });
    for (const [dx, dz, sw, sd] of [[0, 1.8, 3.6, 0.3], [0, -1.8, 3.6, 0.3], [1.8, 0, 0.3, 3.6], [-1.8, 0, 0.3, 3.6]])
        ship.push({ p: [dx, 5 + 28 * 0.74 + 1.4, 2 + dz], s: [sw, 1, sd], color: PS.rail });
    // on-ship spawn template — always on a DECK (never in the water), offset per
    // hull below so you spawn on a (possibly different) boat each round
    const shipSpawns = [
        // (4, -2) moved to (-4, -2): the starboard cannon now parks there and the
        // spawn-safety filter would have silently dropped the spawn every match
        { p: [0, 5.2, 8], yaw: Math.PI }, { p: [-4, 5.2, -2], yaw: 0 }, { p: [0, 5.2, -22], yaw: 0 },
        { p: [0, 7.7, 26], yaw: Math.PI }, { p: [0, 8.2, -20], yaw: 0 }, { p: [-4, 2.1, 10], yaw: Math.PI },
        { p: [3, 5.2, 16], yaw: Math.PI }, { p: [-3, 5.2, -12], yaw: 0 },
    ];
    // --- THE FLEET: three galleons spread across the open sea.
    const fleet = [
        { ox: 0, oz: 0, flag: true, hold: "FLAGSHIP" },
        { ox: 96, oz: 26, flag: false, hold: "EAST GALLEON" },
        { ox: -92, oz: -30, flag: false, hold: "WEST GALLEON" },
    ];
    const spawns = [];
    const pickups = [];
    const vehicles = [];
    const jumpPads = [];
    const holds = [];
    for (const f of fleet) {
        // PLUNDER hold volume: the enclosed gun deck. Floor slab tops out at y1.8
        // (`[0,1.55,2] s[15,0.5,44]`) and the fore/aft bulkheads at z±22.5 rise to
        // y5.2, so the box below is the interior those walls enclose, inset off the
        // hull sides so a carrier swimming alongside never counts as delivered.
        // y spans 1.4–4.3, and the CEILING is the load-bearing number. The hold
        // floor tops out at 1.8 and the MAIN DECK slabs are at y4.7 thickness 0.6,
        // so players walk the deck at y5.0 and the hold's real interior ends at the
        // deck underside, 4.4. A box that reached the deck would score a delivery
        // for someone standing on top of the ship, never entering the hold at all.
        // Below: 1.4 gives slack under the floor (solid hull, unreachable) so a
        // player standing at exactly 1.8 is not decided by a float comparison.
        // ENTRY = the deck HATCH. The main-deck slabs (`[0,4.7,14] s[16,·,32]`,
        // `[0,4.7,-16] s[16,·,20]`, and the two `[±5,4.7,-4] s[6,·,4]` fillets)
        // leave a 4x4 hole at local x-2..2, z-6..-2 — that gap IS the hatch, and
        // the hold stair climbs to it. Stand there and you drop into the hold.
        // RAMPS = the foot of each boarding ramp (`stair(±17,0,0,rise 5,...)`),
        // which climbs through the 3.4 m gap in the hull sides at z~0.
        holds.push({
            p: [f.ox, 2.85, 2 + f.oz], s: [13, 2.9, 42], name: f.hold,
            entry: [f.ox, 5.2, -4 + f.oz],
            ramps: [[-17 + f.ox, 0.5, f.oz], [17 + f.ox, 0.5, f.oz]],
        });
        for (const b of ship)
            B.push({ p: [b.p[0] + f.ox, b.p[1], b.p[2] + f.oz], s: b.s, color: b.color, ...(b.tag ? { tag: b.tag } : {}) });
        for (const sp of shipSpawns)
            spawns.push({ p: [sp.p[0] + f.ox, sp.p[1], sp.p[2] + f.oz], yaw: sp.yaw });
        // GRAV-LIFT BEAMS on each deck: the VIOLET mega-lift — a huge vertical launch
        // toward the sky + the flying ghost ship — and the ORANGE lift up into the
        // rigging / crow's nest. fling:0 = PURE vertical so you rise straight up the
        // mast line instead of being flung overboard.
        jumpPads.push({ p: [-5 + f.ox, 5.1, 6 + f.oz], power: 42, color: "#b26bff", fling: 0 });
        jumpPads.push({ p: [5 + f.ox, 5.1, -8 + f.oz], power: 26, color: "#ff8a2a", fling: 0 });
        // a SUBMARINE off each beam + a HELICOPTER parked amidships on each deck
        vehicles.push({ p: [-26 + f.ox, 0.4, 14 + f.oz], yaw: Math.PI * 0.5, kind: "submarine" });
        vehicles.push({ p: [26 + f.ox, 0.4, -14 + f.oz], yaw: Math.PI * 1.5, kind: "submarine" });
        // deck level (~y5.25): a vehicle's origin IS its contact point, and seating
        // only ever raises — authoring it high left the heli hovering over the deck.
        vehicles.push({ p: [3 + f.ox, 5.3, 12 + f.oz], yaw: Math.PI, kind: "heli" });
        // MANNABLE CANNONS — port + starboard. Mount (Use), aim, FIRE arcing balls
        // at the other ships / swimmers. Barrels face outboard (±X). Each sits at
        // a bulwark rail GAP so it fires through an actual opening — the old
        // z18/z4 mounts faced solid rail + decorative cannon boxes, and any
        // down-angled or off-axis shot detonated 1-3 m out and splashed the
        // gunner (bench-cannon.mjs asserts the clear firing cone from map data).
        // Port: the authored z12 gap. Starboard can't mirror it: z-12 is under
        // the quarterdeck overhang lip (the sanitizer shoved it to z-9, straight
        // into rail), z12 collides with the parked heli, and z±6/±18 are the
        // decor-cannon rows — so it mounts at z-2 and railGaps opens z-4, which
        // together with the z0 ramp gap makes one continuous gunport.
        // x±5 — INBOARD of the bulwark rail (x±8). The map's vehicle sanitizer
        // treats a vehicle whose clearance box (x±1.7) touches the rail as "stuck"
        // and spirals it outward to find air — which threw the old x±6.5 cannons
        // clean off the deck (x11.5) where they sank to the seabed.
        vehicles.push({ p: [-5 + f.ox, 5.3, 12 + f.oz], yaw: Math.PI / 2, kind: "cannon" }); // port, fires −X
        vehicles.push({ p: [5 + f.ox, 5.3, -2 + f.oz], yaw: -Math.PI / 2, kind: "cannon" }); // starboard, fires +X
        // pickups on every deck — EVERY boat carries a JETPACK (reach the ghost
        // ship + fly between hulls); special chests weighted to the flagship
        pickups.push({ p: [3 + f.ox, 5.4, 2 + f.oz], kind: "jetpack" }, { p: [0 + f.ox, 7.7, 26 + f.oz], kind: f.flag ? "rocket" : "grenade" }, { p: [0 + f.ox, 2.1, 2 + f.oz], kind: f.flag ? "imperion" : "armor" }, { p: [4 + f.ox, 5.4, 14 + f.oz], kind: "health" }, { p: [0 + f.ox, 8.2, -20 + f.oz], kind: "armor" }, { p: [-4 + f.ox, 2.1, -12 + f.oz], kind: "speed" }, { p: [4 + f.ox, 5.4, -4 + f.oz], kind: "dmg" }, { p: [0 + f.ox, 5.4, -22 + f.oz], kind: "cloak" });
    }
    // NB: no water/seabed player spawns — you always start on a boat deck.
    // --- GLOBAL SEA: one deep swimmable volume + a solid seabed under the fleet.
    B.push({ p: [0, -4.5, 0], s: [340, 9, 300], color: PS.sea, tag: "water" });
    B.push({ p: [0, -9.7, 0], s: [340, 1.4, 300], color: PS.seabed });
    // --- SUNKEN VILLAGE: a cluster of little stone huts on the seabed in one
    // corner — dive down and swim in through the doorway. Each hut is hollow
    // with a 1.4 m door gap on the −Z face.
    const hut = (cx, cz, col) => {
        const y0 = -9, H = 3.2, W = 4.2, D = 3.6, T = 0.3, top = y0 + H;
        B.push({ p: [cx, y0 + H / 2, cz + D / 2], s: [W, H, T], color: col }); // back wall
        B.push({ p: [cx - W / 2, y0 + H / 2, cz], s: [T, H, D], color: col }); // left
        B.push({ p: [cx + W / 2, y0 + H / 2, cz], s: [T, H, D], color: col }); // right
        const seg = (W - 1.4) / 2; // front wall w/ door gap
        B.push({ p: [cx - (0.7 + seg / 2), y0 + H / 2, cz - D / 2], s: [seg, H, T], color: col });
        B.push({ p: [cx + (0.7 + seg / 2), y0 + H / 2, cz - D / 2], s: [seg, H, T], color: col });
        B.push({ p: [cx, top - 0.5, cz - D / 2], s: [1.4, 1, T], color: col }); // door lintel
        B.push({ p: [cx, top + 0.15, cz], s: [W + 0.4, 0.3, D + 0.4], color: col }); // roof
    };
    // A full sunken village: a cluster of hollow huts + a ruined watchtower +
    // treasure crates, and a loot pickup on the seabed to reward the dive. Fixed
    // (deterministic) layout — the map is built identically on client + server,
    // so NO randomness here or the colliders would desync.
    /** A drowned TEMPLE: colonnade around a raised altar, open on all sides. */
    const temple = (vx, vz, col) => {
        B.push({ p: [vx, -9.1, vz], s: [17, 0.6, 13], color: col }); // stylobate
        for (const dx of [-7, -3.5, 0, 3.5, 7])
            for (const dz of [-5, 5])
                B.push({ p: [vx + dx, -6.9, vz + dz], s: [1, 4, 1], color: col }); // columns
        B.push({ p: [vx, -4.7, vz], s: [18, 0.7, 14], color: col }); // architrave
        B.push({ p: [vx, -8.2, vz], s: [3.4, 1.2, 3.4], color: "#6a5a3c" }); // altar
    };
    /** A TOWER TOWN: slender ruined spires of differing heights + a linking arch. */
    const towers = (vx, vz, col) => {
        const hs = [[0, 0, 9], [8, 5, 6.5], [-7, 6, 7.5], [4, -8, 5], [-9, -6, 8]];
        for (const [dx, dz, h] of hs) {
            B.push({ p: [vx + dx, -9.4 + h / 2, vz + dz], s: [3, h, 3], color: col });
            B.push({ p: [vx + dx, -9.4 + h + 0.2, vz + dz], s: [3.8, 0.4, 3.8], color: "#6a5a3c" }); // cap
        }
        B.push({ p: [vx + 0.5, -4.4, vz + 3], s: [9, 0.6, 1.2], color: col }); // walkway between spires
    };
    /** A WRECK YARD: broken hulls half-buried in the sand, ribs sticking up. */
    const wreck = (vx, vz, col) => {
        B.push({ p: [vx, -8.6, vz], s: [16, 2.2, 5], color: col }); // main hull
        B.push({ p: [vx + 2, -6.6, vz], s: [7, 2, 4.4], color: col }); // upper deck section
        for (const dx of [-6, -3, 0, 3, 6])
            B.push({ p: [vx + dx, -6.4, vz + 2.8], s: [0.4, 3.4, 0.4], color: "#6a5a3c" }); // ribs
        B.push({ p: [vx - 11, -8.8, vz + 4], s: [6, 1.6, 4], color: col }); // torn-off bow
        B.push({ p: [vx + 10, -7.4, vz - 3], s: [0.5, 5, 0.5], color: "#4a3320" }); // snapped mast
    };
    const village = (vx, vz, cols, loot, kind = "huts") => {
        if (kind === "huts") {
            const spots = [[0, 0], [13, 4], [6, 15], [-8, 10], [18, 17], [-12, -3], [9, -7]];
            spots.forEach(([dx, dz], i) => hut(vx + dx, vz + dz, cols[i % 2]));
            B.push({ p: [vx + 6, -6, vz + 6], s: [3, 6, 3], color: "#6a5a3c" }); // ruined watchtower
        }
        else if (kind === "temple") {
            temple(vx, vz, cols[0]);
            for (const [dx, dz] of [[14, 8], [-13, -6]])
                hut(vx + dx, vz + dz, cols[1]);
        }
        else if (kind === "towers") {
            towers(vx, vz, cols[0]);
            for (const [dx, dz] of [[13, 9], [-12, 8]])
                hut(vx + dx, vz + dz, cols[1]);
        }
        else {
            wreck(vx, vz, cols[0]);
            for (const [dx, dz] of [[9, 10], [-9, -9]])
                hut(vx + dx, vz + dz, cols[1]);
        }
        for (const [dx, dz] of [[2, 2], [10, 9], [-3, 14], [15, 2]])
            B.push({ p: [vx + dx, -8.2, vz + dz], s: [1.6, 1.4, 1.6], color: PS.crate });
        pickups.push({ p: [vx + 6, -8.2, vz + 6], kind: loot }); // seabed treasure — dive for it
    };
    // villages spread across the seabed, clear of the three hulls (0,0 / 96,26 / -92,-30)
    village(-122, -102, ["#8f7f5f", "#7d6c4c"], "armor"); // SW corner
    village(122, 104, ["#7d6c4c", "#8f7f5f"], "dmg", "temple"); // NE corner
    village(128, -96, ["#8a7a58", "#6f5f45"], "health", "towers"); // SE corner
    village(-124, 108, ["#7d6c4c", "#8f7f5f"], "armor", "wreck"); // NW corner
    village(6, 120, ["#8f7f5f", "#7d6c4c"], "health"); // N, deep water off the sterns
    village(-6, -122, ["#8a7a58", "#6f5f45"], "speed", "temple"); // S
    // (2026-11-04) the far CORNERS carry health/armor and the mid ring the
    // rocket + rifle: one-gun strips rocket/imperion chests and purge strips
    // rockets, which used to leave two whole corners of the seabed empty.
    // second ring — mid-field, so the seabed is worth exploring between hulls
    village(-70, 40, ["#8f7f5f", "#7d6c4c"], "rocket", "towers"); // W mid
    village(74, -44, ["#7d6c4c", "#8f7f5f"], "imperion", "wreck"); // E mid
    village(48, 62, ["#8a7a58", "#6f5f45"], "dmg", "huts"); // NE mid
    village(-52, -60, ["#8f7f5f", "#7d6c4c"], "rocket", "temple"); // SW mid
    village(-140, 24, ["#7d6c4c", "#8f7f5f"], "speed", "wreck"); // W deep
    village(140, -34, ["#8a7a58", "#6f5f45"], "health", "towers"); // E deep
    return {
        id: "pirate_ship",
        name: "Pirate Ship",
        modes: ["gungame", "ffa", "relay", "onegun", "treasure", "purge", "horde", "burnout", "plunder"],
        theme: "day",
        killZ: -13,
        bounds: { min: [-166, -14, -146], max: [166, 40, 154] },
        boxes: B,
        // The original tide (constants unchanged since it shipped): the sea swells
        // to flood the MAIN deck (~y5) for a scramble; forecastle/quarterdeck stay
        // dry as high ground.
        tide: { periodS: 70, amp: 5.9, highAt: 3.8, label: "🌊 HIGH TIDE — the decks are flooding!" }, // lightning removed 2026-07-23 (owner: "laser out of nowhere")
        spawns,
        pickups,
        vehicles,
        jumpPads,
        holds,
        // THE GHOST SHIP — a solid, landable flying VIOLET GALLEON on a slow sky
        // circuit around the fleet. Elongated ship-shaped deck (a fore-aft strip of
        // overlapping pads, NOT a round UFO disc): padHalf 4.5 → ~9 m beam, fuselage
        // pads span ~33 m of length. Same thick collider slab as Skyhaven's plane
        // (no fast-fall pass-through). Reachable by the deck helis (peaks ~50; heli
        // ceiling = bounds.max.y+85 = 125). galleon:true → the original ghost-ship skin.
        plane: { cx: 0, cz: 0, radius: 92, baseY: 43, climbY: 7, omega: 0.055, padHalf: 4.5, fuselage: [-12, -8, -4, 0, 4, 8, 12], wing: 0, galleon: true },
    };
}
const MAP_PIRATE_SHIP = pirateShip();
MAP_PIRATE_SHIP.ziplines = PIRATE_ZIPLINES;
// curated fleet pickups/vehicles — restored after enrichMaps (which would
// otherwise scatter more pickups out over the open sea)
const PIRATE_PICKUPS = MAP_PIRATE_SHIP.pickups.slice();
const PIRATE_VEHICLES = MAP_PIRATE_SHIP.vehicles.slice();
const PIRATE_JUMPPADS = MAP_PIRATE_SHIP.jumpPads.slice(); // the curated deck grav-lifts
/** MEGA CHIBRE — a floating anatomical sculpture arena (owner request, "full
 *  send"). A shaft platform runs −z→+z from a bulbous glans (tip, with a corona
 *  ridge for cover) down to twin base mounds (high ground) flanking a scrotum
 *  slab. Glowing "vein" ridges double as low cover; fleshy pink palette on the
 *  dark neon theme. Fall off the sculpture edge → killZ. Boxes: p = center,
 *  s = FULL size. */
function megaChibre() {
    const MC = {
        shaft: "#d98a8a", // shaft flesh
        glans: "#c76a86", // head — redder/purple
        corona: "#b05275", // the rim ridge
        coronaGlow: "#ff9ec4",
        meatus: "#7a2530", // dark tip notch
        sac: "#c07f86", // scrotum slab
        ball: "#cf8890", // the two mounds
        vein: "#a86a72", // vein ridges
        veinGlow: "#6f8fd6", // classic blue veins
        bump: "#c07680", // chest-high cover
    };
    // BIGGER + FULLY GROUNDED (2026-07-30): one generous set of overlapping floor
    // slabs (top y=0) covers the WHOLE silhouette footprint with no gaps, so every
    // raised element sits ON floor and every spawn sits on the real surface — no
    // more mid-air spawns or floating geometry.
    const boxes = [
        // ── FLOOR (top surface y=0) — shaft → glans → tip, plus the base slab ──
        { p: [0, -0.5, -4], s: [24, 1, 58], color: MC.shaft }, // shaft   x−12..12, z−33..25
        { p: [0, -0.5, -40], s: [30, 1, 20], color: MC.glans }, // glans   x−15..15, z−50..−30
        { p: [0, -0.5, -51], s: [18, 1, 8], color: MC.glans }, // tip cap        z−55..−47
        { p: [0, -0.5, 36], s: [48, 1, 30], color: MC.sac }, // scrotum x−24..24, z21..51
        // knee-high side rails on the shaft (edge guard), fully over the floor
        { p: [-11, 0.3, -4], s: [1.6, 1.4, 58], color: MC.shaft, glow: MC.veinGlow },
        { p: [11, 0.3, -4], s: [1.6, 1.4, 58], color: MC.shaft, glow: MC.veinGlow },
        // corona ridge (chest-high cover where head meets shaft)
        { p: [0, 1.0, -30], s: [30, 2.4, 3.4], color: MC.corona, glow: MC.coronaGlow },
        { p: [0, 0.15, -53], s: [4, 1.6, 3], color: MC.meatus }, // meatus notch at the tip
        // ── VEINS along the shaft (low glowing cover) ────────────────────────
        { p: [-6, 0.3, -10], s: [2, 1, 40], color: MC.vein, glow: MC.veinGlow },
        { p: [7, 0.3, 0], s: [2, 1, 34], color: MC.vein, glow: MC.veinGlow },
        { p: [0, 0.3, -20], s: [14, 1, 2], color: MC.vein, glow: MC.veinGlow }, // branch
        { p: [3, 0.3, 14], s: [2, 1, 18], color: MC.vein, glow: MC.veinGlow },
        // chest-high veiny bumps for real cover
        { p: [-8, 0.9, 8], s: [3, 2.6, 4], color: MC.bump, glow: MC.veinGlow },
        { p: [8, 0.9, -12], s: [3, 2.6, 4], color: MC.bump, glow: MC.veinGlow },
        { p: [-4, 0.9, -24], s: [3, 2.6, 4], color: MC.bump, glow: MC.veinGlow },
        // ── BASE: twin climbable mounds (high ground), fully within the scrotum slab
        { p: [-13, 0.5, 36], s: [18, 2, 18], color: MC.ball }, // top y=1.5
        { p: [-13, 2, 36], s: [12, 2, 12], color: MC.ball }, // top y=3
        { p: [-13, 3.5, 36], s: [6, 2, 6], color: MC.ball, glow: MC.coronaGlow }, // top y=4.5
        { p: [13, 0.5, 36], s: [18, 2, 18], color: MC.ball },
        { p: [13, 2, 36], s: [12, 2, 12], color: MC.ball },
        { p: [13, 3.5, 36], s: [6, 2, 6], color: MC.ball, glow: MC.coronaGlow },
    ];
    const spawns = [
        { p: [0, 0.15, -46], yaw: Math.PI }, // on the glans, facing down the shaft
        { p: [-9, 0.15, -38], yaw: Math.PI },
        { p: [9, 0.15, -38], yaw: Math.PI },
        { p: [-8, 0.15, -14], yaw: Math.PI * 1.5 }, // shaft, face +x (toward center)
        { p: [8, 0.15, -14], yaw: Math.PI * 0.5 }, // face −x
        { p: [-8, 0.15, 12], yaw: Math.PI * 1.5 },
        { p: [8, 0.15, 12], yaw: Math.PI * 0.5 },
        { p: [0, 0.15, 0], yaw: 0 }, // mid, facing the tip
        { p: [0, 0.15, 30], yaw: 0 }, // base, in the gap between mounds
        { p: [-3, 0.15, 46], yaw: 0 },
        { p: [-13, 4.6, 36], yaw: 0 }, // atop the left mound
        { p: [13, 4.6, 36], yaw: 0 }, // atop the right mound
    ];
    return {
        id: "mega_chibre",
        name: "Mega Chibre",
        modes: ["gungame", "ffa", "relay", "onegun", "treasure", "purge", "horde", "burnout", "plunder"],
        theme: "day",
        killZ: -12,
        bounds: { min: [-42, -16, -60], max: [42, 30, 56] },
        boxes,
        spawns,
        // Hand-placed pickups (enrichMaps is skipped for this map — see the skip
        // list — so nothing auto-scatters off the platform edge). All on the floor.
        pickups: [
            { p: [0, 0.4, 10], kind: "beigechest" }, // the PENIS GUN, dead-center
            { p: [-9, 0.4, 4], kind: "health" },
            { p: [9, 0.4, -4], kind: "armor" },
            { p: [0, 0.4, -40], kind: "health" }, // on the glans
            { p: [0, 0.4, 40], kind: "grenade" }, // base, between the mounds
            { p: [-8, 0.4, -20], kind: "armor" },
            // IMPERION beam rifle — every enriched map gets exactly one (see
            // enrichMaps), but mega_chibre is hand-authored (enrichMaps skipped), so
            // it must place its own. This DOUBLES AS THE RELAY OBJECTIVE: mapForMode's
            // relay branch keeps the map's imperion chest as the baton, so without one
            // relay was unwinnable here (treasure.carrier never set) and silently
            // degraded to deathmatch. Central shaft floor, contested (6 m off the
            // (0,0) spawn point — it used to sit ON it).
            { p: [0, 0.4, -6], kind: "imperion" },
        ],
        // Jump beams (enrichMaps is skipped, so these are hand-placed): two in front
        // of the mounds to reach the high ground, plus a few along the shaft/glans so
        // nobody gets stuck in a low spot on the bigger map.
        jumpPads: [
            { p: [-13, 0.15, 23], power: 24, color: "#ff9ec4" }, // up onto the left mound
            { p: [13, 0.15, 23], power: 24, color: "#ff9ec4" }, // up onto the right mound
            { p: [-8, 0.15, -2], power: 20 },
            { p: [9.5, 0.15, -16.5], power: 20 }, // (was touching the bump's face)
            { p: [0, 0.15, -42], power: 20 }, // near the tip
        ],
    };
}
export const MAP_MEGA_CHIBRE = megaChibre();
export const MAPS = {
    [MAP_MEGA_CHIBRE.id]: MAP_MEGA_CHIBRE, // Mega Chibre (floating flesh sculpture) — all modes
    [MAP_GRID_ARENA.id]: MAP_GRID_ARENA, // Arena (day)
    [MAP_COMPOUND.id]: MAP_COMPOUND, // Compound (day, warehouses)
    [MAP_ROOFTOPS.id]: MAP_ROOFTOPS, // Rooftops (day, vertical)
    [MAP_ROOFTOPS_XL.id]: MAP_ROOFTOPS_XL, // Rooftops XL (4x area, vehicles)
    [MAP_ROOFTOP_SPIDERWEB.id]: MAP_ROOFTOP_SPIDERWEB, // Skyhaven-height violet Rooftops XL
    [MAP_ARENA_XL.id]: MAP_ARENA_XL, // Arena XL (4x area, open cover fights)
    [MAP_VILLAGE.id]: MAP_VILLAGE, // Village (day, CQC houses)
    [MAP_CANYON.id]: MAP_CANYON, // Canyon (day, outdoor cover)
    [MAP_SANDLOT.id]: MAP_SANDLOT, // Practice Field (day)
    [MAP_NEO_ISLAND.id]: MAP_NEO_ISLAND, // Skyhaven battle-royale island
    [MAP_MEDIEVAL_CASTLE.id]: MAP_MEDIEVAL_CASTLE, // Medieval castle with horses and drawbridge
    [MAP_WILD_WEST_TRAIN.id]: MAP_WILD_WEST_TRAIN, // Wild West train pursuit with horses
    [MAP_NEON_DISTRICT.id]: MAP_NEON_DISTRICT, // Neon District (the one neon map)
    [MAP_CHIBRE_ARENA.id]: MAP_CHIBRE_ARENA, // Chibre Arena (imported BLOCK CITY, deathmatch)
    [MAP_PLUMBING_PLAZA.id]: MAP_PLUMBING_PLAZA, // Plumbing Plaza (2nd Chibre arena)
    [MAP_PIRATE_SHIP.id]: MAP_PIRATE_SHIP, // Pirate Ship (galleon on the open sea)
};
enrichMaps();
// DESCENT (reverse Gun Game) plays on every Gun Game map with Gun Game's rules
// — only the ladder ORDER differs (see resolveLadder). Track gungame's map
// support automatically so a new gungame map is a descent map for free and the
// two lists can never drift. mapForMode falls DESCENT through to `return map`
// (same as gungame/burnout — no chest), so no arm there is needed.
for (const m of Object.values(MAPS)) {
    if (m.modes.includes("gungame") && !m.modes.includes("descent"))
        m.modes.push("descent");
}
// Pirate Ship: enrichMaps scatters extra pickups/pads out over the open sea
// (the hulls are narrow). Restore the curated fleet set that sits on the decks.
{
    const ship = MAP_PIRATE_SHIP;
    ship.jumpPads = PIRATE_JUMPPADS; // curated deck grav-lifts (drop enrichMaps' sea-scattered pads)
    ship.pickups = PIRATE_PICKUPS;
    ship.vehicles = PIRATE_VEHICLES;
    ship.ziplines = PIRATE_ZIPLINES; // enrichMaps doesn't touch these, but keep them explicit
}
// Keep the Practice Field mirror spot (0,-18) clear of jump beams.
MAP_SANDLOT.jumpPads = (MAP_SANDLOT.jumpPads ?? []).filter((p) => Math.hypot(p.p[0] - 0, p.p[2] - -18) > 8);
// Now (after enrichMaps placed vehicles/pickups/pads in the REAL field) append
// the neon-dimension copy far to the north + expand the bounds to fit it.
{
    const neon = MAP_SANDLOT.boxes.map((b) => ({
        p: [b.p[0], b.p[1], b.p[2] + PRACTICE_DIM_OFFSET],
        s: b.s,
        color: b.s[1] <= 1.2 ? "#5a4a8a" : "#7a5ab0", // bright neon floor / walls
        glow: "#ff7ae8",
    }));
    MAP_SANDLOT.boxes.push(...neon);
    MAP_SANDLOT.bounds.max[2] = 22 + PRACTICE_DIM_OFFSET + 6;
    // a jetpack chest on the NEON side too (mirror of the real-field one)
    // grounded a few metres from the NEON mirror (mirror sits at z ≈ 182)
    MAP_SANDLOT.pickups.push({ p: [6, 0.3, -12 + PRACTICE_DIM_OFFSET], kind: "jetpack" });
    // a drivable MINI-UFO in the neon dimension (real matches — same as the one
    // parked there in the solo practice range)
    MAP_SANDLOT.vehicles.push({ p: [10, 0.8, -12 + PRACTICE_DIM_OFFSET], yaw: 0, kind: "miniufo" });
}
// FLYING ISLANDS — a chain across the empty gap between the day field
// (z ≤ 22) and the neon dimension (z ≥ 178): rises toward the middle, then
// descends toward the neon side. Appended AFTER the neon copy so they exist
// once, in the gap, not mirrored. Crossing needs a jetpack/heli; falling in
// the gap is death (killZ). A jetpack chest on every island + a heli on two.
MAP_SANDLOT.boxes.push({ p: [-9, 7, 45], s: [7, 0.7, 7], color: D.crate, glow: D.accent, tag: "practice_mover" }, { p: [-9, 6.3, 45], s: [5.5, 0.6, 5.5], color: D.crateDark, tag: "practice_mover" }, // underside
{ p: [9, 12, 85], s: [7, 0.7, 7], color: D.crate, glow: D.accent, tag: "practice_mover" }, { p: [9, 11.3, 85], s: [5.5, 0.6, 5.5], color: D.crateDark, tag: "practice_mover" }, { p: [-1, 15, 110], s: [6, 0.7, 6], color: D.crate, glow: D.accent, tag: "practice_mover" }, { p: [-1, 14.3, 110], s: [4.5, 0.6, 4.5], color: D.crateDark, tag: "practice_mover" }, { p: [7, 9, 155], s: [7, 0.7, 7], color: D.crate, glow: D.accent, tag: "practice_mover" }, { p: [7, 8.3, 155], s: [5.5, 0.6, 5.5], color: D.crateDark, tag: "practice_mover" }, 
// the BIG island — off the chain's flank, holds the rocket-launcher chest
{ p: [-14, 11, 120], s: [11, 0.9, 11], color: D.crate, glow: D.accent, tag: "practice_mover" }, { p: [-14, 10.2, 120], s: [9, 0.8, 9], color: D.crateDark, tag: "practice_mover" }, 
// OUTER RING — five more islands drifting further off the chain,
// a couple of them properly big
{ p: [-28, 9, 62], s: [8, 0.7, 8], color: D.crate, glow: D.accent, tag: "practice_mover" }, { p: [-28, 8.3, 62], s: [6.5, 0.6, 6.5], color: D.crateDark, tag: "practice_mover" }, { p: [18, 18, 70], s: [6, 0.7, 6], color: D.crate, glow: D.accent, tag: "practice_mover" }, { p: [18, 17.3, 70], s: [4.5, 0.6, 4.5], color: D.crateDark, tag: "practice_mover" }, { p: [-31, 16, 100], s: [14, 0.9, 14], color: D.crate, glow: D.accent, tag: "practice_mover" }, // BIG
{ p: [-31, 15.2, 100], s: [11.5, 0.8, 11.5], color: D.crateDark, tag: "practice_mover" }, { p: [27, 14, 95], s: [9, 0.8, 9], color: D.crate, glow: D.accent, tag: "practice_mover" }, { p: [27, 13.25, 95], s: [7, 0.7, 7], color: D.crateDark, tag: "practice_mover" }, { p: [31, 10, 135], s: [13, 0.9, 13], color: D.crate, glow: D.accent, tag: "practice_mover" }, // BIG
{ p: [31, 9.2, 135], s: [10.5, 0.8, 10.5], color: D.crateDark, tag: "practice_mover" });
MAP_SANDLOT.pickups.push({ p: [-11.5, 7.6, 45], kind: "jetpack" }, { p: [11.5, 12.6, 85], kind: "jetpack" }, { p: [-1, 15.6, 110], kind: "jetpack" }, { p: [7, 9.6, 155], kind: "jetpack" }, { p: [-14, 11.75, 120], kind: "rocket" }, // big island's prize
// every outer-ring island carries a jetpack chest
{ p: [-28, 9.65, 62], kind: "jetpack" }, { p: [18, 18.65, 70], kind: "jetpack" }, { p: [-31, 16.75, 100], kind: "jetpack" }, { p: [27, 14.7, 95], kind: "jetpack" }, { p: [31, 10.75, 135], kind: "jetpack" });
MAP_SANDLOT.vehicles.push({ p: [-8, 7.9, 45], yaw: Math.PI * 0.5, kind: "heli" }, { p: [9, 12.9, 85], yaw: Math.PI * 1.5, kind: "heli" });
// A FLYING SKYSCRAPER — a Skyhaven-style tower perched on its own big
// floating island out in the gap. building() builds from y=0, so every box
// is lifted onto the island deck.
{
    const isX = -33, isZ = 128, deckY = 14; // FAR LEFT, out in the gap
    const mover = { tag: "practice_mover", moveGroup: "practice_sky_tower" };
    MAP_SANDLOT.boxes.push({ p: [isX, deckY, isZ], s: [22, 1.0, 22], color: "#3a4048", glow: "#57d6ff", ...mover }, { p: [isX, deckY - 0.9, isZ], s: [18, 0.9, 18], color: "#22262c", ...mover }, { p: [isX, deckY - 2.2, isZ], s: [12, 1.2, 12], color: "#181b20", ...mover });
    const towerTop = deckY + 0.5;
    const tower = building(isX, isZ, 14, 14, 8, { color: "#39435a", glow: "#57d6ff", roof: "#454f63", hollow: true });
    for (const b of tower) {
        MAP_SANDLOT.boxes.push({
            p: [b.p[0], b.p[1] + towerTop, b.p[2]],
            s: b.s, color: b.color, ...(b.glow ? { glow: b.glow } : {}),
            ...mover,
        });
    }
    const H = 8 * STORY;
    MAP_SANDLOT.boxes.push({ p: [isX, towerTop + H + 1.6, isZ], s: [7, 0.5, 7], color: "#ff2ec4", glow: "#ff2ec4", ...mover }); // neon crown
    // (no grav-lift beam — reach the roof via jetpack/heli; a beam inside a
    // drifting tower read as "the beam is moving", so it was removed)
    MAP_SANDLOT.pickups.push({ p: [isX - 5, deckY + 0.9, isZ], kind: "jetpack" }, { p: [isX, towerTop + H + 0.7, isZ - 4], kind: "rocket" });
}
// the outer-ring islands + flying tower drift past the old bounds — widen to fit
MAP_SANDLOT.bounds.min[0] = Math.min(MAP_SANDLOT.bounds.min[0], -70);
MAP_SANDLOT.bounds.max[0] = Math.max(MAP_SANDLOT.bounds.max[0], 56);
MAP_SANDLOT.bounds.max[1] = Math.max(MAP_SANDLOT.bounds.max[1], 60);
// no jump beam may block a tower DOORWAY (doors are on the -Z face center)
{
    const doorSpots = [
        [-44, -34 - 7.5], [-14, -50 - 6.5], [22, -34 - 8.5],
        [48, 6 - 6.5], [8, 16 - 7.5], [-40, 22 - 6.5],
    ];
    const towerFoots = [
        [-44, -34, 15, 15], [-14, -50, 13, 13], [22, -34, 17, 17],
        [48, 6, 13, 13], [8, 16, 15, 15], [-40, 22, 13, 13],
    ];
    const insideTower = (x, z) => towerFoots.some(([cx, cz, w, d]) => Math.abs(x - cx) < w / 2 && Math.abs(z - cz) < d / 2);
    MAP_NEO_ISLAND.jumpPads = (MAP_NEO_ISLAND.jumpPads ?? []).filter((jp) => jp.p[1] < 0 || // undercity pads
        insideTower(jp.p[0], jp.p[2]) || // grav-lift shafts
        doorSpots.every(([dx, dz]) => Math.hypot(jp.p[0] - dx, jp.p[2] - dz) > 6));
}
// Skyhaven exotics live at the EDGES of the island: anchored to the two
// player spawns farthest from the map centre (guaranteed standable ground,
// off the train tracks, away from the mid-map fight).
{
    const vs = MAP_NEO_ISLAND.vehicles ?? (MAP_NEO_ISLAND.vehicles = []);
    const b = MAP_NEO_ISLAND.bounds;
    const cx = (b.min[0] + b.max[0]) / 2, cz = (b.min[2] + b.max[2]) / 2;
    const far = [...MAP_NEO_ISLAND.spawns]
        .sort((s1, s2) => Math.hypot(s2.p[0] - cx, s2.p[2] - cz) - Math.hypot(s1.p[0] - cx, s1.p[2] - cz));
    const a2 = far[0] ?? { p: [0, 1, 0], yaw: 0 };
    // truck goes to the edge OPPOSITE the plane: the spawn farthest from it
    const a1 = [...MAP_NEO_ISLAND.spawns]
        .sort((s1, s2) => Math.hypot(s2.p[0] - a2.p[0], s2.p[2] - a2.p[2]) - Math.hypot(s1.p[0] - a2.p[0], s1.p[2] - a2.p[2]))[0] ?? a2;
    vs.push({ p: [a1.p[0] + 3, a1.p[1] + 1.0, a1.p[2] + 3], yaw: a1.yaw, kind: "truck" }, { p: [a2.p[0] + 3, a2.p[1] + 0.6, a2.p[2] + 3], yaw: a2.yaw, kind: "miniplane" });
}
// ONE time machine per cinematic map, hand-placed on a landmark so it reads as
// a set piece: the medieval keep roof, beside Skyhaven's giant boombox, and a
// wild-west saloon rooftop. The server consumes it once per match.
{
    const DELOREAN_SPAWN = {
        medieval_castle: { p: [-1, 13.0, -3], yaw: 0 }, // central keep roof (top ≈ 12.3)
        neo_island: { p: [19, 1.0, -94], yaw: 0 }, // just east of the boombox (clears its x±15 body)
        wild_west_train_pursuit: { p: [88, 7.0, -70], yaw: Math.PI }, // saloon rooftop (top ≈ 6.3)
    };
    for (const m of [MAP_NEO_ISLAND, MAP_MEDIEVAL_CASTLE, MAP_WILD_WEST_TRAIN]) {
        const vs = m.vehicles ?? (m.vehicles = []);
        if (vs.some((v) => v.kind === "delorean"))
            continue;
        const d = DELOREAN_SPAWN[m.id];
        if (d)
            vs.push({ p: d.p, yaw: d.yaw, kind: "delorean" });
    }
}
const placeSolidCache = new WeakMap();
function placeSolids(m) {
    const hit = placeSolidCache.get(m);
    if (hit && hit.n === m.boxes.length)
        return hit.list;
    const list = [];
    const add = (p, s) => {
        list.push({ x0: p[0] - s[0] / 2, x1: p[0] + s[0] / 2, y0: p[1] - s[1] / 2, y1: p[1] + s[1] / 2, z0: p[2] - s[2] / 2, z1: p[2] + s[2] / 2 });
    };
    for (const b of m.boxes)
        if (b.tag !== "water")
            add(b.p, b.s);
    for (const d of m.drawbridges ?? [])
        if (d.initiallyLowered !== false)
            add(d.p, d.s);
    placeSolidCache.set(m, { n: m.boxes.length, list });
    return list;
}
/** Any solid overlapping the AABB? */
function solidOverlap(m, minX, minY, minZ, maxX, maxY, maxZ) {
    for (const b of placeSolids(m)) {
        if (minX < b.x1 && maxX > b.x0 && minY < b.y1 && maxY > b.y0 && minZ < b.z1 && maxZ > b.z0)
            return true;
    }
    return false;
}
/** Solid tops in [lo, hi] under (x, z), nearest `prefer` first. `hw` grows the
 *  footprint (+) or demands the point sit that far INSIDE the top (−). */
function floorTops(m, x, z, hw, lo, hi, prefer) {
    const out = [];
    for (const b of placeSolids(m)) {
        if (b.y1 < lo || b.y1 > hi)
            continue;
        if (x > b.x0 - hw && x < b.x1 + hw && z > b.z0 - hw && z < b.z1 + hw && !out.includes(b.y1))
            out.push(b.y1);
    }
    return out.sort((a, b) => Math.abs(a - prefer) - Math.abs(b - prefer) || a - b);
}
/** Inside the grounded play ring (CollisionWorld.groundMin/Max) with a margin. */
function inPlayRing(m, x, z, margin) {
    const i = (m.ringInset ?? 2) + margin;
    return x >= m.bounds.min[0] + i && x <= m.bounds.max[0] - i && z >= m.bounds.min[2] + i && z <= m.bounds.max[2] - i;
}
/** Pickup support: its centre at least this far inside the surface it rests
 *  on — no chest balanced on a 0.3 m bulwark rail or hanging off a deck edge. */
const PICK_SUPPORT_IN = 0.25;
/** Can a pickup rest at (x, y, z)? A solid surface 0-1.2 m under its centre
 *  (−0.3 tolerated: the item renders ~1 m up), the item's own volume clear of
 *  every collider, room for a (crouched) player body on that surface to grab
 *  it, inside the play ring and above the void. */
export function pickupSpotOk(m, x, y, z) {
    if (!Number.isFinite(x + y + z) || !inPlayRing(m, x, z, 0.6) || y < m.killZ + 1)
        return false;
    const tops = floorTops(m, x, z, -PICK_SUPPORT_IN, y - 1.2, y + 0.3, y);
    if (!tops.length)
        return false;
    const fl = Math.max(...tops); // the surface it actually rests on
    // the item itself renders at y+1.0 ± 0.25 bob (worldEntities.ts)
    if (solidOverlap(m, x - 0.35, Math.max(fl + 0.05, y + 0.35), z - 0.35, x + 0.35, y + 1.4, z + 0.35))
        return false;
    return !solidOverlap(m, x - 0.4, fl + 0.05, z - 0.4, x + 0.4, fl + 1.2, z + 0.4);
}
/** Can a jump pad live at (x, y, z)? Its disc sits ON a floor (not sunk into
 *  it, not hovering), a player body fits standing on it, and its beam column
 *  is open: ~4 m of sky over a LAUNCH pad; for a GRAV-LIFT column (fling 0)
 *  the whole shaft up to its apex-2.5 m release — floor holes are open space,
 *  but a skybridge across the shaft pins the rider under it (Spiderweb's
 *  street lift under the y≈60 bridge). */
export function padSpotOk(m, jp, x, y, z) {
    if (!Number.isFinite(x + y + z) || !inPlayRing(m, x, z, 0.6) || y < m.killZ + 1)
        return false;
    const tops = floorTops(m, x, z, -0.1, y - 0.5, y + 0.1, y);
    if (!tops.length)
        return false;
    const fl = Math.max(...tops);
    if (solidOverlap(m, x - 0.45, fl + 0.05, z - 0.45, x + 0.45, fl + 1.7, z + 0.45))
        return false;
    const power = jp.power ?? 14.5; // MOVE.jumpPad.power
    const apex = (power * power) / 48; // v²/2g, g = 24
    const col = jp.fling === 0 ? Math.max(2, apex - 2.5) : Math.min(4, apex);
    return !solidOverlap(m, x - 0.3, fl + 1.7, z - 0.3, x + 0.3, fl + Math.max(1.8, col), z + 0.3);
}
const r1 = (v) => Math.round(v * 10) / 10;
/** Ring-search candidates around (x, z): the point itself first (a vertical
 *  re-seat), then rings every 0.5 m, ~0.75 m apart, in a fixed order. */
function* ringCandidates(x, z, maxR) {
    yield [x, z, 0];
    for (let r = 0.5; r <= maxR; r += 0.5) {
        const n = Math.max(8, Math.round((2 * Math.PI * r) / 0.75));
        for (let i = 0; i < n; i++) {
            const a = (i / n) * Math.PI * 2;
            yield [r1(x + Math.cos(a) * r), r1(z + Math.sin(a) * r), r];
        }
    }
}
/** Nearest valid pickup spot to `p` (ring search, on the surface level nearest
 *  the original), keeping `minSep` from every `avoid` point and — when it can —
 *  the 6.5 m vehicle board clearance. `vehClears` lists the clearances to try
 *  in order. null = none within `maxR` (the caller keeps the original; the
 *  bench reports it). */
function nearestPickupSpot(m, p, avoid, minSep, maxR = 16, vehClears = [6.5, 0]) {
    const [x, y, z] = p;
    const sepOk = (q) => avoid.every((a) => Math.hypot(a[0] - q[0], a[1] - q[1], a[2] - q[2]) >= minSep);
    if (pickupSpotOk(m, x, y, z) && sepOk(p))
        return p;
    const own = floorTops(m, x, z, 0.3, y - 1.2, y + 0.3, y);
    const lift = own.length ? Math.min(0.9, Math.max(0.2, y - Math.max(...own))) : 0.4;
    const vehs = m.vehicles ?? [];
    for (const vehClear of vehClears) {
        let best = null, bestDy = Infinity, bestR = -1;
        for (const [cx, cz, r] of ringCandidates(x, z, maxR)) {
            if (best && r > bestR)
                break; // the ring that found one is done
            if (vehClear && vehs.some((v) => Math.hypot(v.p[0] - cx, v.p[2] - cz) < vehClear))
                continue;
            for (const top of floorTops(m, cx, cz, -PICK_SUPPORT_IN, y - 3, y + 1.5, y - lift)) {
                const q = [cx, r1(top + lift), cz];
                if (!pickupSpotOk(m, q[0], q[1], q[2]) || !sepOk(q))
                    continue;
                const dy = Math.abs(q[1] - y);
                if (dy < bestDy) {
                    best = q;
                    bestDy = dy;
                    bestR = r;
                }
                break;
            }
        }
        if (best)
            return best;
    }
    return null;
}
/** RUNTIME placement (server: music chest, purge buy stations): the nearest
 *  valid pickup spot to (x, y, z) within `maxR`, keeping 2.5 m from `avoid`
 *  and preferring the vehicle board clearance — or null if there is none.
 *  Deterministic: a fixed ring order over `map.boxes` (invariant 7). Cost is a
 *  linear box scan per candidate, so call it per placement, not per tick. */
export function findPickupSpot(map, x, y, z, maxR = 8, avoid = []) {
    return nearestPickupSpot(map, [x, y, z], avoid, 2.5, maxR);
}
/** Nearest valid spot for a jump pad (same ring search, re-seated on the floor). */
function nearestPadSpot(m, jp, maxR = 8) {
    const [x, y, z] = jp.p;
    if (padSpotOk(m, jp, x, y, z))
        return jp.p;
    for (const [cx, cz] of ringCandidates(x, z, maxR)) {
        for (const top of floorTops(m, cx, cz, -0.1, y - 1.5, y + 1.0, y - 0.15)) {
            const q = [cx, Math.round((top + 0.15) * 100) / 100, cz];
            if (padSpotOk(m, jp, q[0], q[1], q[2]))
                return q;
        }
    }
    return null;
}
/** First anchor offset that lands on a valid, uncrowded pickup spot (the
 *  anchored chest passes below). Falls back to the nearest valid spot. */
function anchoredSpot(m, a, offsets, minSep = 2.5) {
    const taken = (m.pickups ?? []).map((pk) => pk.p);
    const sepOk = (q) => taken.every((t) => Math.hypot(t[0] - q[0], t[1] - q[1], t[2] - q[2]) >= minSep);
    for (const [ox, oz] of offsets) {
        const q = [r1(a[0] + ox), a[1], r1(a[2] + oz)];
        if (pickupSpotOk(m, q[0], q[1], q[2]) && sepOk(q))
            return q;
    }
    return nearestPickupSpot(m, [r1(a[0] + offsets[0][0]), a[1], r1(a[2] + offsets[0][1])], taken, minSep);
}
// Timed power-up chests on EVERY map (existing chests untouched): anchored
// next to known-good health/armor spots so they always sit on real ground.
for (const m of Object.values(MAPS)) {
    const anchors = (m.pickups ?? []).filter((p) => p.kind === "health" || p.kind === "armor").slice(0, 3);
    const kinds = ["speed", "dmg", "cloak"];
    anchors.forEach((a, i) => {
        // first VALID offset (was a blind +2.4/+1.4 — on Mega Chibre that put the
        // speed chest inside a vein ridge and the dmg chest on the side rail)
        const p = anchoredSpot(m, a.p, [[2.4, 1.4], [-2.4, 1.4], [2.4, -1.4], [-2.4, -1.4], [0, 2.8], [0, -2.8], [2.8, 0], [-2.8, 0]]);
        if (p)
            m.pickups.push({ p, kind: kinds[i % 3] });
    });
}
// One FART BLASTER hazard chest on EVERY map (2026-07-23 ticket): anchored to
// a known-good health/armor ground spot like the power-up/decoy chests above,
// offset diagonally so the three never stack. It is a HAZARD — the server
// never lets it collect; it belches smoke rings at whoever comes close.
// Anchor = the spot FARTHEST from map centre: withWeaponChest converts the
// most CENTRAL health/armor spot into the deathmatch weapon chest at room
// time, and the first cut of this loop ("last anchor") landed the hazard
// 2.8 m from that gun on some maps — anyone grabbing the weapon got blasted
// point-blank and read the pair as one broken chest (bug, 2026-07-23).
for (const m of Object.values(MAPS)) {
    if ((m.pickups ?? []).some((p) => p.kind === "fartblaster"))
        continue;
    const cx = (m.bounds.min[0] + m.bounds.max[0]) / 2;
    const cz = (m.bounds.min[2] + m.bounds.max[2]) / 2;
    let a = null, bd = -1;
    for (const p of m.pickups ?? []) {
        if (p.kind !== "health" && p.kind !== "armor")
            continue;
        // Some maps have low/underwater emergency pickups; a weapon chest placed
        // beside one reads as missing. Prefer normal visible floor anchors.
        if (p.p[1] < -1)
            continue;
        const d = (p.p[0] - cx) ** 2 + (p.p[2] - cz) ** 2;
        if (d > bd) {
            bd = d;
            a = p;
        }
    }
    const fp = a ? anchoredSpot(m, a.p, [[2.4, -1.4], [-2.4, -1.4], [2.4, 1.4], [-2.4, 1.4], [0, -2.8], [0, 2.8], [2.8, 0], [-2.8, 0]], 3) : null;
    if (fp)
        m.pickups.push({ p: fp, kind: "fartblaster" });
}
const CHEST_KIND = new Set([
    "jetpack", "rocket", "speed", "dmg", "cloak", "imperion", "antigrav",
    "decoy", "buy_ammo", "buy_weapon", "buy_perk", "buy_mystery", "buy_pap",
    "music", "weaponchest", "fartblaster", "beigechest", "waterchest",
]);
function predictedWeaponChestSpot(m) {
    const pool = m.pickups ?? [];
    const cx = (m.bounds.min[0] + m.bounds.max[0]) / 2;
    const cz = (m.bounds.min[2] + m.bounds.max[2]) / 2;
    let best = null;
    let bd = Infinity;
    for (const pk of pool) {
        if (pk.kind !== "health" && pk.kind !== "armor" && pk.kind !== "grenade")
            continue;
        if (pk.p[1] < -1)
            continue;
        const d = (pk.p[0] - cx) ** 2 + (pk.p[2] - cz) ** 2;
        if (d < bd) {
            bd = d;
            best = pk;
        }
    }
    return best?.p ?? null;
}
function chestClearance(m, p) {
    let best = Infinity;
    for (const pk of m.pickups ?? []) {
        if (!CHEST_KIND.has(pk.kind))
            continue;
        const d = Math.hypot(pk.p[0] - p[0], pk.p[2] - p[2]);
        if (d < best)
            best = d;
    }
    const weapon = predictedWeaponChestSpot(m);
    if (weapon)
        best = Math.min(best, Math.hypot(weapon[0] - p[0], weapon[2] - p[2]));
    return best;
}
// One WATER GUN chest on EVERY map. Use visible health/armor anchors, but pick
// the offset with the most clearance so it does not read as part of another
// chest cluster.
for (const m of Object.values(MAPS)) {
    if ((m.pickups ?? []).some((p) => p.kind === "waterchest"))
        continue;
    const cx = (m.bounds.min[0] + m.bounds.max[0]) / 2;
    const cz = (m.bounds.min[2] + m.bounds.max[2]) / 2;
    let best = null;
    let bestScore = -1;
    const offsets = [[-6, -3.5], [6, -3.5], [-6, 3.5], [6, 3.5], [-8, 0], [8, 0], [0, -8], [0, 8]];
    for (const p of m.pickups ?? []) {
        if (p.kind !== "health" && p.kind !== "armor")
            continue;
        if (p.p[1] < -1)
            continue;
        const anchorD = Math.hypot(p.p[0] - cx, p.p[2] - cz);
        for (const [ox, oz] of offsets) {
            const wp = [
                Math.max(m.bounds.min[0] + 2, Math.min(m.bounds.max[0] - 2, p.p[0] + ox)),
                p.p[1],
                Math.max(m.bounds.min[2] + 2, Math.min(m.bounds.max[2] - 2, p.p[2] + oz)),
            ];
            // most clearance AMONG VALID SPOTS — maximising clearance alone walked
            // the chest straight off Mega Chibre's 24 m-wide shaft into the void
            if (!pickupSpotOk(m, wp[0], wp[1], wp[2]))
                continue;
            const clear = chestClearance(m, wp);
            const score = clear * 1000 + anchorD;
            if (score > bestScore) {
                bestScore = score;
                best = wp;
            }
        }
    }
    if (best)
        m.pickups.push({ p: best, kind: "waterchest" });
}
// BATTLE ROYALE runs anywhere Gun Game does — same map needs, same ladder,
// one life. Derived here so every current and future gungame map offers it
// without each author remembering to list it.
for (const m of Object.values(MAPS)) {
    if (m.modes.includes("gungame") && !m.modes.includes("royale"))
        m.modes.push("royale");
}
// One BODY DOUBLE chest on every map that still lacks one (hand-authored maps
// skip enrichMaps' kind-guarded placement): anchored to a known-good
// health/armor spot exactly like the power-up chests above, offset the other
// way so the two never stack. Runs before the vehicle-clearance nudge so a
// decoy chest is also pushed out of any parked vehicle's board radius.
for (const m of Object.values(MAPS)) {
    if ((m.pickups ?? []).some((p) => p.kind === "decoy"))
        continue;
    let best = null;
    let bestClear = -1;
    const offsets = [[-2.4, -1.4], [2.4, 1.4], [-4.8, 1.4], [4.8, -1.4], [0, 5.2], [0, -5.2]];
    for (const a of m.pickups ?? []) {
        if (a.kind !== "health" && a.kind !== "armor")
            continue;
        if (a.p[1] < -1)
            continue;
        for (const [ox, oz] of offsets) {
            const p = [
                Math.max(m.bounds.min[0] + 2, Math.min(m.bounds.max[0] - 2, a.p[0] + ox)),
                a.p[1],
                Math.max(m.bounds.min[2] + 2, Math.min(m.bounds.max[2] - 2, a.p[2] + oz)),
            ];
            if (!pickupSpotOk(m, p[0], p[1], p[2]))
                continue;
            const clear = chestClearance(m, p);
            if (clear > bestClear) {
                bestClear = clear;
                best = p;
            }
        }
    }
    if (best)
        m.pickups.push({ p: best, kind: "decoy" });
}
// Keep every pickup CLEAR of vehicles. A vehicle parked on a cloak/speed/dmg
// chest meant you collected it (and heard its announcer line) just by walking
// up to board — this was the "ghost sound every time I enter the helicopter"
// bug (a heli scattered onto a cloak powerup). Nudge the pickup out past the
// ~4.6m board radius, clamped to the map so it never lands out of bounds.
for (const m of Object.values(MAPS)) {
    const vehs = m.vehicles ?? [];
    if (!vehs.length)
        continue;
    const bnd = m.bounds;
    for (const pk of m.pickups ?? []) {
        for (const v of vehs) {
            let dx = pk.p[0] - v.p[0], dz = pk.p[2] - v.p[2];
            let d = Math.hypot(dx, dz);
            if (d >= 6.5)
                continue;
            if (d < 0.001) {
                dx = 1;
                dz = 0;
                d = 1;
            } // exactly overlapping → pick a direction
            const pushed = [
                Math.max(bnd.min[0] + 2, Math.min(bnd.max[0] - 2, v.p[0] + (dx / d) * 7)),
                pk.p[1],
                Math.max(bnd.min[2] + 2, Math.min(bnd.max[2] - 2, v.p[2] + (dz / d) * 7)),
            ];
            // a blind 7 m shove pushed Practice Field's sky-island jetpacks off their
            // 7 m islands into thin air: only take a VALID spot (nearest one to the
            // shove, vehicle clearance preferred), else leave it where it was
            const others = (m.pickups ?? []).filter((o) => o !== pk).map((o) => o.p);
            const spot = pickupSpotOk(m, pushed[0], pushed[1], pushed[2]) ? pushed : nearestPickupSpot(m, pushed, others, 2.5, 8, [6.5]);
            if (spot)
                pk.p = spot;
        }
    }
}
/** Hand-laid pickup spots for maps whose generated layout bunched up (every
 *  auto pass grows outward from the map centre or anchors next to an existing
 *  health/armor spot, so chests stacked in a few clumps and whole lanes got
 *  none). Per map, per KIND: the listed positions replace that kind's spots in
 *  order — only when the counts match, so a pass that later adds or removes a
 *  chest of that kind leaves it alone instead of silently mis-assigning
 *  (bench-pickup-placement catches the fallout). Positions are probed against
 *  the map's colliders and the on-foot flood fill by that bench. */
const PICKUP_LAYOUT = {
    // ARENA (owner: "on arena it is really bad"): everything sat in the middle
    // band, four chests within 5 m around (5, 13) and two touching at (-11, -2).
    // Now point-symmetric: health on the two raised platforms, armor/grenades in
    // the four L-wall pockets, the rifle on the south arm, the power-ups and
    // chests pairwise in the outer lanes.
    grid_arena: {
        health: [[-20, 2.3, -3.5], [20, 2.3, 3.5]],
        armor: [[-12.5, 0.3, -12.5], [12.5, 0.3, 12.5]],
        grenade: [[12.5, 0.3, -12.5], [-12.5, 0.3, 12.5]],
        imperion: [[0, 0.3, 12.6]],
        antigrav: [[-21.5, 0.3, -10]],
        decoy: [[21.5, 0.3, 10]],
        jetpack: [[14.4, 0.3, -22]],
        waterchest: [[-14.4, 0.3, 22]],
        speed: [[-6.5, 0.3, 20]],
        dmg: [[6.5, 0.3, -20]],
        cloak: [[19.5, 0.3, -9]],
        fartblaster: [[-19.5, 0.3, 9]],
    },
    // The auto-enriched maps: enrichMaps fills spots from the centre outward and
    // the power-up / hazard chests anchor 2.8 m off the first health/armor, so
    // each map had a 3-4 chest clump mid-map and empty outer lanes. Moving the
    // clumped health/armor (and the odd chest) into the biggest holes of the
    // reachable floor fixes the coverage; everything else stays put. Spots were
    // picked by a coverage search over the bench's on-foot flood fill.
    rooftops: {
        health: [[-27.7, 0.3, 26.3], [22.3, 0.3, 30.3]],
        armor: [[-23.7, 0.3, -27.7], [-21.2, 0.2, -4.4]],
    },
    rooftops_xl: {
        health: [[60.3, 12.6, 58.3], [-59.7, 10.6, -57.7]], // two rooftops
        armor: [[52.3, 0.3, -63.7], [-63.7, 8.6, 60.3]],
        cloak: [[-1.7, 7.6, -67.7]],
    },
    rooftop_spiderweb: {
        health: [[62.3, 0.3, -53.7], [62.3, 0.3, 52.3]],
        armor: [[66.3, 36.8, 2.3], [16.3, 0.3, 70.3]],
    },
    arena_xl: {
        health: [[34.3, 0.3, -31.7], [-23.7, 0.3, 38.3]],
        armor: [[30.3, 0.3, 36.3], [-29.7, 0.3, -39.7]],
        antigrav: [[40.3, 0.3, 6.3]],
    },
    canyon: {
        health: [[30.3, 0.3, -24.7], [-31.7, 0.3, 25.3]],
        armor: [[30.3, 0.3, 27.3], [-27.7, 0.3, -30.7]],
    },
    neon_district: {
        health: [[-21.7, 0.3, -23.7], [20.3, 0.3, 22.3]],
        armor: [[26.3, 0.3, -17.7], [-10.4, 0.2, 11.2]],
    },
    chibre_arena: {
        health: [[4.1, 0.3, -27.9], [-19.9, 0.3, -1.9]],
        armor: [[24.1, 0.3, -5.9], [15.2, 0.3, 14]],
    },
    plumbing_plaza: {
        armor: [[7, 0.3, 27.5], [-25.9, 0.3, 20.1]],
        health: [[7, 0.3, -27.5], [26.1, 0.3, -21.9]],
    },
    // MEDIEVAL CASTLE: 8 pickups were packed into the keep/courtyard and the
    // whole southern half of the countryside (and both flanks) had none. Six of
    // the keep's spares go out to the fields; the dungeon rocket stays put.
    medieval_castle: {
        armor: [[-113.7, 0.3, 80.3], [74.9, 0.4, -106.9]],
        health: [[-125.7, 0.3, -39.7], [114.3, 0.3, 80.3], [0, 0.4, -50], [7, 12.7, 10]],
        cloak: [[44, -4.7, 46], [1.2, 9.6, -9.6], [114.3, 0.3, -23.7]],
        dmg: [[-35.8, 10.2, 49.5], [2.3, 0.3, 116.3], [-25.6, 0.4, 17.4]],
        speed: [[36.2, 10.2, 49.5], [-129.7, 0.3, -111.7]],
    },
    // SKYHAVEN: 15 of 33 pickups sat inside the central towers/undercity; the
    // island's outer ring (beaches, airport, docks) had almost none.
    neo_island: {
        armor: [[79.3, 0.6, 79.3], [-84.7, 0.6, 71.3], [15, 45.6, -34], [2, 33.6, 16], [-96.7, 0.5, -4.7], [0, 0.4, 60], [8, 0.4, -8], [1.5, 0.4, -30]],
        health: [[-76.7, 0.6, -84.7], [48, 26.2, 6], [-45, 21.6, 22], [-44.7, 0.5, 95.3], [67.3, 0.6, -88.7], [23.3, 0.5, 95.3], [1.5, 0.4, 30]],
        antigrav: [[-0.7, -6, -92.7]],
    },
    // WILD WEST: town pairs (church/cantina/bank) out to the empty far corners.
    wild_west_train_pursuit: {
        health: [[134.3, 0.3, 110.3], [95, 0.4, -70], [-119, 0.4, -78], [-124, 0.8, -45]],
        armor: [[110.3, 0.3, -133.7], [-45.7, 0.3, -129.7], [-121, 0.4, 84], [44.4, 0.4, -74.1]],
    },
    // MEGA CHIBRE: the water chest was OFF the sculpture (x -16 over the void),
    // speed inside a vein ridge, dmg on the side rail, and the glans held three
    // chests + a pad in a 4 m clump while the base and both mounds had nothing.
    mega_chibre: {
        speed: [[-8.5, 3.3, 36]], // left mound, middle tier (high ground — pad up)
        dmg: [[8.5, 3.3, 36]], // right mound, middle tier
        cloak: [[-11, 0.4, -44]], // glans, west lobe
        fartblaster: [[11, 0.4, -44]], // glans, east lobe
        waterchest: [[20, 0.4, 48]], // base slab, behind the right mound
        decoy: [[-20, 0.4, 48]], // base slab, behind the left mound
    },
};
function applyPickupLayout(m) {
    const layout = PICKUP_LAYOUT[m.id];
    if (!layout || !m.pickups)
        return;
    for (const [kind, spots] of Object.entries(layout)) {
        const of = m.pickups.filter((pk) => pk.kind === kind);
        if (of.length !== spots.length)
            continue;
        of.forEach((pk, i) => { pk.p = [spots[i][0], spots[i][1], spots[i][2]]; });
    }
}
// LAST placement word: any pickup or pad still on an invalid spot (hand-authored
// coordinates that drifted as boxes were added: a chest inside a crate, a beam
// inside a wall, a power-up over the void) moves to the nearest valid spot on
// the same level; pickups also keep 2.5 m apart (one grab = one chest).
settlePlacements();
function settlePlacements() {
    for (const m of Object.values(MAPS)) {
        applyPickupLayout(m);
        const placed = [];
        for (const pk of m.pickups ?? []) {
            const spot = nearestPickupSpot(m, pk.p, placed, 2.5);
            if (spot)
                pk.p = spot;
            placed.push(pk.p);
        }
        for (const jp of m.jumpPads ?? []) {
            const spot = nearestPadSpot(m, jp);
            if (spot)
                jp.p = spot;
        }
    }
}
export const DEFAULT_MAP_ID = MAP_SANDLOT.id;
// ---- spawn sanitizer: no map may spawn a player inside a wall/block ----
// (hand-authored spawns drift as maps evolve — the desert map had several
// buried in geometry). Runs LAST, after every map mutation above.
{
    const HW = 0.5, HT = 1.8;
    const collides = (m, x, y, z) => m.boxes.some((b) => x + HW > b.p[0] - b.s[0] / 2 && x - HW < b.p[0] + b.s[0] / 2 &&
        y + HT > b.p[1] - b.s[1] / 2 && y < b.p[1] + b.s[1] / 2 &&
        z + HW > b.p[2] - b.s[2] / 2 && z - HW < b.p[2] + b.s[2] / 2);
    // vehicles too: a kart born inside a wall is stuck forever
    const vehClear = (m, x, y, z) => m.boxes.some((b) => x + 1.7 > b.p[0] - b.s[0] / 2 && x - 1.7 < b.p[0] + b.s[0] / 2 &&
        y + 2.2 > b.p[1] - b.s[1] / 2 && y + 0.4 < b.p[1] + b.s[1] / 2 &&
        z + 2.7 > b.p[2] - b.s[2] / 2 && z - 2.7 < b.p[2] + b.s[2] / 2);
    for (const m of Object.values(MAPS)) {
        for (const vh of m.vehicles ?? []) {
            if (!vehClear(m, vh.p[0], vh.p[1], vh.p[2]))
                continue;
            outer2: for (let ry = 0; ry <= 4 && true; ry += 2) {
                for (let r = 2; r <= 18; r++) {
                    for (let a = 0; a < 8; a++) {
                        const ang = (a / 8) * Math.PI * 2;
                        const nx = vh.p[0] + Math.cos(ang) * r;
                        const nz = vh.p[2] + Math.sin(ang) * r;
                        if (!vehClear(m, nx, vh.p[1] + ry, nz)) {
                            vh.p = [nx, vh.p[1] + ry, nz];
                            break outer2;
                        }
                    }
                }
            }
        }
        for (const sp of m.spawns) {
            if (!collides(m, sp.p[0], sp.p[1] + 0.1, sp.p[2]))
                continue;
            // spiral outward (then upward) for the nearest clear spot
            let fixed = false;
            outer: for (let ry = 0; ry <= 6 && !fixed; ry += 2) {
                for (let r = 1; r <= 14; r++) {
                    for (let a = 0; a < 8; a++) {
                        const ang = (a / 8) * Math.PI * 2;
                        const nx = sp.p[0] + Math.cos(ang) * r;
                        const nz = sp.p[2] + Math.sin(ang) * r;
                        const ny = sp.p[1] + ry;
                        if (!collides(m, nx, ny + 0.1, nz)) {
                            sp.p = [nx, ny, nz];
                            fixed = true;
                            break outer;
                        }
                    }
                }
            }
        }
    }
}
//# sourceMappingURL=maps.js.map